<template>
  <div class="fcp-report" style="overflow-y: auto">
    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <template v-else>
    <div class="fcp-report__backdrop" :class="{ 'fcp-report__backdrop--edit-locked': isEditLocked }">
      <!-- Header bar -->

      <!-- fcp-header-row is this component's one meaningful direct child of
           .fcp-report__backdrop - "row no-gutters mt-1" (REC UMRs/BDX/Cashbook) is NOT a sibling
           of this at the backdrop level, despite an earlier version of this comment assuming so -
           it's nested inside .fcp-col-main, further down (Column B, inside this same row). flex:
           1 here (see the SCSS) is what gives THIS row, and everything nested inside it, a real
           viewport-driven height in the first place, rather than one sized off its own content -
           without it, .fcp-variance-row's own flex: 1 (further down, inside .fcp-col-main) had
           nothing real to grow into. -->
      <div class="row no-gutters fcp-header-row">
        <!-- Column A: FCP pill + Last Import Date + Report Period -->
        <div class="col-12 col-lg-1-5 fcp-rail pr-lg-3">
          <div class="fcp-header__pill mb-2">
            <span class="fcp-header__pill-text">RECONCILIATION</span>
          </div>

          <div class="fcp-divider-bars">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div
            class="select-report-period mb-2"
            :style="reportPeriodMaxHeight ? { maxHeight: `${reportPeriodMaxHeight}px` } : null"
          >
            <div class="fcp-panel__title">Report Period</div>
            <div class="fcp-period-tree">
              <label class="fcp-check">
                <input type="checkbox" :checked="allPeriodsSelected" @change="toggleAllPeriods" />
                Select all
              </label>
              <div v-for="group in periodTree" :key="group.year" class="fcp-period-year">
                <label class="fcp-check fcp-check--year font-weight-bold">
                  <b-icon
                    :icon="isYearExpanded(group.year) ? 'chevron-down' : 'chevron-right'"
                    font-scale="0.8"
                    class="mr-1 fcp-check__chevron"
                    role="button"
                    @click.prevent="toggleYearExpanded(group.year)"
                  ></b-icon>
                  <input
                    type="checkbox"
                    :checked="isYearSelected(group)"
                    :indeterminate.prop="isYearPartiallySelected(group)"
                    @change="toggleYear(group)"
                  />
                  {{ group.year }}
                </label>
                <template v-if="isYearExpanded(group.year)">
                  <label
                    v-for="month in group.months"
                    :key="month.key"
                    class="fcp-check fcp-check--indent"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedPeriods.includes(month.key)"
                      @change="togglePeriod(month.key)"
                    />
                    {{ month.label }}
                  </label>
                </template>
              </div>
            </div>
          </div>

          <div class="fcp-rail-dropdowns mb-2">
            <div class="fcp-dropdown fcp-dropdown--rail mb-2">
              <span class="small-font mb-1 d-flex align-items-center justify-content-between">
                <span>Party</span>
                <rec-filter-eraser
                  :active="isTopFilterActive('party')"
                  title="Clear Party filter"
                  @clear="clearTopFilter('party')"
                />
              </span>
              <b-dropdown
                :text="dcaDropdownLabel"
                toggle-class="fcp-checklist-toggle"
                menu-class="fcp-checklist-menu fcp-checklist-menu--custom-checkbox"
                variant="link"
                no-caret
                boundary="window"
                :popper-opts="dropdownPopperOpts()"
                @hidden="closeDcaFlyout(); resetChecklistSearches()"
              >
                <template #button-content>
                  {{ dcaDropdownLabel }}
                  <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                </template>
                <!-- @submit.prevent (every b-dropdown-form in this file - toolbar AND column-header
                     checklists alike, same fix throughout) - bootstrap-vue's b-dropdown-form
                     renders a real <form>, so pressing Enter in its own search box (a plain text
                     input, no submit button of its own) triggered the browser's native form
                     submission instead of just narrowing the checklist. That submission navigates
                     the page, which resets every ref here back to its initial/default state
                     (report period aside, everything else re-seeds from a now-query-less URL) and
                     surfaces as an unrelated-looking request failure once whatever was still
                     in-flight gets torn down mid-navigation. Reported live (Reconciliation page,
                     Account Name column header, but reproducible from ANY of these checklists). -->
                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredDcaGroups">
                  <div class="fcp-checklist-search">
                    <b-icon icon="search" font-scale="0.8"></b-icon>
                    <input
                      type="text"
                      v-model="dcaSearch"
                      placeholder="Search"
                      class="fcp-checklist-search__input"
                    />
                  </div>
                  <label class="fcp-check">
                    <input type="checkbox" :checked="allDcaGroupsSelected" @change="toggleAllDcaGroups" />
                    Select all
                  </label>
                  <template v-if="filteredDcaPartyGroups.length">
                    <!-- Clicking anywhere on a row (except the checkbox itself) opens the
                         single shared flyout below - see togglePartyTypeFlyout. The checkbox
                         stops its own click so it only toggles selection, instead of also
                         opening/closing the flyout. -->
                    <div
                      v-for="group in filteredDcaPartyGroups"
                      :key="group.partyType"
                      class="fcp-check fcp-check--year font-weight-bold fcp-dca-party-row"
                      :class="{ 'fcp-dca-party-row--active': isPartyTypeExpanded(group.partyType) }"
                      @click="togglePartyTypeFlyout(group.partyType, $event)"
                    >
                      <b-icon
                        :icon="isPartyTypeExpanded(group.partyType) ? 'chevron-down' : 'chevron-right'"
                        font-scale="0.8"
                        class="mr-1 fcp-check__chevron"
                      ></b-icon>
                      <input
                        type="checkbox"
                        :checked="isPartyTypeSelected(group)"
                        :indeterminate.prop="isPartyTypePartiallySelected(group)"
                        @click.stop
                        @change="togglePartyType(group)"
                      />
                      {{ dcaSectionLabel(group.partyType) }} ({{ group.names.length }})
                    </div>
                  </template>
                  <div v-else class="small-font font-italic px-1">No matches</div>
                </b-dropdown-form>
              </b-dropdown>

              <!-- Portal target - moved to <body> on mount (see onMounted/onBeforeUnmount)
                   so it escapes the dropdown menu's Popper transform/overflow clipping.
                   v-show (not v-if) keeps this element's identity stable across opens/closes,
                   since the portal only runs once at mount. @focusin.stop alongside @click.stop:
                   <b-dropdown> closes itself on a global 'focusin' bubbling to document (its own
                   mechanism for detecting focus moving outside the dropdown), completely
                   separate from the 'click' event @click.stop already handles - checking a
                   checkbox in here focuses it, and since this div is portaled outside the
                   dropdown's own root element, bootstrap-vue saw that focusin as "outside" and
                   closed the whole dropdown after every single selection. -->
              <div
                v-show="dcaFlyoutGroup"
                ref="dcaFlyoutRef"
                class="fcp-dca-flyout"
                :style="dcaFlyout ? { top: `${dcaFlyout.top}px`, left: `${dcaFlyout.left}px` } : {}"
                @click.stop
                @focusin.stop
              >
                <template v-if="dcaFlyoutGroup">
                  <div class="fcp-dca-flyout__header">{{ dcaSectionLabel(dcaFlyoutGroup.partyType) }}</div>
                  <label
                    v-for="name in dcaFlyoutGroup.names"
                    :key="`${dcaFlyoutGroup.partyType}::${name}`"
                    class="fcp-check"
                  >
                    <input
                      type="checkbox"
                      :checked="isDcaGroupSelected(dcaFlyoutGroup.partyType, name)"
                      @change="toggleDcaGroup(dcaFlyoutGroup.partyType, name)"
                    />
                    {{ maskPartyGroupName(dcaFlyoutGroup.partyType, name) }}
                  </label>
                </template>
              </div>
            </div>

            <div class="fcp-dropdown fcp-dropdown--rail position-relative">
              <span class="small-font mb-1 d-flex align-items-center justify-content-between">
                <span class="d-inline-flex align-items-center">
                  Source
                  <span v-if="varianceFilterOptionsLoading" class="fcp-filter-spinner"><b-spinner small label="Updating options"></b-spinner></span>
                </span>
                <rec-filter-eraser
                  :active="isTopFilterActive('source')"
                  title="Clear Source filter"
                  @clear="clearTopFilter('source')"
                />
              </span>
              <b-dropdown
                :text="sourceDropdownLabel"
                toggle-class="fcp-checklist-toggle"
                menu-class="fcp-checklist-menu"
                variant="link"
                no-caret
                boundary="window"
                :popper-opts="dropdownPopperOpts()"
              >
                <template #button-content>
                  {{ sourceDropdownLabel }}
                  <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                </template>
                <b-dropdown-item :active="null === sourceFilter" @click="sourceFilter = null">
                  All
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="isBdxSourceOptionAvailable"
                  :active="'bdx' === sourceFilter"
                  @click="sourceFilter = 'bdx'"
                >
                  BDX Only
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="isCashbookSourceOptionAvailable"
                  :active="'cashbook' === sourceFilter"
                  @click="sourceFilter = 'cashbook'"
                >
                  CB Only
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="isBothSourceOptionAvailable"
                  :active="'both' === sourceFilter"
                  @click="sourceFilter = 'both'"
                >
                  Both
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <div class="fcp-clear-filters mb-2">
            <button class="btn btn-warning btn-sm fcp-clear-filters__btn" @click="clearFilters">
              Clear Filters
            </button>
          </div>
        </div>

        <!-- Column B+C: KPI + filter toolbar on one line, spanning the width of both
             the FCP UMRs panel and the Variance Details panel below -->
        <div class="col-12 col-lg-10-5 fcp-col-main">
          <div class="fcp-toolbar-row d-flex flex-nowrap align-items-stretch mb-2">
            <!-- LIQ-2571: warning triangle that opens/closes the Account Name Reconciliation panel
                 below, inline. The badge is the number of keys with no Account Name or one that
                 isn't in their UMR's cash book; no badge when there are none. -->
            <button
              v-if="canReconcileAccountNames"
              type="button"
              class="fcp-anr-toggle mr-3 flex-grow-0 flex-shrink-0"
              :aria-expanded="accountNamePanelOpen ? 'true' : 'false'"
              :aria-label="accountNameToggleTitle"
              :title="accountNameToggleTitle"
              @click="toggleAccountNamePanel"
            >
              <b-icon icon="exclamation-triangle-fill" font-scale="1.8"></b-icon>
              <span v-if="accountNameOutstandingCount" class="fcp-anr-toggle__badge">
                {{ accountNameOutstandingCount > 999 ? '999+' : accountNameOutstandingCount }}
              </span>
            </button>

            <div class="fcp-kpi mr-1 flex-grow-0 flex-shrink-0">
              <div class="fcp-kpi__label">REC Accounts</div>
              <div class="fcp-kpi__value">
                <!-- See recUmrLoading - this count comes from the same /rec-umrs response. -->
                <b-spinner v-if="recUmrLoading" small label="Loading"></b-spinner>
                <template v-else>{{ displayedFcpAccountCount === null ? '—' : displayedFcpAccountCount }}</template>
              </div>
            </div>

            <div class="fcp-toolbar d-flex flex-nowrap flex-shrink-0 flex-grow-1 align-items-stretch">
              <div class="fcp-toggle fcp-toolbar__item mr-3 mb-0 d-flex flex-column align-items-center text-center">
                <span class="small-font mb-1 d-block">{{ varianceToggleLabel }}</span>
                <div
                  class="fcp-tristate"
                  :class="[
                    true === varianceFilter
                      ? 'fcp-tristate--right fcp-tristate--red'
                      : false === varianceFilter
                      ? 'fcp-tristate--left fcp-tristate--green'
                      : 'fcp-tristate--center'
                  ]"
                  @click="advanceVarianceFilter"
                >
                  <div class="fcp-tristate__knob"></div>
                </div>
              </div>
              <div class="fcp-toggle fcp-toolbar__item mr-3 mb-0 d-flex flex-column align-items-center text-center">
                <span class="small-font mb-1 d-block">{{ fcpToggleLabel }}</span>
                <div
                  class="fcp-tristate"
                  :class="[
                    true === fcpFilter
                      ? 'fcp-tristate--right'
                      : false === fcpFilter
                      ? 'fcp-tristate--left'
                      : 'fcp-tristate--center',
                    { 'fcp-tristate--disabled': !fcpToggleEnabled }
                  ]"
                  @click="advanceFcpFilter"
                >
                  <div class="fcp-tristate__knob"></div>
                </div>
              </div>
              <div class="fcp-dropdown fcp-toolbar__item mr-2 mb-0">
                <span class="small-font mb-1 d-flex align-items-center justify-content-between">
                  <span>Review Status</span>
                  <rec-filter-eraser
                    :active="isTopFilterActive('reviewStatus')"
                    title="Clear Review Status filter"
                    @clear="clearTopFilter('reviewStatus')"
                  />
                </span>
                <b-dropdown
                  :text="reviewStatusDropdownLabel"
                  toggle-class="fcp-checklist-toggle"
                  menu-class="fcp-checklist-menu fcp-checklist-menu--custom-checkbox"
                  variant="link"
                  no-caret
                  boundary="window"
                  :popper-opts="dropdownPopperOpts()"
                >
                  <template #button-content>
                    {{ reviewStatusDropdownLabel }}
                    <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                  </template>
                  <b-dropdown-form @click.stop @submit.prevent>
                    <label class="fcp-check">
                      <input type="checkbox" :checked="allReviewStatusSelected" @change="toggleAllReviewStatus" />
                      Select all
                    </label>
                    <label v-for="status in reviewStatusOptions" :key="status" class="fcp-check">
                      <input
                        type="checkbox"
                        :checked="isReviewStatusSelected(status)"
                        @change="toggleReviewStatus(status)"
                      />
                      {{ status }}
                    </label>
                  </b-dropdown-form>
                </b-dropdown>
              </div>

              <div class="fcp-dropdown fcp-toolbar__item mr-2 mb-0 position-relative">
                <span class="small-font mb-1 d-flex align-items-center justify-content-between">
                  <span class="d-inline-flex align-items-center">
                    UMR
                    <span v-if="varianceFilterOptionsLoading" class="fcp-filter-spinner"><b-spinner small label="Updating options"></b-spinner></span>
                  </span>
                  <rec-filter-eraser
                    :active="isTopFilterActive('umr')"
                    title="Clear UMR filter"
                    @clear="clearTopFilter('umr')"
                  />
                </span>
                <b-dropdown
                  @hidden="resetChecklistSearches"
                  lazy
                  :text="umrDropdownLabel"
                  toggle-class="fcp-checklist-toggle"
                  menu-class="fcp-checklist-menu fcp-checklist-menu--custom-checkbox"
                  variant="link"
                  no-caret
                  boundary="window"
                  :popper-opts="dropdownPopperOpts()"
                >
                  <template #button-content>
                    {{ umrDropdownLabel }}
                    <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                  </template>
                  <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredUmrs">
                    <div class="fcp-checklist-search">
                      <b-icon icon="search" font-scale="0.8"></b-icon>
                      <input
                        type="text"
                        v-model="umrSearch"
                        placeholder="Search"
                        class="fcp-checklist-search__input"
                      />
                    </div>
                    <label class="fcp-check">
                      <input type="checkbox" :checked="allUmrsSelected" @change="toggleAllUmrs" />
                      Select all
                    </label>
                    <label v-for="umr in filteredUmrOptions" :key="umr" class="fcp-check">
                      <input
                        type="checkbox"
                        :checked="isUmrSelected(umr)"
                        @change="toggleUmr(umr)"
                      />
                      {{ maskUmr(umr) }}
                    </label>
                  </b-dropdown-form>
                </b-dropdown>
              </div>

              <div class="fcp-dropdown fcp-toolbar__item mr-2 mb-0 position-relative">
                <span class="small-font mb-1 d-flex align-items-center justify-content-between">
                  <span class="d-inline-flex align-items-center">
                    Claim Reference
                    <span v-if="varianceFilterOptionsLoading" class="fcp-filter-spinner"><b-spinner small label="Updating options"></b-spinner></span>
                  </span>
                  <rec-filter-eraser
                    :active="isTopFilterActive('claim')"
                    title="Clear Claim Reference filter"
                    @clear="clearTopFilter('claim')"
                  />
                </span>
                <b-dropdown
                  @hidden="resetChecklistSearches"
                  lazy
                  :text="claimDropdownLabel"
                  toggle-class="fcp-checklist-toggle"
                  menu-class="fcp-checklist-menu fcp-checklist-menu--custom-checkbox"
                  variant="link"
                  no-caret
                  boundary="window"
                  :popper-opts="dropdownPopperOpts()"
                >
                  <template #button-content>
                    {{ claimDropdownLabel }}
                    <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                  </template>
                  <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredClaims">
                    <div class="fcp-checklist-search">
                      <b-icon icon="search" font-scale="0.8"></b-icon>
                      <input
                        type="text"
                        v-model="claimSearch"
                        placeholder="Search"
                        class="fcp-checklist-search__input"
                      />
                    </div>
                    <label class="fcp-check">
                      <input type="checkbox" :checked="allClaimsSelected" @change="toggleAllClaims" />
                      Select all
                    </label>
                    <label v-for="claim in displayedClaimOptions" :key="claim" class="fcp-check">
                      <input
                        type="checkbox"
                        :checked="isClaimSelected(claim)"
                        @change="toggleClaim(claim)"
                      />
                      {{ maskClaim(claim) }}
                    </label>
                    <div v-if="hiddenClaimCount" class="small text-muted px-2 py-1">
                      +{{ hiddenClaimCount.toLocaleString() }} more claims, type to see the claim
                    </div>
                  </b-dropdown-form>
                </b-dropdown>
              </div>
              <div class="fcp-dropdown fcp-dropdown--wide fcp-toolbar__item mb-0 position-relative">
                <span class="small-font mb-1 d-flex align-items-center justify-content-between">
                  <span class="d-inline-flex align-items-center">
                    Matched Account Name Search (BDX + REC)
                    <span v-if="varianceFilterOptionsLoading" class="fcp-filter-spinner"><b-spinner small label="Updating options"></b-spinner></span>
                  </span>
                  <rec-filter-eraser
                    :active="isTopFilterActive('accountName')"
                    title="Clear Account Name filter"
                    @clear="clearTopFilter('accountName')"
                  />
                </span>
                <b-dropdown
                  @hidden="resetChecklistSearches"
                  lazy
                  :text="accountNameDropdownLabel"
                  toggle-class="fcp-checklist-toggle"
                  menu-class="fcp-checklist-menu fcp-checklist-menu--wide fcp-checklist-menu--custom-checkbox"
                  variant="link"
                  no-caret
                  boundary="window"
                  :popper-opts="dropdownPopperOpts()"
                >
                  <template #button-content>
                    {{ accountNameDropdownLabel }}
                    <b-icon icon="chevron-down" font-scale="0.7" class="ml-1"></b-icon>
                  </template>
                  <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredAccountNames">
                    <div class="fcp-checklist-search">
                      <b-icon icon="search" font-scale="0.8"></b-icon>
                      <input
                        type="text"
                        v-model="accountNameSearch"
                        placeholder="Search"
                        class="fcp-checklist-search__input"
                      />
                    </div>
                    <label class="fcp-check">
                      <input type="checkbox" :checked="allAccountNamesSelected" @change="toggleAllAccountNames" />
                      Select all
                    </label>
                    <label v-for="name in filteredAccountNameOptions" :key="name" class="fcp-check">
                      <input
                        type="checkbox"
                        :checked="isAccountNameSelected(name)"
                        @change="toggleAccountName(name)"
                      />
                      {{ maskAccountName(name) }}
                    </label>
                  </b-dropdown-form>
                </b-dropdown>
              </div>
            </div>

            <!-- Demo Mode - superuser only, and only on non-production environments
                 (isDemoModeAvailable, from the backend's ENVIRONMENT setting). See demoMode's own
                 comment in the script. Sits as
                 its own item outside .fcp-toolbar, same as .fcp-kpi does on the other side of it,
                 since it's a display-mode switch rather than a data filter. Reuses .fcp-tristate
                 (identical look to the Variance/FCP toggles above) driven as a plain binary
                 switch - no separate CSS, no VeeValidate-based BaseSwitch needed here. -->
            <div
              v-if="isSuperUser && isDemoModeAvailable"
              class="fcp-toggle fcp-toolbar__item mb-0 d-flex flex-column align-items-center text-center"
            >
              <span class="small-font mb-1 d-block">Demo Mode</span>
              <div
                class="fcp-tristate"
                :class="demoMode ? 'fcp-tristate--right fcp-tristate--green' : 'fcp-tristate--left fcp-tristate--red'"
                @click="demoMode = !demoMode"
              >
                <div class="fcp-tristate__knob"></div>
              </div>
            </div>
          </div>

          <!-- LIQ-2571: opened by the warning triangle above, in Variance Transaction Details'
               place - VTD is hidden (v-show, so its rows, scroll position and selection are still
               there when the panel closes) and the panel takes its slot and size; the REC UMRs/
               BDX/Cashbook row below doesn't move. The triangle can't be clicked while VTD has
               unsaved edits: edit mode locks the toolbar (isEditLocked). -->
          <rec-account-name-reconciliation-panel
            v-if="isAccountNamePanelShown"
            ref="accountNamePanelRef"
            class="fcp-anr-panel"
            @counted="onAccountNamesCounted"
          />

          <!-- Variance Transaction Details is now its own full-width row.
               FCP UMRs/BDX Detail/Cashbook Detail move below it as three
               side-by-side peers (FCP UMRs previously sat beside this table +
               the BDX/Cashbook row combined). fcp-variance-row (distinct from
               the plain "row no-gutters mt-1" below, so the two can be styled
               independently) is what grows to fill leftover vertical space in
               .fcp-col-main - see its own SCSS comment. -->
          <div v-show="!isAccountNamePanelShown" class="row no-gutters fcp-variance-row">
            <div class="col-12">
              <div class="fcp-panel" ref="variancePanelRef">
                <div class="fcp-panel__title fcp-panel__title--variance fcp-panel__title--with-load-more">
                  <span>Variance Transaction Details</span>
                  <div v-if="varianceTotalRows" class="fcp-variance-load-more">
                    <span class="fcp-variance-load-more__count">
                      Showing {{ displayedVarianceRows.length }} of {{ varianceTotalRows }} rows
                    </span>
                    <button
                      v-if="hasMoreVarianceDetails"
                      type="button"
                      class="fcp-variance-load-more__btn"
                      :disabled="varianceLoadingMore"
                      @click="loadMoreVarianceDetails"
                    >
                      {{ varianceLoadingMore ? 'Loading…' : `Load ${varianceLoadMoreCount} more` }}
                    </button>
                  </div>
                  <div v-else></div>
                  <div class="fcp-panel__title-actions d-flex align-items-center">
                    <div v-if="checkedRowKeys.size" class="fcp-variance-edit-bar">
                      <span class="small-font mr-2">{{ checkedRowKeys.size }} row(s) selected</span>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm mr-2 fcp-variance-edit-bar__save"
                        :disabled="savingVarianceEdits"
                        @click="saveVarianceEdits"
                      >
                        {{ savingVarianceEdits ? 'Saving…' : 'Save' }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-link btn-sm p-0 fcp-variance-edit-bar__discard"
                        :disabled="savingVarianceEdits"
                        @click="discardVarianceEdits"
                      >
                        Discard
                      </button>
                    </div>
                    <span class="d-inline-flex align-items-center">
                      <rec-filter-eraser
                        :active="isVarianceTableFilterActive"
                        title="Clear this table's column filters"
                        @clear="clearVarianceTableFilters"
                      />
                      <span
                        class="fcp-download-icon"
                        :class="{ 'fcp-download-icon--disabled': varianceExporting || demoMode }"
                        role="button"
                        :title="demoMode ? DEMO_EXPORT_DISABLED_TITLE : varianceExporting ? 'Exporting…' : 'Export data'"
                        @click="!varianceExporting && !demoMode && downloadVarianceExcel()"
                      >
                        <b-icon icon="download" font-scale="1"></b-icon>
                      </span>
                    </span>
                  </div>
                </div>
                <!-- Cold-hit overlay: a separate, more prominent indicator from b-table's own
                     #table-busy slot below - shown only when isColdHit AND actually loading, so a
                     normal (warm) load still just gets the same spinner with a plain "Loading…"
                     underneath, without the cold-start explanation text or the panel-covering
                     overlay treatment. This only appears for the specific case of a real cold
                     cache hit (see isColdHit/fetchReport's own is_cold handling - a genuine signal
                     from the backend, not a guess). -->
                <b-overlay :show="isColdHit && (loading || varianceLoadingInitial)" rounded="sm" opacity="0.75">
                  <template #overlay>
                    <div class="text-center">
                      <div class="fcp-loading-spinner" role="status" aria-label="Loading"></div>
                      <p class="mt-2 mb-0">
                        Data is loading — this is a cold start (new data just landed), which can
                        take up to about 20 seconds.
                      </p>
                    </div>
                  </template>
                <div class="table-responsive mb-0 variance-table position-relative">
                  <b-table
                    :items="displayedVarianceRows"
                    :fields="varianceFields"
                    :busy="loading || varianceLoadingInitial"
                    sticky-header
                    striped
                    hover
                    small
                    no-local-sorting
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    :tbody-tr-class="(item) => (item && isTransactionRowSelected('variance', item) ? 'fcp-transaction-row--active' : '')"
                    @row-clicked="(item, index, event) => handleTransactionRowClick('variance', item, index, event)"
                    @row-hovered="(item, index, event) => showRowTooltip(item, event)"
                    @row-unhovered="hideRowTooltip"
                    @mousemove.native="moveRowTooltip"
                  >
                    <!-- Status?'s own head() override - a second, independent access point onto
                         approvalStatusFilter, the exact same filter the toolbar's own Review
                         Status dropdown already drives (explicit decision to add rather than
                         migrate, per Variance Type's own precedent). RecStatusFilterMenu.vue
                         is the checklist popover itself, portaled below the table. -->
                    <template #head(variance_status)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <span
                            class="fcp-th-search-toggle"
                            role="button"
                            tabindex="0"
                            @click.stop="statusFilterMenuRef.toggle($event)"
                            @keydown.enter.stop="statusFilterMenuRef.toggle($event)"
                          >
                            <b-icon v-if="!isApprovalStatusFilterActive" icon="search" font-scale="1"></b-icon>
                            <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, `variance:${data.field.key}`)"
                      ></span>
                    </template>
                    <!-- Source's own head() override - a second, independent access point onto
                         sourceFilter, the exact same filter the toolbar's own Source dropdown
                         already drives. Reuses the shared inlineOptionsMenuRef instance (single-
                         select, same as Variance Type/Admin Approval) rather than a new
                         component - see SOURCE_FILTER_MENU_OPTIONS/setSourceFilter's own
                         comments for the "" <-> null mapping this needs that those two don't. -->
                    <template #head(source)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <span
                            class="fcp-th-search-toggle"
                            role="button"
                            tabindex="0"
                            @click.stop="
                              inlineOptionsMenuRef.toggle(
                                'source-column-filter',
                                $event,
                                sourceFilterMenuOptions,
                                sourceFilter || '',
                                setSourceFilter
                              )
                            "
                            @keydown.enter.stop="
                              inlineOptionsMenuRef.toggle(
                                'source-column-filter',
                                $event,
                                sourceFilterMenuOptions,
                                sourceFilter || '',
                                setSourceFilter
                              )
                            "
                          >
                            <b-icon v-if="!isSourceFilterActive" icon="search" font-scale="1"></b-icon>
                            <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, `variance:${data.field.key}`)"
                      ></span>
                    </template>
                    <template #head(__select)>
                      <div class="fcp-th-edit-header">
                        <span>Edit</span>
                        <input
                          type="checkbox"
                          :checked="allVisibleRowsChecked"
                          @change="toggleAllCheckedRows"
                        />
                      </div>
                    </template>
                    <template #cell(__select)="{ item }">
                      <input
                        type="checkbox"
                        :disabled="!isRowCheckable(item)"
                        :checked="checkedRowKeys.has(rowKey(item))"
                        @change="toggleCheckedRow(item)"
                      />
                    </template>
                    <!-- Variance Type's own head() override - its former toolbar dropdown
                         (checklist + its own search-to-narrow box) moved here wholesale,
                         behind the same search icon every other searchable column uses,
                         instead of the plain text box the generic #head() below renders.
                         varianceTypeFilter/varianceTypeSearch/etc. are unchanged; only
                         where their UI lives moved. -->
                    <template #head(variance_type)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <b-dropdown
                            @hidden="resetChecklistSearches"
                            toggle-class="fcp-th-search-toggle"
                            menu-class="fcp-checklist-menu fcp-checklist-menu--column-header fcp-checklist-menu--custom-checkbox"
                            variant="link"
                            no-caret
                            boundary="window"
                            :popper-opts="dropdownPopperOpts()"
                          >
                            <template #button-content>
                              <b-icon v-if="!isVarianceTypeFilterActive" icon="search" font-scale="1"></b-icon>
                              <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                            </template>
                            <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredVarianceTypes">
                              <div class="fcp-checklist-search">
                                <b-icon icon="search" font-scale="0.8"></b-icon>
                                <input
                                  type="text"
                                  v-model="varianceTypeSearch"
                                  placeholder="Search"
                                  class="fcp-checklist-search__input"
                                />
                              </div>
                              <label class="fcp-check">
                                <input
                                  type="checkbox"
                                  :checked="allVarianceTypesSelected"
                                  @change="toggleAllVarianceTypes"
                                />
                                Select all
                              </label>
                              <label v-for="type in filteredVarianceTypeOptions" :key="type" class="fcp-check">
                                <input
                                  type="checkbox"
                                  :checked="isVarianceTypeSelected(type)"
                                  @change="toggleVarianceType(type)"
                                />
                                {{ type }}
                              </label>
                            </b-dropdown-form>
                          </b-dropdown>
                          <span
                            class="fcp-th-sort-icon"
                            role="button"
                            @click.stop="toggleColumnSort('variance_type')"
                          >
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'variance_type' === sortBy && !sortDesc }"
                            ></span>
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'variance_type' === sortBy && sortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, 'variance:variance_type')"
                      ></span>
                    </template>
                    <!-- Admin Approval's own head() override - same structural/style treatment as
                         Variance Type's own dropdown above (inline b-dropdown-form checklist
                         behind the search icon), but reuses approvalStatusFilter - the exact same
                         state backing the Status? column's own dropdown/toolbar Review Status
                         dropdown - rather than a new, separate filter, since Admin Approval's
                         displayed value (see #cell(admin_approval) above) is itself derived 1:1
                         from variance_status; picking a status here also reflects in the Status?
                         column's own dropdown and vice versa. Sort routes through the same generic
                         toggleColumnSort/sort_by plumbing Variance Type already uses, sorting by
                         variance_status - there's no separate "admin_approval" field on the
                         backend to sort by. -->
                    <template #head(admin_approval)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <b-dropdown
                            toggle-class="fcp-th-search-toggle"
                            menu-class="fcp-checklist-menu fcp-checklist-menu--column-header fcp-checklist-menu--custom-checkbox"
                            variant="link"
                            no-caret
                            boundary="window"
                            :popper-opts="dropdownPopperOpts()"
                          >
                            <template #button-content>
                              <b-icon v-if="!isApprovalStatusFilterActive" icon="search" font-scale="1"></b-icon>
                              <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                            </template>
                            <b-dropdown-form @click.stop @submit.prevent>
                              <label class="fcp-check">
                                <input
                                  type="checkbox"
                                  :checked="allReviewStatusSelected"
                                  @change="toggleAllReviewStatus"
                                />
                                Select all
                              </label>
                              <label
                                v-for="option in ADMIN_APPROVAL_FILTER_DISPLAY_OPTIONS"
                                :key="option.value"
                                class="fcp-check"
                              >
                                <input
                                  type="checkbox"
                                  :checked="isReviewStatusSelected(option.value)"
                                  @change="toggleReviewStatus(option.value)"
                                />
                                {{ option.label }}
                              </label>
                            </b-dropdown-form>
                          </b-dropdown>
                          <span
                            class="fcp-th-sort-icon"
                            role="button"
                            @click.stop="toggleColumnSort('variance_status')"
                          >
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'variance_status' === sortBy && !sortDesc }"
                            ></span>
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'variance_status' === sortBy && sortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, 'variance:admin_approval')"
                      ></span>
                    </template>
                    <!-- UMR's own head() override - same structural/style treatment as Account
                         Name's own dropdown below (inline b-dropdown-form checklist with its own
                         search-to-narrow box, behind the search icon), reusing umrFilter/
                         umrSearch/etc. wholesale - the exact same state backing the toolbar's own
                         UMR dropdown - rather than a new, separate filter. Both stay in sync since
                         they share these same refs. Per explicit request (this used to be a plain
                         text search box against columnSearch, same treatment as Submitter
                         Comments/Admin Comments still get). -->
                    <template #head(umr)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <b-dropdown
                            @hidden="resetChecklistSearches"
                            toggle-class="fcp-th-search-toggle"
                            menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                            variant="link"
                            no-caret
                            boundary="window"
                            :popper-opts="dropdownPopperOpts()"
                            lazy
                          >
                            <template #button-content>
                              <b-icon v-if="!isUmrFilterActive" icon="search" font-scale="1"></b-icon>
                              <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                            </template>
                            <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredUmrs">
                              <div class="fcp-checklist-search">
                                <b-icon icon="search" font-scale="0.8"></b-icon>
                                <input
                                  type="text"
                                  v-model="umrSearch"
                                  placeholder="Search"
                                  class="fcp-checklist-search__input"
                                />
                              </div>
                              <label class="fcp-check">
                                <input type="checkbox" :checked="allUmrsSelected" @change="toggleAllUmrs" />
                                Select all
                              </label>
                              <label v-for="umr in filteredUmrOptions" :key="umr" class="fcp-check">
                                <input
                                  type="checkbox"
                                  :checked="isUmrSelected(umr)"
                                  @change="toggleUmr(umr)"
                                />
                                {{ maskUmr(umr) }}
                              </label>
                            </b-dropdown-form>
                          </b-dropdown>
                          <span
                            class="fcp-th-sort-icon"
                            role="button"
                            @click.stop="toggleColumnSort('umr')"
                          >
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === sortBy && !sortDesc }"
                            ></span>
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === sortBy && sortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, 'variance:umr')"
                      ></span>
                    </template>
                    <!-- Claim Reference's own head() override - same treatment as UMR's own above,
                         reusing claimFilter/claimSearch/etc. wholesale - the exact same state
                         backing the toolbar's own Claim Reference dropdown. -->
                    <template #head(claim)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <b-dropdown
                            @hidden="resetChecklistSearches"
                            toggle-class="fcp-th-search-toggle"
                            menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                            variant="link"
                            no-caret
                            boundary="window"
                            :popper-opts="dropdownPopperOpts()"
                            lazy
                          >
                            <template #button-content>
                              <b-icon v-if="!isClaimFilterActive" icon="search" font-scale="1"></b-icon>
                              <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                            </template>
                            <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredClaims">
                              <div class="fcp-checklist-search">
                                <b-icon icon="search" font-scale="0.8"></b-icon>
                                <input
                                  type="text"
                                  v-model="claimSearch"
                                  placeholder="Search"
                                  class="fcp-checklist-search__input"
                                />
                              </div>
                              <label class="fcp-check">
                                <input type="checkbox" :checked="allClaimsSelected" @change="toggleAllClaims" />
                                Select all
                              </label>
                              <label v-for="claim in displayedClaimOptions" :key="claim" class="fcp-check">
                                <input
                                  type="checkbox"
                                  :checked="isClaimSelected(claim)"
                                  @change="toggleClaim(claim)"
                                />
                                {{ maskClaim(claim) }}
                              </label>
                              <div v-if="hiddenClaimCount" class="small text-muted px-2 py-1">
                                +{{ hiddenClaimCount.toLocaleString() }} more claims, type to see the claim
                              </div>
                            </b-dropdown-form>
                          </b-dropdown>
                          <span
                            class="fcp-th-sort-icon"
                            role="button"
                            @click.stop="toggleColumnSort('claim')"
                          >
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'claim' === sortBy && !sortDesc }"
                            ></span>
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'claim' === sortBy && sortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, 'variance:claim')"
                      ></span>
                    </template>
                    <!-- Account Name's own head() override - same structural/style treatment as
                         Variance Type's own dropdown above (inline b-dropdown-form checklist with
                         its own search-to-narrow box, behind the search icon), reusing
                         accountNameFilter/accountNameSearch/etc. wholesale - the exact same state
                         backing the toolbar's own Matched Account Name dropdown - rather than a
                         new, separate filter. Both stay in sync since they share these same refs.
                         menu-class uses --column-header-scrollable, NOT plain --column-header -
                         Account Name's list is genuinely unbounded (unlike Variance Type/Admin
                         Approval's small fixed option sets), so it needs a real width/height cap
                         and scrollbar (see that class' own comment in the .scss). Also the only
                         one of these four with the `right` prop - Account Name is the rightmost
                         column, so its search icon sits near the page's right edge; left-aligning
                         the menu (every other column's default) pushed it off the page (reported
                         bug). `right` opens it growing leftward from the icon instead. -->
                    <template #head(account_name)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <b-dropdown
                            @hidden="resetChecklistSearches"
                            toggle-class="fcp-th-search-toggle"
                            menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--custom-checkbox"
                            variant="link"
                            no-caret
                            right
                            boundary="window"
                            :popper-opts="dropdownPopperOpts()"
                            lazy
                          >
                            <template #button-content>
                              <b-icon v-if="!isAccountNameFilterActive" icon="search" font-scale="1"></b-icon>
                              <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                            </template>
                            <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredAccountNames">
                              <div class="fcp-checklist-search">
                                <b-icon icon="search" font-scale="0.8"></b-icon>
                                <input
                                  type="text"
                                  v-model="accountNameSearch"
                                  placeholder="Search"
                                  class="fcp-checklist-search__input"
                                />
                              </div>
                              <label class="fcp-check">
                                <input
                                  type="checkbox"
                                  :checked="allAccountNamesSelected"
                                  @change="toggleAllAccountNames"
                                />
                                Select all
                              </label>
                              <label v-for="name in filteredAccountNameOptions" :key="name" class="fcp-check">
                                <input
                                  type="checkbox"
                                  :checked="isAccountNameSelected(name)"
                                  @change="toggleAccountName(name)"
                                />
                                {{ maskAccountName(name) }}
                              </label>
                            </b-dropdown-form>
                          </b-dropdown>
                          <span
                            class="fcp-th-sort-icon"
                            role="button"
                            @click.stop="toggleColumnSort('account_name')"
                          >
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'account_name' === sortBy && !sortDesc }"
                            ></span>
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'account_name' === sortBy && sortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, 'variance:account_name')"
                      ></span>
                    </template>
                    <!-- Report Period's own head() override - same structural/style treatment as
                         Account Name's own dropdown above (column-header search icon triggering
                         an inline b-dropdown-form, sized/scrollable via
                         --column-header-scrollable), but reuses selectedPeriods/periodTree/etc.
                         wholesale - the exact same state and Year > Month tree UI backing Column
                         A's own Report Period box - rather than a new, separate filter/UI. Both
                         stay in sync since they share these same refs, EXCEPT expand/collapse
                         state - that's its own periodDropdownExpandedYears (see its own comment),
                         NOT the shared expandedYears Column A uses, so opening/closing a year here
                         doesn't also shift Column A's own box open/closed (reported bug: it did,
                         when this first shared expandedYears wholesale too). Unlike every other
                         filter here, selectedPeriods is the one that's genuinely
                         server-side (see its own watch()) - toggling a checkbox here fires the
                         exact same debounced refetch Column A's own tree already does. Also
                         carries --custom-checkbox (per explicit request to match Column A's own
                         look) - replaces the plain native checkboxes every other column-header
                         dropdown here keeps with the same black-square/indeterminate-dot styling
                         .select-report-period's own tree already uses (see that class' own
                         comment in the .scss - already shared by the Party/Review Status/UMR/
                         Claim/Account Name toolbar dropdowns, not a new style being introduced). -->
                    <template #head(report_month_year)="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span class="fcp-th-actions">
                          <b-dropdown
                            toggle-class="fcp-th-search-toggle"
                            menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--custom-checkbox fcp-checklist-menu--period-tree"
                            variant="link"
                            no-caret
                            boundary="window"
                            :popper-opts="dropdownPopperOpts()"
                          >
                            <template #button-content>
                              <b-icon v-if="!isPeriodFilterActive" icon="search" font-scale="1"></b-icon>
                              <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                            </template>
                            <b-dropdown-form @click.stop @submit.prevent>
                              <label class="fcp-check">
                                <input type="checkbox" :checked="allPeriodsSelected" @change="toggleAllPeriods" />
                                Select all
                              </label>
                              <div v-for="group in periodTree" :key="group.year" class="fcp-period-year">
                                <label class="fcp-check fcp-check--year font-weight-bold">
                                  <b-icon
                                    :icon="isPeriodDropdownYearExpanded(group.year) ? 'chevron-down' : 'chevron-right'"
                                    font-scale="0.8"
                                    class="mr-1 fcp-check__chevron"
                                    role="button"
                                    @click.prevent="togglePeriodDropdownYearExpanded(group.year)"
                                  ></b-icon>
                                  <input
                                    type="checkbox"
                                    :checked="isYearSelected(group)"
                                    :indeterminate.prop="isYearPartiallySelected(group)"
                                    @change="toggleYear(group)"
                                  />
                                  {{ group.year }}
                                </label>
                                <template v-if="isPeriodDropdownYearExpanded(group.year)">
                                  <label
                                    v-for="month in group.months"
                                    :key="month.key"
                                    class="fcp-check fcp-check--indent"
                                  >
                                    <input
                                      type="checkbox"
                                      :checked="selectedPeriods.includes(month.key)"
                                      @change="togglePeriod(month.key)"
                                    />
                                    {{ month.label }}
                                  </label>
                                </template>
                              </div>
                            </b-dropdown-form>
                          </b-dropdown>
                          <span
                            class="fcp-th-sort-icon"
                            role="button"
                            @click.stop="toggleColumnSort('report_month_year')"
                          >
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === sortBy && !sortDesc }"
                            ></span>
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                              :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === sortBy && sortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, 'variance:report_month_year')"
                      ></span>
                    </template>
                    <template #head()="data">
                      <div class="fcp-th-resizer" :style="columnStyle(`variance:${data.field.key}`)">
                        <span class="fcp-th-resizer__label">{{ data.label }}</span>
                        <span v-if="HEADER_SEARCH_SORT_FIELD_KEYS.includes(data.field.key)" class="fcp-th-actions">
                          <span
                            class="fcp-th-search-toggle"
                            role="button"
                            tabindex="0"
                            @click.stop="columnSearchMenuRef.toggle(data.field.key, $event, columnSearchTypeFor(data.field.key))"
                            @keydown.enter.stop="columnSearchMenuRef.toggle(data.field.key, $event, columnSearchTypeFor(data.field.key))"
                          >
                            <b-icon v-if="!isColumnSearchActive(data.field.key)" icon="search" font-scale="1"></b-icon>
                            <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                          </span>
                          <span
                            class="fcp-th-sort-icon"
                            role="button"
                            @click.stop="toggleColumnSort(data.field.key)"
                          >
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                              :class="{ 'fcp-th-sort-icon__caret--active': sortBy === data.field.key && !sortDesc }"
                            ></span>
                            <span
                              class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                              :class="{ 'fcp-th-sort-icon__caret--active': sortBy === data.field.key && sortDesc }"
                            ></span>
                          </span>
                        </span>
                      </div>
                      <span
                        class="fcp-col-resize-handle"
                        @mousedown="startColumnResize($event, `variance:${data.field.key}`)"
                      ></span>
                    </template>
                    <template #cell()="{ value, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">{{ maskCellValue(field.key, value) }}</div>
                    </template>
                    <template #cell(source)="{ value, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">{{ 'FCP' === value ? 'CB' : value }}</div>
                    </template>
                    <template #cell(total_paid_bdx)="{ value, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">{{ formatCurrencyNoSymbol(value) }}</div>
                    </template>
                    <template #cell(total_paid_fcp)="{ value, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">{{ formatCurrencyNoSymbol(value) }}</div>
                    </template>
                    <template #cell(variance_amount)="{ value, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">
                        <span class="text-danger font-weight-bold">{{ formatCurrencyNoSymbol(value) }}</span>
                      </div>
                    </template>
                    <template #cell(variance_status)="{ value, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">
                        <b-icon v-if="'Approved' === value" icon="check-circle-fill" variant="success"></b-icon>
                        <b-icon v-else-if="'Pending Approval' === value" icon="flag-fill" class="fcp-icon-pending"></b-icon>
                        <b-icon v-else-if="'Rejected' === value" icon="x-circle-fill" variant="danger"></b-icon>
                        <b-icon v-else-if="'Not Submitted' === value" icon="flag-fill" variant="danger"></b-icon>
                        <b-icon v-else icon="dash-circle" variant="secondary"></b-icon>
                      </div>
                    </template>
                    <template #cell(variance_type)="{ value, item, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">
                        <template v-if="isRowEditableBySubmitter(item)">
                          <div
                            class="fcp-variance-edit-input fcp-inline-options-toggle"
                            :class="{ 'fcp-variance-edit-input--invalid': isVarianceTypeInvalid(item) }"
                            role="button"
                            tabindex="0"
                            @click.stop="
                              inlineOptionsMenuRef.toggle(
                                `${rowKey(item)}:variance_type`,
                                $event,
                                VARIANCE_TYPE_MENU_OPTIONS,
                                varianceTypeChoice(item),
                                (choice) => onVarianceTypeChange(item, choice)
                              )
                            "
                          >
                            <span class="fcp-inline-options-toggle__label">{{ varianceTypeChoice(item) || 'Select…' }}</span>
                            <b-icon icon="chevron-down" font-scale="0.7"></b-icon>
                          </div>
                          <input
                            v-if="'Other' === varianceTypeChoice(item)"
                            type="text"
                            class="form-control form-control-sm fcp-variance-edit-input mt-1"
                            :class="{ 'fcp-variance-edit-input--invalid': isVarianceTypeInvalid(item) }"
                            placeholder="Describe reason"
                            :value="varianceTypeOtherText(item)"
                            @input="onVarianceTypeOtherInput(item, $event.target.value)"
                          />
                        </template>
                        <template v-else>{{ value }}</template>
                      </div>
                    </template>
                    <template #cell(submission_date)="{ value, item, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)" :title="submitDateTitle(item)">
                        {{ formatDate(pendingSubmissionDate(item) || value) }}
                      </div>
                    </template>
                    <template #cell(submit_variance_comment)="{ value, item, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)" :title="value">
                        <template v-if="isRowEditableBySubmitter(item)">
                          <input
                            type="text"
                            class="form-control form-control-sm fcp-variance-edit-input"
                            :class="{ 'fcp-variance-edit-input--invalid': isCommentInvalid(item) }"
                            :value="pendingComment(item)"
                            @input="onCommentInput(item, $event.target.value)"
                          />
                        </template>
                        <template v-else>{{ value }}</template>
                      </div>
                    </template>
                    <template #cell(admin_approval)="{ item, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)">
                        <template v-if="isRowEditableByAdmin(item)">
                          <div
                            class="fcp-variance-edit-input fcp-inline-options-toggle"
                            role="button"
                            tabindex="0"
                            @click.stop="
                              inlineOptionsMenuRef.toggle(
                                `${rowKey(item)}:admin_approval`,
                                $event,
                                adminApprovalMenuOptions,
                                adminApprovalChoice(item),
                                (choice) => onAdminApprovalChange(item, choice)
                              )
                            "
                          >
                            <span class="fcp-inline-options-toggle__label">{{ adminApprovalChoice(item) || 'Select…' }}</span>
                            <b-icon icon="chevron-down" font-scale="0.7"></b-icon>
                          </div>
                        </template>
                        <template v-else>
                          <b-icon v-if="'Approved' === item.variance_status" icon="check-circle-fill" variant="success"></b-icon>
                          <b-icon v-else-if="'Pending Approval' === item.variance_status" icon="flag-fill" class="fcp-icon-pending"></b-icon>
                          <b-icon v-else-if="'Rejected' === item.variance_status" icon="x-circle-fill" variant="danger"></b-icon>
                          <b-icon v-else icon="dash-circle" variant="secondary"></b-icon>
                        </template>
                      </div>
                    </template>
                    <template #cell(admin_variance_comment)="{ value, item, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)" :title="value">
                        <template v-if="isRowEditableByAdmin(item)">
                          <input
                            type="text"
                            class="form-control form-control-sm fcp-variance-edit-input"
                            :class="{ 'fcp-variance-edit-input--invalid': isAdminCommentInvalid(item) }"
                            :value="pendingAdminComment(item)"
                            @input="onAdminCommentInput(item, $event.target.value)"
                          />
                        </template>
                        <template v-else>{{ value }}</template>
                      </div>
                    </template>
                    <template #cell(admin_variance_date)="{ value, item, field }">
                      <div class="fcp-td-resizer" :style="columnStyle(`variance:${field.key}`)" :title="adminDateTitle(item)">
                        {{ formatDate(pendingAdminDate(item) || value) }}
                      </div>
                    </template>

                    <template #bottom-row>
                      <td></td>
                      <td class="font-weight-bold">
                        <div class="fcp-td-resizer" :style="columnStyle('variance:report_month_year')">
                          Total ({{ varianceTotalRows }})
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td class="font-weight-bold text-right">
                        <div class="fcp-td-resizer" :style="columnStyle('variance:total_paid_bdx')">
                          {{ formatCurrencyNoSymbol(varianceTotals.bdx) }}
                        </div>
                      </td>
                      <td class="font-weight-bold text-right">
                        <div class="fcp-td-resizer" :style="columnStyle('variance:total_paid_fcp')">
                          {{ formatCurrencyNoSymbol(varianceTotals.fcp) }}
                        </div>
                      </td>
                      <td class="font-weight-bold text-danger text-right">
                        <div class="fcp-td-resizer" :style="columnStyle('variance:variance_amount')">
                          {{ formatCurrencyNoSymbol(varianceTotals.variance) }}
                        </div>
                      </td>
                      <td v-if="canEditVariance" class="fcp-variance-select-cell"></td>
                      <td colspan="7"></td>
                    </template>

                    <template #table-busy>
                      <div v-if="!isColdHit" class="fcp-table-busy d-flex flex-column align-items-center justify-content-center text-muted">
                        <div class="fcp-loading-spinner" role="status" aria-label="Loading"></div>
                        <p class="mt-2 mb-0">Loading…</p>
                      </div>
                    </template>
                    <template #empty>
                      <div class="text-center text-muted py-3">No variance rows match the current filters.</div>
                    </template>
                  </b-table>
                  <div v-if="noReportPeriodSelected" class="fcp-no-periods">No report period selected</div>
                </div>
                </b-overlay>

                <!-- Covers the whole panel (title bar + table), not just the table, so the
                     Save/Discard buttons themselves are visually blocked too while a save is in
                     flight - matches disabling them below, not just a cosmetic addition. -->
                <div v-if="savingVarianceEdits" class="fcp-variance-saving-overlay">Saving...</div>

                <!-- Own open/closed position state lives entirely inside this child component
                     (see RecColumnSearchMenu.vue) rather than a ref on this component - keeping it
                     out of this component's own reactive graph is deliberate: this table's ~15
                     #cell() scoped slots aren't "stable" to bootstrap-vue's b-table, so any reactive
                     change read during THIS component's own render forces a full re-render of every
                     row/cell (confirmed via profiling: opening the popover was costing 7-10s over
                     ~100 rows) - a plain ref here would pay that cost on every open/close even though
                     nothing in the table's data changed. columnSearch (the actual per-column search
                     text, which SHOULD narrow the table) still lives up here and flows down as a
                     prop/event pair, same as any other controlled-by-parent component. -->
                <rec-column-search-menu
                  ref="columnSearchMenuRef"
                  :column-search="columnSearch"
                  :range-bounds="varianceRangeBounds"
                  @search="setColumnSearch"
                />

                <!-- BDX Detail by Transaction's own popover instance - own separate columnSearch
                     object (bdxColumnSearch), so it can't collide with Variance's columnSearch
                     above even where a field key is spelled the same (e.g. total_paid_bdx exists
                     on both tables). range-bounds comes straight from /bdx-details' own response
                     (bdxRangeBounds - see fetchBdxDetails), same as Variance's own
                     varianceRangeBounds above - both are real, server-side cross-filtered min/max
                     (service.numeric_column_bounds) over the FULL dataset, not computed
                     client-side from whatever rows happen to already be loaded. -->
                <rec-column-search-menu
                  ref="bdxColumnSearchMenuRef"
                  :column-search="bdxColumnSearch"
                  :range-bounds="bdxRangeBounds"
                  @search="setBdxColumnSearch"
                />

                <!-- Cashbook Details by Transaction counterpart to the BDX popover above - same
                     reasoning, own separate cashbookColumnSearch/cashbookRangeBounds. -->
                <rec-column-search-menu
                  ref="cashbookColumnSearchMenuRef"
                  :column-search="cashbookColumnSearch"
                  :range-bounds="cashbookRangeBounds"
                  @search="setCashbookColumnSearch"
                />

                <!-- Variance Type/Admin Approval's own dropdown - see RecInlineOptionsMenu.vue's
                     own top comment for why this replaces a native <select> (option-list highlight
                     color isn't stylable there) and why it's a single shared instance, same
                     reasoning as rec-column-search-menu above. -->
                <rec-inline-options-menu ref="inlineOptionsMenuRef" />

                <!-- Status? column's own checklist - see #head(variance_status) above and
                     RecStatusFilterMenu.vue's own top comment. approvalStatusFilter itself stays
                     up here (same as columnSearch does for rec-column-search-menu) since it's
                     also read by the toolbar's own Review Status dropdown and by
                     displayedVarianceRows' own filtering - only this checklist's open/closed
                     state is isolated to the child. -->
                <rec-status-filter-menu
                  ref="statusFilterMenuRef"
                  :options="reviewStatusOptions"
                  :selected-options="approvalStatusFilter || []"
                  :all-selected="allReviewStatusSelected"
                  @toggle-all="toggleAllReviewStatus"
                  @toggle-option="toggleReviewStatus"
                />
              </div>
            </div>
          </div>

          <!-- FCP UMRs, BDX Detail, and Cashbook Detail — side by side, right
               under Variance Transaction Details -->
          <div class="row no-gutters mt-1">
            <div class="col-12 col-lg-3 pr-lg-3 mb-1 fcp-col-umr">
              <div class="fcp-umr-panel">
                <div class="fcp-panel">
                  <div class="fcp-panel__title d-flex align-items-center justify-content-between">
                    <span>REC UMRs</span>
                    <span
                      v-if="renderedFcpUmrList.length < displayedFcpUmrList.length"
                      class="fcp-variance-load-more__count"
                    >
                      Showing {{ renderedFcpUmrList.length }} of {{ displayedFcpUmrList.length }}
                    </span>
                    <span class="d-inline-flex align-items-center">
                      <rec-filter-eraser
                        :active="isRecUmrsTableFilterActive"
                        title="Clear this table's column filters"
                        @clear="clearRecUmrsTableFilters"
                      />
                      <span
                        class="fcp-download-icon"
                        :class="{ 'fcp-download-icon--disabled': umrExporting }"
                        role="button"
                        :title="umrExporting ? 'Exporting…' : 'Export data'"
                        @click="!umrExporting && downloadUmrExcel()"
                      >
                        <b-icon icon="download" font-scale="1"></b-icon>
                      </span>
                    </span>
                  </div>
                  <div class="fcp-umr-list position-relative" @scroll.passive="onRecUmrListScroll">
                    <table
                      class="table table-sm mb-0 umr-table"
                      :aria-busy="recUmrLoading ? 'true' : 'false'"
                    >
                      <thead>
                        <tr>
                          <!-- Reuses selectedPeriods/periodTree/etc. wholesale - the exact same
                               state backing Column A's own Report Period box and Variance
                               Transaction Details' own Report Period column - rather than a new,
                               separate filter. Own independent expand-state (see
                               umrPanelPeriodDropdownExpandedYears' own comment), same reasoning
                               as Variance's own copy. -->
                          <th>
                            <div class="fcp-th-resizer">
                              <span class="fcp-th-resizer__label">Report Period</span>
                              <span class="fcp-th-actions">
                                <b-dropdown
                                  toggle-class="fcp-th-search-toggle"
                                  menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--custom-checkbox fcp-checklist-menu--period-tree"
                                  variant="link"
                                  no-caret
                                  boundary="window"
                                  :popper-opts="dropdownPopperOpts()"
                                >
                                  <template #button-content>
                                    <b-icon v-if="!isPeriodFilterActive" icon="search" font-scale="1"></b-icon>
                                    <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                  </template>
                                  <b-dropdown-form @click.stop @submit.prevent>
                                    <label class="fcp-check">
                                      <input type="checkbox" :checked="allPeriodsSelected" @change="toggleAllPeriods" />
                                      Select all
                                    </label>
                                    <div v-for="group in periodTree" :key="group.year" class="fcp-period-year">
                                      <label class="fcp-check fcp-check--year font-weight-bold">
                                        <b-icon
                                          :icon="isUmrPanelPeriodDropdownYearExpanded(group.year) ? 'chevron-down' : 'chevron-right'"
                                          font-scale="0.8"
                                          class="mr-1 fcp-check__chevron"
                                          role="button"
                                          @click.prevent="toggleUmrPanelPeriodDropdownYearExpanded(group.year)"
                                        ></b-icon>
                                        <input
                                          type="checkbox"
                                          :checked="isYearSelected(group)"
                                          :indeterminate.prop="isYearPartiallySelected(group)"
                                          @change="toggleYear(group)"
                                        />
                                        {{ group.year }}
                                      </label>
                                      <template v-if="isUmrPanelPeriodDropdownYearExpanded(group.year)">
                                        <label
                                          v-for="month in group.months"
                                          :key="month.key"
                                          class="fcp-check fcp-check--indent"
                                        >
                                          <input
                                            type="checkbox"
                                            :checked="selectedPeriods.includes(month.key)"
                                            @change="togglePeriod(month.key)"
                                          />
                                          {{ month.label }}
                                        </label>
                                      </template>
                                    </div>
                                  </b-dropdown-form>
                                </b-dropdown>
                                <span
                                  class="fcp-th-sort-icon"
                                  role="button"
                                  @click.stop="toggleUmrPanelSort('report_month_year')"
                                >
                                  <span
                                    class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                    :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === umrPanelSortBy && !umrPanelSortDesc }"
                                  ></span>
                                  <span
                                    class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                    :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === umrPanelSortBy && umrPanelSortDesc }"
                                  ></span>
                                </span>
                              </span>
                            </div>
                          </th>
                          <!-- Reuses approvalStatusFilter/statusFilterMenuRef wholesale - the
                               exact same shared popover Variance Transaction Details' own
                               Status? column already drives. -->
                          <th>
                            <div class="fcp-th-resizer">
                              <span class="fcp-th-resizer__label">Status?</span>
                              <span class="fcp-th-actions">
                                <span
                                  class="fcp-th-search-toggle"
                                  role="button"
                                  tabindex="0"
                                  @click.stop="statusFilterMenuRef.toggle($event)"
                                  @keydown.enter.stop="statusFilterMenuRef.toggle($event)"
                                >
                                  <b-icon v-if="!isApprovalStatusFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </span>
                                <span
                                  class="fcp-th-sort-icon"
                                  role="button"
                                  @click.stop="toggleUmrPanelSort('variance_status')"
                                >
                                  <span
                                    class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                    :class="{ 'fcp-th-sort-icon__caret--active': 'variance_status' === umrPanelSortBy && !umrPanelSortDesc }"
                                  ></span>
                                  <span
                                    class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                    :class="{ 'fcp-th-sort-icon__caret--active': 'variance_status' === umrPanelSortBy && umrPanelSortDesc }"
                                  ></span>
                                </span>
                              </span>
                            </div>
                          </th>
                          <!-- Reuses umrFilter/umrSearch/etc. wholesale - the exact same state
                               backing the toolbar's own UMR dropdown and Variance Transaction
                               Details' own UMR column. -->
                          <th>
                            <div class="fcp-th-resizer">
                              <span class="fcp-th-resizer__label">UMR</span>
                              <span class="fcp-th-actions">
                                <b-dropdown
                                  @hidden="resetChecklistSearches"
                                  toggle-class="fcp-th-search-toggle"
                                  menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                                  variant="link"
                                  no-caret
                                  boundary="window"
                                  :popper-opts="dropdownPopperOpts()"
                                  lazy
                                >
                                  <template #button-content>
                                    <b-icon v-if="!isUmrFilterActive" icon="search" font-scale="1"></b-icon>
                                    <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                  </template>
                                  <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredUmrs">
                                    <div class="fcp-checklist-search">
                                      <b-icon icon="search" font-scale="0.8"></b-icon>
                                      <input
                                        type="text"
                                        v-model="umrSearch"
                                        placeholder="Search"
                                        class="fcp-checklist-search__input"
                                      />
                                    </div>
                                    <label class="fcp-check">
                                      <input type="checkbox" :checked="allUmrsSelected" @change="toggleAllUmrs" />
                                      Select all
                                    </label>
                                    <label v-for="umr in filteredUmrOptions" :key="umr" class="fcp-check">
                                      <input
                                        type="checkbox"
                                        :checked="isUmrSelected(umr)"
                                        @change="toggleUmr(umr)"
                                      />
                                      {{ maskUmr(umr) }}
                                    </label>
                                  </b-dropdown-form>
                                </b-dropdown>
                                <span
                                  class="fcp-th-sort-icon"
                                  role="button"
                                  @click.stop="toggleUmrPanelSort('umr')"
                                >
                                  <span
                                    class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                    :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === umrPanelSortBy && !umrPanelSortDesc }"
                                  ></span>
                                  <span
                                    class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                    :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === umrPanelSortBy && umrPanelSortDesc }"
                                  ></span>
                                </span>
                              </span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- Row clicks ignored while recUmrLoading, same as bootstrap-vue's own
                             stopIfBusy does for BDX/Cashbook - clicking a dimmed row would drill into
                             the previous selection's data. -->
                        <tr
                          v-for="row in renderedFcpUmrList"
                          :key="`${row.report_month_year}-${row.umr}`"
                          role="button"
                          :class="{ 'fcp-umr-list__row--active': isUmrRowSelected(row.umr) }"
                          @click="!recUmrLoading && selectUmrFromList(row.umr)"
                        >
                          <td>{{ row.report_month_year }}</td>
                          <td class="text-center">
                            <b-icon v-if="!row.has_variance || 'Approved' === row.variance_status" icon="check-lg" variant="success"></b-icon>
                            <b-icon v-else-if="'Pending Approval' === row.variance_status" icon="flag-fill" class="fcp-icon-pending"></b-icon>
                            <b-icon v-else icon="flag-fill" variant="danger"></b-icon>
                          </td>
                          <td>{{ maskUmr(row.umr) }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="noReportPeriodSelected" class="fcp-no-periods">No report period selected</div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="'cashbook' !== sourceFilter"
              class="col-12 mb-1"
              :class="'bdx' === sourceFilter ? 'col-lg-9' : 'col-lg-4-5 pr-lg-3'"
            >
              <div class="fcp-panel">
                <div class="fcp-panel__title d-flex align-items-center justify-content-between">
                  <span>BDX Detail by Transaction</span>
                  <div v-if="bdxTotalRows" class="fcp-variance-load-more">
                    <span class="fcp-variance-load-more__count">
                      Showing {{ bdxDetailRows.length }} of {{ bdxTotalRows }} rows
                    </span>
                    <button
                      v-if="hasMoreBdxDetails"
                      type="button"
                      class="fcp-variance-load-more__btn"
                      :disabled="bdxLoadingMore"
                      @click="loadMoreBdxDetails"
                    >
                      {{ bdxLoadingMore ? 'Loading…' : `Load ${bdxLoadMoreCount} more` }}
                    </button>
                  </div>
                  <div v-else></div>
                  <span class="d-inline-flex align-items-center">
                    <rec-filter-eraser
                      :active="isBdxTableFilterActive"
                      title="Clear this table's column filters"
                      @clear="clearBdxTableFilters"
                    />
                    <span
                      class="fcp-download-icon"
                      :class="{ 'fcp-download-icon--disabled': bdxExporting || demoMode }"
                      role="button"
                      :title="demoMode ? DEMO_EXPORT_DISABLED_TITLE : bdxExporting ? 'Exporting…' : 'Export data'"
                      @click="!bdxExporting && !demoMode && downloadBdxExcel()"
                    >
                      <b-icon icon="download" font-scale="1"></b-icon>
                    </span>
                  </span>
                </div>
                <div class="table-responsive mb-0 fcp-detail-table position-relative">
                      <b-table
                        :items="bdxDetailRows"
                        :fields="bdxFields"
                        :busy="bdxLoadingInitial"
                        sticky-header="200px"
                        small
                        striped
                        hover
                        :tbody-tr-class="(item) => (item && isTransactionRowSelected('bdx', item) ? 'fcp-transaction-row--active' : '')"
                        @row-clicked="(item, index, event) => handleTransactionRowClick('bdx', item, index, event)"
                      >
                        <!-- Report Period's own head() override - same structural/style treatment
                             as Variance Transaction Details' own Report Period column, reusing
                             selectedPeriods/periodTree/etc. wholesale - the exact same state
                             backing Column A's own Report Period box - rather than a new, separate
                             filter (this column is already server-filtered by selectedPeriods via
                             bdxDetailsFilterParams, this just gives it a second, on-column access
                             point). Own independent expand state - see
                             bdxPeriodDropdownExpandedYears' own comment. -->
                        <template #head(report_month_year)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`bdx:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--custom-checkbox fcp-checklist-menu--period-tree"
                                variant="link"
                                no-caret
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                              >
                                <template #button-content>
                                  <b-icon v-if="!isPeriodFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent>
                                  <label class="fcp-check">
                                    <input type="checkbox" :checked="allPeriodsSelected" @change="toggleAllPeriods" />
                                    Select all
                                  </label>
                                  <div v-for="group in periodTree" :key="group.year" class="fcp-period-year">
                                    <label class="fcp-check fcp-check--year font-weight-bold">
                                      <b-icon
                                        :icon="isBdxPeriodDropdownYearExpanded(group.year) ? 'chevron-down' : 'chevron-right'"
                                        font-scale="0.8"
                                        class="mr-1 fcp-check__chevron"
                                        role="button"
                                        @click.prevent="toggleBdxPeriodDropdownYearExpanded(group.year)"
                                      ></b-icon>
                                      <input
                                        type="checkbox"
                                        :checked="isYearSelected(group)"
                                        :indeterminate.prop="isYearPartiallySelected(group)"
                                        @change="toggleYear(group)"
                                      />
                                      {{ group.year }}
                                    </label>
                                    <template v-if="isBdxPeriodDropdownYearExpanded(group.year)">
                                      <label
                                        v-for="month in group.months"
                                        :key="month.key"
                                        class="fcp-check fcp-check--indent"
                                      >
                                        <input
                                          type="checkbox"
                                          :checked="selectedPeriods.includes(month.key)"
                                          @change="togglePeriod(month.key)"
                                        />
                                        {{ month.label }}
                                      </label>
                                    </template>
                                  </div>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleBdxColumnSort('report_month_year')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === bdxSortBy && !bdxSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === bdxSortBy && bdxSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'bdx:report_month_year')"
                          ></span>
                        </template>
                        <!-- UMR's own head() override - reuses umrFilter/umrSearch/etc. wholesale -
                             the exact same state backing the toolbar's own UMR dropdown/Variance's/
                             REC UMRs' own UMR columns. -->
                        <template #head(umr)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`bdx:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                @hidden="resetChecklistSearches"
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                                variant="link"
                                no-caret
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                                lazy
                              >
                                <template #button-content>
                                  <b-icon v-if="!isUmrFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredUmrs">
                                  <div class="fcp-checklist-search">
                                    <b-icon icon="search" font-scale="0.8"></b-icon>
                                    <input
                                      type="text"
                                      v-model="umrSearch"
                                      placeholder="Search"
                                      class="fcp-checklist-search__input"
                                    />
                                  </div>
                                  <label class="fcp-check">
                                    <input type="checkbox" :checked="allUmrsSelected" @change="toggleAllUmrs" />
                                    Select all
                                  </label>
                                  <label v-for="umr in filteredUmrOptions" :key="umr" class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="isUmrSelected(umr)"
                                      @change="toggleUmr(umr)"
                                    />
                                    {{ maskUmr(umr) }}
                                  </label>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleBdxColumnSort('umr')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === bdxSortBy && !bdxSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === bdxSortBy && bdxSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'bdx:umr')"
                          ></span>
                        </template>
                        <!-- Claim Reference's own head() override - same treatment as UMR's own
                             above, reusing claimFilter/claimSearch/etc. wholesale. -->
                        <template #head(claim)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`bdx:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                @hidden="resetChecklistSearches"
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                                variant="link"
                                no-caret
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                                lazy
                              >
                                <template #button-content>
                                  <b-icon v-if="!isClaimFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredClaims">
                                  <div class="fcp-checklist-search">
                                    <b-icon icon="search" font-scale="0.8"></b-icon>
                                    <input
                                      type="text"
                                      v-model="claimSearch"
                                      placeholder="Search"
                                      class="fcp-checklist-search__input"
                                    />
                                  </div>
                                  <label class="fcp-check">
                                    <input type="checkbox" :checked="allClaimsSelected" @change="toggleAllClaims" />
                                    Select all
                                  </label>
                                  <label v-for="claim in displayedClaimOptions" :key="claim" class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="isClaimSelected(claim)"
                                      @change="toggleClaim(claim)"
                                    />
                                    {{ maskClaim(claim) }}
                                  </label>
                                  <div v-if="hiddenClaimCount" class="small text-muted px-2 py-1">
                                    +{{ hiddenClaimCount.toLocaleString() }} more claims, type to see the claim
                                  </div>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleBdxColumnSort('claim')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'claim' === bdxSortBy && !bdxSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'claim' === bdxSortBy && bdxSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'bdx:claim')"
                          ></span>
                        </template>
                        <!-- Account Name's own head() override - same treatment, reusing
                             accountNameFilter/accountNameSearch/etc. wholesale. Not the rightmost
                             column in this table (limeiq_key follows it), so no `right` prop needed
                             here (unlike Cashbook's own copy, where Account Name IS last). -->
                        <template #head(account_name)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`bdx:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                @hidden="resetChecklistSearches"
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--custom-checkbox"
                                variant="link"
                                no-caret
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                                lazy
                              >
                                <template #button-content>
                                  <b-icon v-if="!isAccountNameFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredAccountNames">
                                  <div class="fcp-checklist-search">
                                    <b-icon icon="search" font-scale="0.8"></b-icon>
                                    <input
                                      type="text"
                                      v-model="accountNameSearch"
                                      placeholder="Search"
                                      class="fcp-checklist-search__input"
                                    />
                                  </div>
                                  <label class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="allAccountNamesSelected"
                                      @change="toggleAllAccountNames"
                                    />
                                    Select all
                                  </label>
                                  <label v-for="name in filteredAccountNameOptions" :key="name" class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="isAccountNameSelected(name)"
                                      @change="toggleAccountName(name)"
                                    />
                                    {{ maskAccountName(name) }}
                                  </label>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleBdxColumnSort('account_name')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'account_name' === bdxSortBy && !bdxSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'account_name' === bdxSortBy && bdxSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'bdx:account_name')"
                          ></span>
                        </template>
                        <template #head()="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`bdx:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <!-- TPA/Coverholder (LIQ-2402): same checklist dropdown as UMR's own
                                   head() override above, one template for both keys - see
                                   BDX_CHECKLIST_FIELD_KEYS. -->
                              <b-dropdown
                                v-if="BDX_CHECKLIST_FIELD_KEYS.includes(data.field.key)"
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                                variant="link"
                                no-caret
                                lazy
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                              >
                                <template #button-content>
                                  <b-icon v-if="!isBdxColumnSearchActive(data.field.key)" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredBdxChecklist(data.field.key)">
                                  <div class="fcp-checklist-search">
                                    <b-icon icon="search" font-scale="0.8"></b-icon>
                                    <input
                                      type="text"
                                      v-model="bdxChecklistSearch[data.field.key]"
                                      placeholder="Search"
                                      class="fcp-checklist-search__input"
                                    />
                                  </div>
                                  <label class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="allBdxChecklistSelected(data.field.key)"
                                      @change="toggleAllBdxChecklist(data.field.key)"
                                    />
                                    Select all
                                  </label>
                                  <label
                                    v-for="name in filteredBdxChecklistOptions(data.field.key)"
                                    :key="name"
                                    class="fcp-check"
                                  >
                                    <input
                                      type="checkbox"
                                      :checked="isBdxChecklistSelected(data.field.key, name)"
                                      @change="toggleBdxChecklist(data.field.key, name)"
                                    />
                                    {{ maskCellValue(data.field.key, name) }}
                                  </label>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                v-if="BDX_HEADER_SEARCH_FIELD_KEYS.includes(data.field.key)"
                                class="fcp-th-search-toggle"
                                role="button"
                                tabindex="0"
                                @click.stop="bdxColumnSearchMenuRef.toggle(data.field.key, $event, bdxColumnSearchTypeFor(data.field.key))"
                                @keydown.enter.stop="bdxColumnSearchMenuRef.toggle(data.field.key, $event, bdxColumnSearchTypeFor(data.field.key))"
                              >
                                <b-icon v-if="!isBdxColumnSearchActive(data.field.key)" icon="search" font-scale="1"></b-icon>
                                <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                              </span>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleBdxColumnSort(data.field.key)"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': bdxSortBy === data.field.key && !bdxSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': bdxSortBy === data.field.key && bdxSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, `bdx:${data.field.key}`)"
                          ></span>
                        </template>
                        <template #cell()="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`bdx:${field.key}`)">{{ maskCellValue(field.key, value) }}</div>
                        </template>
                        <!-- Starts narrower than its longest names (see columnWidths), so the full
                             name shows on hover - same as Cashbook's Narrative. -->
                        <template #cell(coverholder)="{ value, field }">
                          <div
                            class="fcp-td-resizer"
                            :style="columnStyle(`bdx:${field.key}`)"
                            :title="maskCellValue(field.key, value)"
                          >{{ maskCellValue(field.key, value) }}</div>
                        </template>
                        <template #cell(total_paid_bdx)="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`bdx:${field.key}`)">{{ formatCurrencyNoSymbol(value) }}</div>
                        </template>
                        <template #cell(paid_tpa_fees)="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`bdx:${field.key}`)">{{ formatCurrencyNoSymbol(value) }}</div>
                        </template>
                        <template #cell(fcp_start_date)="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`bdx:${field.key}`)">{{ formatDate(value) }}</div>
                        </template>
                        <template #bottom-row>
                          <td class="font-weight-bold">
                            <div class="fcp-td-resizer" :style="columnStyle('bdx:report_month_year')">Total</div>
                          </td>
                          <!-- TPA, Coverholder, UMR, Section #, Claim Reference -->
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td class="font-weight-bold text-right">
                            <div class="fcp-td-resizer" :style="columnStyle('bdx:total_paid_bdx')">{{ formatCurrencyNoSymbol(bdxTotal) }}</div>
                          </td>
                          <td class="font-weight-bold text-right">
                            <div class="fcp-td-resizer" :style="columnStyle('bdx:paid_tpa_fees')">{{ formatCurrencyNoSymbol(paidTpaFeesTotal) }}</div>
                          </td>
                          <td colspan="3"></td>
                        </template>
                        <template #empty>
                          <div class="text-center text-muted py-3">No rows match the current filters.</div>
                        </template>
                      </b-table>
                      <div v-if="noReportPeriodSelected" class="fcp-no-periods">No report period selected</div>
                    </div>
                  </div>
                </div>

            <div
              v-if="'bdx' !== sourceFilter"
              class="col-12 mb-1"
              :class="'cashbook' === sourceFilter ? 'col-lg-9' : 'col-lg-4-5'"
            >
              <div class="fcp-panel">
                <div class="fcp-panel__title d-flex align-items-center justify-content-between">
                  <span>Cashbook Details by Transaction</span>
                  <div v-if="cashbookTotalRows" class="fcp-variance-load-more">
                    <span class="fcp-variance-load-more__count">
                      Showing {{ cashbookDetailRows.length }} of {{ cashbookTotalRows }} rows
                    </span>
                    <button
                      v-if="hasMoreCashbookDetails"
                      type="button"
                      class="fcp-variance-load-more__btn"
                      :disabled="cashbookLoadingMore"
                      @click="loadMoreCashbookDetails"
                    >
                      {{ cashbookLoadingMore ? 'Loading…' : `Load ${cashbookLoadMoreCount} more` }}
                    </button>
                  </div>
                  <div v-else></div>
                  <span class="d-inline-flex align-items-center">
                    <rec-filter-eraser
                      :active="isCashbookTableFilterActive"
                      title="Clear this table's column filters"
                      @clear="clearCashbookTableFilters"
                    />
                    <span
                      class="fcp-download-icon"
                      :class="{ 'fcp-download-icon--disabled': cashbookExporting || demoMode }"
                      role="button"
                      :title="demoMode ? DEMO_EXPORT_DISABLED_TITLE : cashbookExporting ? 'Exporting…' : 'Export data'"
                      @click="!cashbookExporting && !demoMode && downloadCashbookExcel()"
                    >
                      <b-icon icon="download" font-scale="1"></b-icon>
                    </span>
                  </span>
                </div>
                <div class="table-responsive mb-0 fcp-detail-table position-relative">
                      <b-table
                        :items="cashbookDetailRows"
                        :fields="cashbookFields"
                        :busy="cashbookLoadingInitial"
                        sticky-header="200px"
                        small
                        striped
                        hover
                        :tbody-tr-class="(item) => (item && isTransactionRowSelected('cashbook', item) ? 'fcp-transaction-row--active' : '')"
                        @row-clicked="(item, index, event) => handleTransactionRowClick('cashbook', item, index, event)"
                      >
                        <!-- Report Period's own head() override - same treatment as BDX Detail's
                             own copy above, reusing selectedPeriods/periodTree/etc. wholesale, with
                             this table's own independent expand state
                             (cashbookPeriodDropdownExpandedYears). -->
                        <template #head(report_month_year)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`cashbook:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--custom-checkbox fcp-checklist-menu--period-tree"
                                variant="link"
                                no-caret
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                              >
                                <template #button-content>
                                  <b-icon v-if="!isPeriodFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent>
                                  <label class="fcp-check">
                                    <input type="checkbox" :checked="allPeriodsSelected" @change="toggleAllPeriods" />
                                    Select all
                                  </label>
                                  <div v-for="group in periodTree" :key="group.year" class="fcp-period-year">
                                    <label class="fcp-check fcp-check--year font-weight-bold">
                                      <b-icon
                                        :icon="isCashbookPeriodDropdownYearExpanded(group.year) ? 'chevron-down' : 'chevron-right'"
                                        font-scale="0.8"
                                        class="mr-1 fcp-check__chevron"
                                        role="button"
                                        @click.prevent="toggleCashbookPeriodDropdownYearExpanded(group.year)"
                                      ></b-icon>
                                      <input
                                        type="checkbox"
                                        :checked="isYearSelected(group)"
                                        :indeterminate.prop="isYearPartiallySelected(group)"
                                        @change="toggleYear(group)"
                                      />
                                      {{ group.year }}
                                    </label>
                                    <template v-if="isCashbookPeriodDropdownYearExpanded(group.year)">
                                      <label
                                        v-for="month in group.months"
                                        :key="month.key"
                                        class="fcp-check fcp-check--indent"
                                      >
                                        <input
                                          type="checkbox"
                                          :checked="selectedPeriods.includes(month.key)"
                                          @change="togglePeriod(month.key)"
                                        />
                                        {{ month.label }}
                                      </label>
                                    </template>
                                  </div>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleCashbookColumnSort('report_month_year')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === cashbookSortBy && !cashbookSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'report_month_year' === cashbookSortBy && cashbookSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'cashbook:report_month_year')"
                          ></span>
                        </template>
                        <!-- UMR's own head() override - reuses umrFilter/umrSearch/etc. wholesale. -->
                        <template #head(umr)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`cashbook:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                @hidden="resetChecklistSearches"
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                                variant="link"
                                no-caret
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                                lazy
                              >
                                <template #button-content>
                                  <b-icon v-if="!isUmrFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredUmrs">
                                  <div class="fcp-checklist-search">
                                    <b-icon icon="search" font-scale="0.8"></b-icon>
                                    <input
                                      type="text"
                                      v-model="umrSearch"
                                      placeholder="Search"
                                      class="fcp-checklist-search__input"
                                    />
                                  </div>
                                  <label class="fcp-check">
                                    <input type="checkbox" :checked="allUmrsSelected" @change="toggleAllUmrs" />
                                    Select all
                                  </label>
                                  <label v-for="umr in filteredUmrOptions" :key="umr" class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="isUmrSelected(umr)"
                                      @change="toggleUmr(umr)"
                                    />
                                    {{ maskUmr(umr) }}
                                  </label>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleCashbookColumnSort('umr')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === cashbookSortBy && !cashbookSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'umr' === cashbookSortBy && cashbookSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'cashbook:umr')"
                          ></span>
                        </template>
                        <!-- Claim Reference's own head() override - same treatment as UMR's own
                             above, reusing claimFilter/claimSearch/etc. wholesale. -->
                        <template #head(claim)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`cashbook:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                @hidden="resetChecklistSearches"
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--code-list fcp-checklist-menu--custom-checkbox"
                                variant="link"
                                no-caret
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                                lazy
                              >
                                <template #button-content>
                                  <b-icon v-if="!isClaimFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredClaims">
                                  <div class="fcp-checklist-search">
                                    <b-icon icon="search" font-scale="0.8"></b-icon>
                                    <input
                                      type="text"
                                      v-model="claimSearch"
                                      placeholder="Search"
                                      class="fcp-checklist-search__input"
                                    />
                                  </div>
                                  <label class="fcp-check">
                                    <input type="checkbox" :checked="allClaimsSelected" @change="toggleAllClaims" />
                                    Select all
                                  </label>
                                  <label v-for="claim in displayedClaimOptions" :key="claim" class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="isClaimSelected(claim)"
                                      @change="toggleClaim(claim)"
                                    />
                                    {{ maskClaim(claim) }}
                                  </label>
                                  <div v-if="hiddenClaimCount" class="small text-muted px-2 py-1">
                                    +{{ hiddenClaimCount.toLocaleString() }} more claims, type to see the claim
                                  </div>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleCashbookColumnSort('claim')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'claim' === cashbookSortBy && !cashbookSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'claim' === cashbookSortBy && cashbookSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'cashbook:claim')"
                          ></span>
                        </template>
                        <!-- Account Name's own head() override - same treatment, reusing
                             accountNameFilter/accountNameSearch/etc. wholesale. IS the rightmost
                             column in this table, so `right` opens the menu growing leftward from
                             the icon instead of off the page's right edge (same reasoning as
                             Variance's own copy). -->
                        <template #head(account_name)="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`cashbook:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <b-dropdown
                                @hidden="resetChecklistSearches"
                                toggle-class="fcp-th-search-toggle"
                                menu-class="fcp-checklist-menu fcp-checklist-menu--column-header-scrollable fcp-checklist-menu--custom-checkbox"
                                variant="link"
                                no-caret
                                right
                                boundary="window"
                                :popper-opts="dropdownPopperOpts()"
                                lazy
                              >
                                <template #button-content>
                                  <b-icon v-if="!isAccountNameFilterActive" icon="search" font-scale="1"></b-icon>
                                  <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                                </template>
                                <b-dropdown-form @click.stop @submit.prevent="selectAllFilteredAccountNames">
                                  <div class="fcp-checklist-search">
                                    <b-icon icon="search" font-scale="0.8"></b-icon>
                                    <input
                                      type="text"
                                      v-model="accountNameSearch"
                                      placeholder="Search"
                                      class="fcp-checklist-search__input"
                                    />
                                  </div>
                                  <label class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="allAccountNamesSelected"
                                      @change="toggleAllAccountNames"
                                    />
                                    Select all
                                  </label>
                                  <label v-for="name in filteredAccountNameOptions" :key="name" class="fcp-check">
                                    <input
                                      type="checkbox"
                                      :checked="isAccountNameSelected(name)"
                                      @change="toggleAccountName(name)"
                                    />
                                    {{ maskAccountName(name) }}
                                  </label>
                                </b-dropdown-form>
                              </b-dropdown>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleCashbookColumnSort('account_name')"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'account_name' === cashbookSortBy && !cashbookSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': 'account_name' === cashbookSortBy && cashbookSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, 'cashbook:account_name')"
                          ></span>
                        </template>
                        <template #head()="data">
                          <div class="fcp-th-resizer" :style="columnStyle(`cashbook:${data.field.key}`)">
                            <span class="fcp-th-resizer__label">{{ data.label }}</span>
                            <span class="fcp-th-actions">
                              <span
                                v-if="CASHBOOK_HEADER_SEARCH_FIELD_KEYS.includes(data.field.key)"
                                class="fcp-th-search-toggle"
                                role="button"
                                tabindex="0"
                                @click.stop="cashbookColumnSearchMenuRef.toggle(data.field.key, $event, cashbookColumnSearchTypeFor(data.field.key))"
                                @keydown.enter.stop="cashbookColumnSearchMenuRef.toggle(data.field.key, $event, cashbookColumnSearchTypeFor(data.field.key))"
                              >
                                <b-icon v-if="!isCashbookColumnSearchActive(data.field.key)" icon="search" font-scale="1"></b-icon>
                                <img v-else :src="filterAppliedIcon" class="fcp-th-filter-icon" alt="Filtered" />
                              </span>
                              <span
                                class="fcp-th-sort-icon"
                                role="button"
                                @click.stop="toggleCashbookColumnSort(data.field.key)"
                              >
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--up"
                                  :class="{ 'fcp-th-sort-icon__caret--active': cashbookSortBy === data.field.key && !cashbookSortDesc }"
                                ></span>
                                <span
                                  class="fcp-th-sort-icon__caret fcp-th-sort-icon__caret--down"
                                  :class="{ 'fcp-th-sort-icon__caret--active': cashbookSortBy === data.field.key && cashbookSortDesc }"
                                ></span>
                              </span>
                            </span>
                          </div>
                          <span
                            class="fcp-col-resize-handle"
                            @mousedown="startColumnResize($event, `cashbook:${data.field.key}`)"
                          ></span>
                        </template>
                        <template #cell()="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`cashbook:${field.key}`)">{{ maskCellValue(field.key, value) }}</div>
                        </template>
                        <template #cell(debit)="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`cashbook:${field.key}`)">{{ formatCurrencyNoSymbol(value) }}</div>
                        </template>
                        <template #cell(credit)="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`cashbook:${field.key}`)">{{ formatCurrencyNoSymbol(value) }}</div>
                        </template>
                        <template #cell(narrative)="{ value, field }">
                          <div class="fcp-td-resizer" :style="columnStyle(`cashbook:${field.key}`)" :title="value">{{ value }}</div>
                        </template>
                        <template #bottom-row>
                          <td class="font-weight-bold">
                            <div class="fcp-td-resizer" :style="columnStyle('cashbook:report_month_year')">Total</div>
                          </td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td class="font-weight-bold text-right">
                            <div class="fcp-td-resizer" :style="columnStyle('cashbook:debit')">{{ formatCurrencyNoSymbol(debitTotal) }}</div>
                          </td>
                          <td class="font-weight-bold text-right">
                            <div class="fcp-td-resizer" :style="columnStyle('cashbook:credit')">{{ formatCurrencyNoSymbol(creditTotal) }}</div>
                          </td>
                          <td colspan="2"></td>
                        </template>
                        <template #empty>
                          <div class="text-center text-muted py-3">No rows match the current filters.</div>
                        </template>
                      </b-table>
                      <div v-if="noReportPeriodSelected" class="fcp-no-periods">No report period selected</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Shared row-hover tooltip for the Variance/BDX/Cashbook tables: shows
         every field of whichever row is currently hovered, regardless of
         which column the cursor is over. -->
    <div
      v-if="hoveredRowTooltip"
      class="fcp-row-tooltip"
      :style="{ left: hoveredRowTooltip.x + 'px', top: hoveredRowTooltip.y + 'px' }"
    >
      <div v-for="entry in hoveredRowTooltip.entries" :key="entry.label" class="fcp-row-tooltip__row">
        <span class="fcp-row-tooltip__label">{{ entry.label }}</span>
        <span
          class="fcp-row-tooltip__value"
          :class="{ 'fcp-row-tooltip__value--blank': entry.isBlank }"
        >{{ entry.text }}</span>
      </div>
    </div>
    </template>

    <!-- Guards navigating away from this page (see beforeRouteLeave/guardUnsavedChanges) while a
         row is checked with unsaved edits - NOT in-page filter/sort/drill-down changes, which
         proceed without warning. Save calls the exact same saveVarianceEdits the panel's own Save
         button does - a validation failure there (missing Variance Type/Comment) leaves this modal
         closed but the row still checked, same as clicking Save directly would, rather than
         silently discarding the blocked navigation. -->
    <b-modal
      v-model="showUnsavedChangesModal"
      title="Unsaved changes"
      modal-class="fcp-unsaved-changes-modal"
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      centered
      size="sm"
    >
      <p class="mb-0">
        You have unsaved changes on one or more checked rows. Save them, discard them, or cancel
        and stay on this page.
      </p>

      <template #modal-footer>
        <button type="button" class="btn btn-white btn-sm" @click="cancelUnsavedChangesModal">
          Cancel
        </button>
        <button type="button" class="btn btn-warning btn-sm" @click="confirmDiscardAndContinue">
          Discard and continue
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="savingVarianceEdits"
          @click="confirmSaveAndContinue"
        >
          {{ savingVarianceEdits ? 'Saving…' : 'Save and continue' }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  getCurrentInstance
} from '@vue/composition-api'
import { useRoute, useRouter } from 'vue2-helpers/vue-router'
import { format, parse, isValid } from 'date-fns'
import { debounce, startCase, throttle } from 'lodash-es'
import * as XLSX from 'xlsx'
// Base axios package (not the configured @util/reconciliationAxios instance the services use
// internally) - only needed here for CancelToken/isCancel, which this project's pinned
// axios@0.21.4 only exposes as static properties on the top-level package, not on instances
// created via axios.create(). See fetchMyRole/fetchReportPeriodRange/fetchReport/
// fetchPartyGroupOptions/fetchVarianceDetails's own cancel-source tracking below.
import axios from 'axios'

import { useCurrentUser, useToast, useUserCompany } from '@composables'
import { DATE_FORMAT } from '@constants'
import { ReconciliationService, FCPVariance } from '@services'
import RecAccountNameReconciliationPanel from '@components/RecAccountNameReconciliationPanel'
import RecColumnSearchMenu from '@components/RecColumnSearchMenu'
import RecFilterEraser from '@components/RecFilterEraser'
import RecInlineOptionsMenu from '@components/RecInlineOptionsMenu'
import RecStatusFilterMenu from '@components/RecStatusFilterMenu'
import filterAppliedIcon from '@assets/img/rec-filter-icon.png'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

// Builds { year: string, months: string[] (Jan-first) }[] from GET /report-period-range's
// earliest_report_period, paired with today's own date for the upper bound - replaces a hardcoded
// 2021-2026 REPORT_PERIOD_YEARS/ALL_PERIOD_KEYS pair that used to show every caller the same fixed
// range regardless of their own RLS-scoped data (see fetchReportPeriodRange in setup() below).
//
// HISTORY OF THIS DECISION: the upper bound used to be a second field the backend returned
// (latest_calendar_year), always showing all 12 months of that year through December even if the
// caller's own data didn't reach that far yet - so new periods stayed selectable without a
// refetch. That field cost a second ~11s DB round trip (MAX(YEAR(...)) over the same live view
// earliest_report_period already scans) for a value real data can never exceed anyway - a report
// period can't be dated in the future. Per explicit decision, replaced with today's own date,
// computed here instead: no server round trip needed to learn what today is, and the picker now
// stops at the CURRENT month rather than December, so it never offers a future month that's
// guaranteed to have zero data.
const buildReportPeriodYearMonths = (earliestReportPeriod) => {
  if (!earliestReportPeriod) return []

  const [earliestMonthName, earliestYearStr] = earliestReportPeriod.split(' ')
  const earliestYear = parseInt(earliestYearStr, 10)
  const earliestMonthIndex = MONTH_NAMES.indexOf(earliestMonthName)

  if (Number.isNaN(earliestYear) || 0 > earliestMonthIndex) return []

  const now = new Date()
  const latestYear = now.getFullYear()
  const latestMonthIndex = now.getMonth()

  const years = []
  for (let year = latestYear; year >= earliestYear; year--) {
    const isEarliestYear = year === earliestYear
    const isLatestYear = year === latestYear
    const months = MONTH_NAMES.slice(
      isEarliestYear ? earliestMonthIndex : 0,
      isLatestYear ? latestMonthIndex + 1 : MONTH_NAMES.length
    )
    years.push({ year: String(year), months })
  }
  return years
}

const VARIANCE_TYPE_OPTIONS = [
  '(Blank)',
  'Amount Mismatch',
  'BDX Entry Missing',
  'Cashbook Entry Missing',
  'UMR Mismatch',
  'LimeIQ Fees',
  'Other'
]

const REVIEW_STATUS_OPTIONS = ['Approved', 'Pending Approval', 'Not Submitted', 'Rejected']

// Variance Transaction Details header columns that get their own per-column
// text search + sort controls (see columnSearch/toggleColumnSort below) via
// the generic #head() template - the remaining columns (Report Period,
// Status?, Admin Approval, Account Name) are already covered by the
// toolbar's own filters/dropdowns, so they don't need a duplicate per-column
// control. Source and Status? DO now also have their own dedicated
// #head(source)/#head(variance_status) overrides (single-select/checklist
// dropdowns, not this generic text-box treatment) - same reasoning
// variance_type's own override below has, so neither is in this list either.
// total_paid_bdx/total_paid_fcp are plain numeric columns like
// variance_amount above - columnSearch's own string-includes match and
// sort_by both already work generically for any field key, no special
// casing needed to add them here.
const HEADER_SEARCH_SORT_FIELD_KEYS = [
  'total_paid_bdx',
  'total_paid_fcp',
  'variance_amount',
  'submit_variance_comment',
  'admin_variance_comment',
  'submission_date',
  'admin_variance_date'
]

// Subset of HEADER_SEARCH_SORT_FIELD_KEYS above that gets a min/max range popover (see
// RecColumnSearchMenu's own 'range' mode) instead of the plain free-text search box every other
// column there keeps - per explicit request, for these three amount columns "between X and Y"
// is a more useful filter than a substring match. columnSearch's own value shape for these keys
// is { min, max }, not a plain string - see displayedVarianceRows/the URL query-building effect
// below, both of which branch on this same list.
const RANGE_FILTER_FIELD_KEYS = ['total_paid_bdx', 'total_paid_fcp', 'variance_amount']

// Same idea as RANGE_FILTER_FIELD_KEYS above, but a From/To date-range popover (see
// RecColumnSearchMenu's own 'date-range' mode) instead of either the plain text box or the
// numeric Min/Max - per explicit request, for these two date columns "between this date and that
// date" is what's actually useful, not a substring match against a formatted date string.
// columnSearch's own value shape for these keys is { from, to } (plain "YYYY-MM-DD" strings,
// straight from each <input type="date">), not a plain string - see displayedVarianceRows/the
// URL query-building effect below, both of which branch on this same list.
const DATE_RANGE_FILTER_FIELD_KEYS = ['submission_date', 'admin_variance_date']

// BDX Detail by Transaction / Cashbook Details by Transaction's own per-column search fields - same
// role as HEADER_SEARCH_SORT_FIELD_KEYS/RANGE_FILTER_FIELD_KEYS/DATE_RANGE_FILTER_FIELD_KEYS above,
// but for a REAL server-side search (see BdxDetailsRequest/CashbookDetailsRequest's own new
// fields) rather than Variance's client-side-only column search - added per explicit decision that
// BDX/Cashbook's search should search the full filtered dataset, not just whatever's already
// loaded. umr/claim/account_name/report_month_year are deliberately NOT in these lists even though
// every column on these two tables gets a sort caret (see toggleBdxColumnSort/
// toggleCashbookColumnSort) - those four dimensions already have a fully-working, always-visible
// filter in the report's own toolbar (UMR/Claim Reference/Matched Account Name/Report Period),
// which already narrows BDX/Cashbook today (see bdxDetailsFilterParams/cashbookDetailsFilterParams
// below) - a duplicate inline search box in these two tables' own headers for those four would be a
// convenience, not new capability, so it's left for a later pass rather than doubling the amount of
// near-identical checklist-dropdown template this change touches.
const BDX_HEADER_SEARCH_FIELD_KEYS = ['section_number', 'total_paid_bdx', 'paid_tpa_fees', 'fcp_start_date', 'limeiq_key']
const BDX_RANGE_FILTER_FIELD_KEYS = ['total_paid_bdx', 'paid_tpa_fees']
const BDX_DATE_RANGE_FILTER_FIELD_KEYS = ['fcp_start_date']
// LIQ-2402: TPA/Coverholder get a tick-box checklist in their own header (like UMR's), not the
// search popover the keys above use. Their selections live in bdxColumnSearch as arrays, and their
// option lists come from /bdx-details' own filter_options (see bdxFilterOptions).
const BDX_CHECKLIST_FIELD_KEYS = ['tpa', 'coverholder']

const CASHBOOK_HEADER_SEARCH_FIELD_KEYS = ['section_number', 'debit', 'credit', 'narrative']
const CASHBOOK_RANGE_FILTER_FIELD_KEYS = ['debit', 'credit']

// Same list minus the filter-only "(Blank)" entry — used for the Variance Type
// edit dropdown, where a TPA picks an actual type rather than filtering by one.
const VARIANCE_TYPE_SUBMIT_OPTIONS = VARIANCE_TYPE_OPTIONS.filter((type) => '(Blank)' !== type)

// {value, label} pairs for RecInlineOptionsMenu (see its own toggle() calls in the Variance Type/
// Admin Approval #cell() templates below) - plain strings would work for every option except
// Variance Type's own "" (Select…), which needs a label distinct from its value.
const VARIANCE_TYPE_MENU_OPTIONS = [
  { value: '', label: 'Select…' },
  ...VARIANCE_TYPE_SUBMIT_OPTIONS.map((type) => ({ value: type, label: type }))
]

// Exports are built server-side as finished .xlsx files, so Demo Mode can't mask them the way it
// masks on-screen values - export is disabled outright while Demo Mode is on rather than leak real data.
const DEMO_EXPORT_DISABLED_TITLE = 'Export is disabled in Demo Mode'

const ADMIN_APPROVAL_MENU_OPTIONS = ['Approved', 'Pending', 'Rejected'].map((status) => ({
  value: status,
  label: status
}))

// Admin Approval column's own header filter checklist (see #head(admin_approval) below) - its
// own requested order/labels ("Pending" rather than the real "Pending Approval" value), distinct
// from REVIEW_STATUS_OPTIONS' own order/labels which the Status? column and toolbar Review Status
// dropdown keep unchanged. value is still the real variance_status string underneath - this just
// controls how the checklist itself is drawn, not what's pushed into approvalStatusFilter.
const ADMIN_APPROVAL_FILTER_DISPLAY_OPTIONS = [
  { value: 'Approved', label: 'Approved' },
  { value: 'Pending Approval', label: 'Pending' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Not Submitted', label: 'Not Submitted' }
]

// Source column's own dropdown (see #head(source) below) - reuses the exact same
// RecInlineOptionsMenu shared instance the Variance Type/Admin Approval cells already use,
// rather than a new component, since sourceFilter is a single value same as those two (unlike
// approvalStatusFilter, a multi-select array, which needed RecStatusFilterMenu's checkbox list
// instead). "" stands in for sourceFilter's own null ("All", no restriction) - a real value is
// needed here since RecInlineOptionsMenu keys each option by option.value, and Vue :key doesn't
// tolerate null. See setSourceFilter below for the "" <-> null mapping back.
const SOURCE_FILTER_MENU_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'bdx', label: 'BDX Only' },
  { value: 'cashbook', label: 'CB Only' },
  { value: 'both', label: 'Both' }
]

export default defineComponent({
  name: 'rec-production-report',
  components: {
    RecAccountNameReconciliationPanel,
    RecColumnSearchMenu,
    RecFilterEraser,
    RecInlineOptionsMenu,
    RecStatusFilterMenu
  },
  setup() {
    // .proxy is the actual component instance (Options-API-shaped `this`), same as
    // AppNavbar.vue's own getCurrentInstance() usage - needed only for $root below, to listen for
    // bootstrap-vue's own dropdown shown/hidden broadcasts (see openDropdownVm's own comment).
    const { proxy } = getCurrentInstance()

    const route = useRoute()
    const router = useRouter()

    // Filter state <-> URL query params, so a filtered view can be bookmarked/
    // shared. Only read once, here, to seed each filter ref's initial value -
    // deliberately NOT re-applied reactively on external route changes (e.g.
    // browser back/forward), which would need its own loop-prevention against
    // the write side (urlQuery/router.replace below).
    const initialQuery = route.query

    // Repeated-key params (?umr=A&umr=B) come back from vue-router as an array
    // already; a single occurrence (?umr=A) comes back as a bare string - this
    // normalizes both, and treats the param's total absence as null (this
    // report's own "no restriction" sentinel throughout, as opposed to `[]`
    // which several of these filters use to mean "explicitly nothing").
    const parseArrayQueryParam = (value) => (undefined === value ? null : [].concat(value))

    const { fcpRole, userId, userName, hasActiveRecStatus, isSuperUser, isDemoModeAvailable } = useCurrentUser()
    const { roleName: companyRoleName } = useUserCompany()
    const { showToast } = useToast()

    // LIQ-2571: Account Name Reconciliation panel - superusers and Underwriter companies only (the
    // rec service, limeiq's Lincoln proxy and Lincoln each enforce the same rule server-side; this
    // just decides whether to show the button). The panel keeps its own state; only whether it's
    // open and the button's count live here.
    const canReconcileAccountNames = computed(() => !!isSuperUser.value || 'Underwriter' === companyRoleName.value)
    const accountNamePanelOpen = ref(false)
    const accountNameOutstandingCount = ref(null)

    const fetchAccountNameSummary = async () => {
      if (!canReconcileAccountNames.value) return
      try {
        const response = await ReconciliationService.getAccountNameReconciliationSummary()
        accountNameOutstandingCount.value = (response && response.data && response.data.outstanding_count) || 0
      } catch (err) {
        // Non-fatal: the button just shows "—" rather than blocking the report.
        accountNameOutstandingCount.value = null
      }
    }

    // Shown in Variance Transaction Details' place - see the template.
    const isAccountNamePanelShown = computed(() => canReconcileAccountNames.value && accountNamePanelOpen.value)

    const toggleAccountNamePanel = () => {
      accountNamePanelOpen.value = !accountNamePanelOpen.value
    }

    const accountNameToggleTitle = computed(() => {
      const count = accountNameOutstandingCount.value
      return count ? `Account Name Reconciliation - ${count} to fix` : 'Account Name Reconciliation'
    })

    const onAccountNamesCounted = (count) => {
      accountNameOutstandingCount.value = count
    }

    // Our own axios timeout (ETIMEDOUT - see reconciliationAxios.js) and the ingress's 504 both
    // mean the request ran too long - usually a very large selection - so say that and what to do,
    // rather than a bare "Failed to ...". Otherwise the backend's own detail, then `fallback`.
    const requestErrorMessage = (err, fallback) => {
      const timedOut = (err && err.code === 'ETIMEDOUT') || (err && err.response && err.response.status === 504)
      if (timedOut) return `${fallback} The request took too long - try fewer report periods, or try again shortly.`
      return (err && err.response && err.response.data && err.response.data.detail) || fallback
    }

    // 'User' is this app's submitter/TPA role. Admin is deliberately included too - Admin can now
    // submit a variance (Variance Type/Submitter Comments) and approve it in the same action, not
    // just respond to one a User already submitted (see isRowCheckable's own Admin branch for the
    // one restriction that doesn't relax: a closed/Approved/Rejected row still can't be
    // re-litigated). Submitter Comments is optional for Admin even though it's required for User -
    // see saveVarianceEdits. Both User and Admin require hasActiveRecStatus (see useCurrentUser -
    // this user's OWN Members.user_rec_status, distinct from fcpRole itself, which is
    // company-wide) - REVISED per explicit decision: a User with an inactive/NULL REC status is
    // now read-only too, same as Admin, not exempt the way an earlier version of this had it.
    // Admin's lead-underwriter requirement is now a PER-ROW check (see rowPassesLeadGate/
    // isRowLeadUnderwriter below), not an account-wide one - this computed only covers the
    // role-level/REC-status half (a User only needs the REC status itself, the lead-underwriter
    // concept being specific to the reviewer/Admin side). Superuser stays exempt (same
    // unconditional-bypass treatment as everywhere else in this report). This only gates editing,
    // never viewing - anyone who fails these checks still sees every row, same as always, they
    // just can't check/edit them (see isRowCheckable/isRowEditableBySubmitter/isRowEditableByAdmin,
    // which layer rowPassesLeadGate on top of this for Admin).
    const canEditVariance = computed(
      () =>
        'Superuser' === fcpRole.value ||
        ('User' === fcpRole.value && hasActiveRecStatus.value) ||
        ('Admin' === fcpRole.value && hasActiveRecStatus.value)
    )

    // The reviewer side of the same workflow - unlocks Admin Approval/Admin
    // Comments alongside (not instead of, for Admin/Superuser) Variance Type/
    // Submitter Comments above. Admin and Superuser both get this dual access
    // now - canEditVariance and canEditAdminReview are NOT mutually exclusive
    // for either (see saveVarianceEdits, which merges both field sets rather
    // than picking one).
    // Same split as canEditVariance above: the per-row lead-underwriter requirement lives in
    // rowPassesLeadGate now, not here. This computed is just hasActiveRecStatus (see useCurrentUser
    // - this user's OWN Members.user_rec_status, distinct from fcpRole itself, which is
    // company-wide) - only a lead underwriter whose own REC status is Active can respond to a
    // submitted variance, per explicit decision, but "lead underwriter" is decided per-row now (a
    // lead-somewhere-but-not-here Admin no longer gets a blanket pass - see isRowLeadUnderwriter's
    // own comment for the bug this fixes). Superuser is exempt from all of this (same
    // unconditional-bypass treatment as role="admin" everywhere else in this report). This only
    // gates editing/approving, never viewing - a User who fails either check still sees every row,
    // same as always, they just can't check/edit them (see isRowCheckable/isRowEditableByAdmin).
    const canEditAdminReview = computed(
      () =>
        'Superuser' === fcpRole.value ||
        ('Admin' === fcpRole.value && hasActiveRecStatus.value)
    )

    // Shared by the Admin Approval dropdown (adminApprovalMenuOptions below) and
    // saveVarianceEdits' clearing restriction (LIQ-2444 follow-up) - a single source of
    // truth for "is this session exempt from the clear-restriction", rather than each
    // call site re-deriving its own 'Superuser' === fcpRole.value check.
    const isSuperuser = computed(() => 'Superuser' === fcpRole.value)

    // Only a Superuser sees the extra "Select…" option on top of Approved/Pending/Rejected -
    // picking it is how they fully WIPE an existing admin review (status AND admin_variance_date/
    // admin_variance_user, see saveVarianceEdits) rather than just resetting the decision to
    // Pending, which still re-stamps admin_variance_date/admin_variance_user with today's date and
    // this Superuser's own id (a reset is itself a new, dateable admin action; "Select…" instead
    // means "there was never a review here", so nothing should be stamped at all). Mirrors
    // VARIANCE_TYPE_MENU_OPTIONS' own '' "Select…" sentinel above, same {value: '', label} shape.
    const adminApprovalMenuOptions = computed(() =>
      isSuperuser.value
        ? [{ value: '', label: 'Select…' }, ...ADMIN_APPROVAL_MENU_OPTIONS]
        : ADMIN_APPROVAL_MENU_OPTIONS
    )

    const loading = ref(false)
    const error = ref(null)
    const generatedAt = ref(null)
    // Whether the backend's shared cache was CONFIRMED cold for this load (a real signal echoed
    // back on /production's own response's is_cold field - see fetchReport - not a guess) - drives
    // the Variance Transaction Details table's cold-start overlay in the template. Gated there by
    // `&& (loading || varianceLoadingInitial)` too, so a stale true value left over after a load
    // finishes can't show the overlay with nothing actually loading.
    const isColdHit = ref(false)
    // Account Name/Claim Reference/Source/UMR's own cross-filtered dropdown option lists, from
    // /variance-details/filter-options (see fetchVarianceFilterOptions below) - already sorted,
    // already narrowed by every OTHER active filter, server-side (service.available_account_name_
    // options/available_claim_options/available_source_options/available_umr_options). Replaces
    // what used to be derived client-side from a full, unpaginated per-claim fetch (rawRows, since
    // removed - see fetchRecUmrRollup/fetchReport's own LIQ-2403 history note), which contributed
    // directly to /production's own reported "select all report periods" freeze.
    const varianceFilterOptions = ref({ account_name: [], claim: [], source: [], umr: [] })
    // Drives the spinner in the corner of each filter box fed by varianceFilterOptions (LIQ-2403).
    const varianceFilterOptionsLoading = ref(false)

    // Which role/role_value this caller sends on every other rec endpoint - the backend no
    // longer derives access scope from the token itself (see docs/rec-report-rls-replication.md).
    // Resolved once from GET /my-role on mount, before the first /production call. null until
    // resolved (or if resolution failed/came back empty - fetchReport treats that as "can't fetch").
    const roleScope = ref(null)

    // { year, months }[] (Jan-first order) from GET /report-period-range - replaces the old
    // hardcoded REPORT_PERIOD_YEARS. Empty until fetchReportPeriodRange resolves on mount (see
    // onMounted below); periodTree/allPeriodKeys/reportPeriodYears below derive from this so they
    // can't drift apart from each other.
    const reportPeriodYearMonths = ref([])

    const reportPeriodYears = computed(() => reportPeriodYearMonths.value.map(({ year }) => year))

    const allPeriodKeys = computed(() =>
      reportPeriodYearMonths.value.flatMap(({ year, months }) => months.map((m) => `${m} ${year}`))
    )

    // Populated once reportPeriodYearMonths resolves (empty array until then) - see
    // fetchReportPeriodRange in onMounted below, which also seeds this to "everything selected"
    // the same way the old `ref([...ALL_PERIOD_KEYS])` initializer used to. If the URL already
    // named explicit periods, that seeding is skipped instead (see hadExplicitPeriodsInUrl) so a
    // shared link doesn't get silently widened back out to "everything" on load.
    const hadExplicitPeriodsInUrl = undefined !== initialQuery.periods
    const selectedPeriods = ref(parseArrayQueryParam(initialQuery.periods) || [])
    // The user has actually unticked every period - not the brief moment on mount before the
    // period list has loaded (same distinction as fetchReport's periodsNotYetResolved).
    const noReportPeriodSelected = computed(
      () => !selectedPeriods.value.length && (hadExplicitPeriodsInUrl || reportPeriodYearMonths.value.length > 0)
    )
    // Tri-state toggles: null = default/center (show all), true/false = right/left
    const varianceFilter = ref(
      'yes' === initialQuery.variance ? true : 'no' === initialQuery.variance ? false : null
    )
    // Single-select Source dropdown (Column A, below Party) - replaces the old
    // Claims tristate (BDX-only/Cashbook-only). null = "All" (no restriction -
    // every row regardless of source or match status), 'bdx' = BDX-only rows
    // (present in BDX, no matching Cashbook entry), 'cashbook' = Cashbook-only
    // rows (present in Cashbook, no matching BDX entry), 'both' = rows present in
    // BOTH BDX and Cashbook (fcp_status === 1, i.e. reconciled/matched) - the same
    // fcp_status the FCP? tristate below reads too.
    const sourceFilter = ref(
      ['bdx', 'cashbook', 'both'].includes(initialQuery.source) ? initialQuery.source : null
    )
    const approvalStatusFilter = ref(parseArrayQueryParam(initialQuery.status))
    // Backed by a real column now - dbo.rls_filterset_globalumr.fcp_status, threaded through
    // vw_fcp_program -> fcp_variance_transactions_period -> fetch_variance_period_rows -> here as
    // row.fcp_status. Was disabled while that plumbing didn't exist; re-enabled per explicit
    // decision (see docs/rec-report-rls-replication.md's fcp_status entry) once
    // vw_fcp_global_umr_key_map stopped filtering fcp_status = 1 out at the base, so this toggle
    // has real "No" data to show instead of always resolving to "Yes".
    const fcpToggleEnabled = true
    // The "FCP?" tri-state toggle - per explicit decision, same left/right/center convention as
    // every other tri-state here (see varianceFilter above): null = default/center ("All", no
    // restriction), true = right ("Yes", fcp_status 1), false = left ("No", fcp_status 0). Same
    // plain yes/no URL encoding as varianceFilter too, now that the default is null like every
    // other tri-state (no separate "both" sentinel needed).
    const fcpFilter = ref(
      'yes' === initialQuery.fcp ? true : 'no' === initialQuery.fcp ? false : null
    )

    // Named setters for the tristate zones to call rather than assigning varianceFilter/fcpFilter
    // inline in the template (which is what they did originally).
    const setVarianceFilter = (value) => {
      varianceFilter.value = value
    }

    const setFcpFilter = (value) => {
      fcpFilter.value = value
    }

    // Single click target advancing the knob one step to the right each click, wrapping back to
    // the start (false -> null -> true -> false...) - replaces the old 3-independently-clickable-
    // zones interaction (left/center/right each jumping straight to their own value in one
    // click), per explicit request, mirroring the same change already made to BDXValidationReport
    // .vue's own 3 tristate toggles (advanceBdxStatus/advanceClaimStatusToggle/
    // advanceSeverityToggle there). Shared by both toggles below - false/null/true is the same
    // left/center/right order for either one, just with (Variance) or without (FCP) a color class.
    const TRISTATE_FILTER_CYCLE = [false, null, true]
    const advanceVarianceFilter = () => {
      const currentIndex = TRISTATE_FILTER_CYCLE.indexOf(varianceFilter.value)
      varianceFilter.value = TRISTATE_FILTER_CYCLE[(currentIndex + 1) % TRISTATE_FILTER_CYCLE.length]
    }

    // fcpToggleEnabled guard replaces the old per-zone v-if="fcpToggleEnabled" (the zones
    // themselves are gone now - see the template) - a no-op click while disabled, same effective
    // behavior as before.
    const advanceFcpFilter = () => {
      if (!fcpToggleEnabled) return
      const currentIndex = TRISTATE_FILTER_CYCLE.indexOf(fcpFilter.value)
      fcpFilter.value = TRISTATE_FILTER_CYCLE[(currentIndex + 1) % TRISTATE_FILTER_CYCLE.length]
    }
    // null = no restriction ("all") and renders every checkbox unchecked (not
    // pre-selected) — same nullable-multi-select shape as umrFilter/
    // accountNameFilter below. Starting unchecked-but-unfiltered means picking
    // one specific type is a single click (see toggleVarianceType) instead of
    // first having to clear a "start with everything checked" state.
    const varianceTypeFilter = ref(parseArrayQueryParam(initialQuery.type))
    // null = no restriction ("all"); otherwise an array of selected UMRs —
    // same nullable-multi-select shape as accountNameFilter below.
    const umrFilter = ref(parseArrayQueryParam(initialQuery.umr))
    const accountNameFilter = ref(parseArrayQueryParam(initialQuery.account))
    // Same nullable-multi-select shape as umrFilter/accountNameFilter - cross-filters
    // with both of them the same way they cross-filter with each other (see
    // varianceFilterOptions/fetchVarianceFilterOptions below), and, like
    // them, also narrows BDX Detail and Cashbook Details (see bdxDetailsFilterParams/
    // cashbookDetailsFilterParams/effectiveUmrAndClaimExcluding) since claim reference exists at
    // that same per-transaction
    // grain in all three tables.
    const claimFilter = ref(parseArrayQueryParam(initialQuery.claim))
    const selectedUmrRow = ref(initialQuery.umr_row || null)

    // Per explicit decision: clicking a row in Variance Transaction Details/BDX Detail/Cashbook
    // Details narrows the OTHER TWO tables down to that same (UMR, Claim Reference) - not the
    // table you clicked in, which only highlights the clicked row. Independent of/additive with
    // selectedUmrRow above (the REC UMRs panel's own click-to-drill-down) - narrows further
    // within whatever UMR is already selected there, rather than replacing it. sourceTable is
    // part of the state (not just umr/claim) specifically so each of the three tables knows
    // whether IT is the one to exempt from narrowing.
    const selectedTransaction = ref(null)

    // Feeds each table's own filter-change watcher below (not selectedTransaction directly) -
    // clicking a row IN a given table itself doesn't narrow its own fetch (each of these three
    // stays null in exactly that case), so watching selectedTransaction directly would trigger a
    // pointless reset-and-refetch (discarding whatever "Load more" progress the user had made)
    // every time they click a row in that same table. sourceTable exclusion here matches
    // effectiveUmrAndClaimForVarianceDetails/effectiveUmrAndClaimExcluding's own per-table checks
    // exactly - one narrowing computed per table, since each excludes a different sourceTable.
    const varianceNarrowingFromTransaction = computed(() => {
      if (!selectedTransaction.value || 'variance' === selectedTransaction.value.sourceTable) return null

      return `${selectedTransaction.value.umr}|||${selectedTransaction.value.claim}`
    })

    const bdxNarrowingFromTransaction = computed(() => {
      if (!selectedTransaction.value || 'bdx' === selectedTransaction.value.sourceTable) return null

      return `${selectedTransaction.value.umr}|||${selectedTransaction.value.claim}`
    })

    const cashbookNarrowingFromTransaction = computed(() => {
      if (!selectedTransaction.value || 'cashbook' === selectedTransaction.value.sourceTable) return null

      return `${selectedTransaction.value.umr}|||${selectedTransaction.value.claim}`
    })

    // CORRECTION (found via user testing, not assumed): "is this the exact row I clicked before"
    // was comparing only (umr, claim) - the same pair matchesSelectedTransaction deliberately uses
    // for cross-table narrowing (a confirmed, separate decision - that one intentionally ignores
    // report period). But (umr, claim) is NOT a unique row identity here: the same claim can span
    // multiple report periods as separate rows. Re-clicking "the same row" could silently compare
    // against a DIFFERENT row that happens to share the same UMR+Claim (a different month), so the
    // toggle-off never matched. Toggle/highlight now key on the full identity (report_month_year
    // included, same fields rowKey() already uses for the edit-checkbox feature) - cross-table
    // narrowing above is untouched, still umr+claim only, per that earlier explicit decision.
    //
    // CORRECTION 2 (LIQ-2411, found via user testing, not assumed): even (umr, claim,
    // report_month_year) together is still NOT a unique row identity for BDX/Cashbook Detail -
    // those are individual transaction-grain rows, and multiple distinct transactions can
    // legitimately share all three (confirmed live: one real group had 244 separate cashbook
    // transactions under the same UMR/Claim/Period). Clicking one highlighted every row in the
    // group. BDX/Cashbook rows now carry a real, stable per-transaction `id` from the backend
    // (added for exactly this) - use it when present.
    //
    // CORRECTION 3 (LIQ-2563): Variance Transaction Details rows have no `id`, and are one row per
    // ACCOUNT + claim + period, not per claim + period - different accounts can share the same UMR,
    // claim and period (confirmed: 130 such groups, e.g. one UMR/claim/month under three accounts),
    // so clicking one highlighted all of them. They now match on account_name too, the same four
    // fields rowKey() already uses for the edit checkboxes.
    const isSameTransactionRow = (a, sourceTable, row) => {
      if (!a || a.sourceTable !== sourceTable) return false
      if (undefined !== row.id) return a.id === row.id
      return (
        a.umr === row.umr &&
        a.claim === row.claim &&
        a.reportMonthYear === row.report_month_year &&
        a.accountName === (row.account_name || '')
      )
    }

    // Toggles off on a second click of the same row (same match key as isTransactionRowSelected
    // below), matching selectUmrFromList's own toggle pattern. Clicking a row in a DIFFERENT
    // table (or a different row in the same table) simply replaces the prior selection - only one
    // transaction drill-down is active at a time.
    const selectTransactionRow = (sourceTable, row) => {
      const isSame = isSameTransactionRow(selectedTransaction.value, sourceTable, row)

      selectedTransaction.value = isSame
        ? null
        : {
            sourceTable,
            id: row.id,
            umr: row.umr,
            claim: row.claim,
            reportMonthYear: row.report_month_year,
            accountName: row.account_name || '',
          }
    }

    const isTransactionRowSelected = (sourceTable, row) =>
      isSameTransactionRow(selectedTransaction.value, sourceTable, row)

    // b-table's @row-clicked fires for a click anywhere inside the row, including the Variance
    // table's own edit checkbox/select/input controls - those are a different action (mark this
    // row for editing, change its value) and shouldn't also toggle the transaction drill-down.
    // REPORTED BUG: checking the input/select/etc. tags alone missed a click that lands on the
    // Edit checkbox's own <td> (.fcp-variance-select-cell) but outside the small checkbox element
    // itself - that padding area is neither an <input> nor any other tag this guard checked, so
    // it fell through and selected the row instead of checking the box. Checking the cell's own
    // class too covers that same td's whole clickable area, not just the checkbox glyph within it.
    const handleTransactionRowClick = (sourceTable, item, index, event) => {
      if (
        event &&
        event.target &&
        event.target.closest('input, select, textarea, button, a, label, .fcp-variance-select-cell')
      )
        return

      selectTransactionRow(sourceTable, item)
    }

    // Custom column-resize handle for the Variance/BDX/Cashbook tables, replacing
    // the browser's native CSS `resize` grip (which always renders as a diagonal
    // double-line icon) with a plain draggable divider line. Widths are keyed by
    // `${table}:${fieldKey}` so the three tables resize independently even where
    // they share a field key (e.g. "umr").
    //
    // 'variance:claim'/'variance:submit_variance_comment' seed a default here
    // (rather than starting empty like every other column) because their
    // values vary wildly in length - one long value on any row otherwise
    // stretches the whole column (and the table) to fit it. Still just a
    // starting width, not a cap: startColumnResize below overwrites the same
    // key, so the user can still drag either wider.
    const columnWidths = ref({
      'variance:claim': 140,
      'variance:submit_variance_comment': 160,
      'cashbook:narrative': 160,
      // Some Coverholder group names run to 60+ characters (LIQ-2402).
      'bdx:coverholder': 160
    })

    const columnStyle = (widthKey) => {
      const width = columnWidths.value[widthKey]

      // Only clip/ellipsis once a width exists for this column (either the
      // 'variance:claim' default above or a user drag) — left unconstrained,
      // cells should size to fit their content (matching table auto-layout)
      // rather than truncating by default. Applied to every cell in the
      // column (header, body, and the Total row), not just the header —
      // table-layout: auto sizes each column to its widest cell regardless of
      // which cell asked for a narrower width, so constraining only the
      // header can't actually shrink the column while a wider, unconstrained
      // value cell is still in play.
      return width
        ? {
            width: `${width}px`,
            maxWidth: `${width}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        : {}
    }

    // Keeps whichever ONE of this report's ~20 separate column-header checklist <b-dropdown>s
    // (UMR/Claim/Account Name/Report Period/Variance Type/Admin Approval, across all four tables)
    // is currently open tracking its own trigger icon, however that icon's position changes.
    // FIRST ATTEMPT (reported not working) only re-positioned during startColumnResize's own drag
    // - too narrow a fix: picking a value in one of these very dropdowns re-filters the table,
    // which reflows every column's own table-layout: auto width (more/fewer matching rows can
    // change the widest cell in ANY column), moving this same dropdown's own icon with no resize
    // drag involved at all. Rather than chasing every individual cause (resize drag, a filter
    // narrowing the table, a sort, anything else that can reflow this table), this polls the
    // toggle's own actual bounding rect every animation frame while a dropdown is open and only
    // calls updatePopper() (bootstrap-vue's own public "re-computes position" method) when that
    // rect has genuinely changed since the last frame - correct regardless of the cause, same
    // "don't assume why, verify against the real rect" approach RecColumnSearchMenu.vue's own
    // onScroll already uses.
    //
    // The menu's own height is watched too (LIQ-2402, reported): typing in a checklist's search box
    // shrinks the list, and a menu that had been placed ABOVE its icon (not enough room below, e.g.
    // the BDX grid near the bottom of the page) kept the position worked out for its full height -
    // left floating mid-page, well away from the icon, since the icon itself never moved.
    let openDropdownVm = null
    let openDropdownToggleEl = null
    let openDropdownMenuEl = null
    let openDropdownLastRect = null
    let openDropdownLastMenuHeight = null
    let openDropdownRafId = null

    const trackOpenDropdownPosition = () => {
      if (!openDropdownVm || !openDropdownToggleEl) {
        openDropdownRafId = null
        return
      }

      const rect = openDropdownToggleEl.getBoundingClientRect()
      const menuHeight = openDropdownMenuEl ? openDropdownMenuEl.offsetHeight : null
      const last = openDropdownLastRect
      if (!last || rect.top !== last.top || rect.left !== last.left || menuHeight !== openDropdownLastMenuHeight) {
        openDropdownVm.updatePopper()
        openDropdownLastRect = rect
        openDropdownLastMenuHeight = menuHeight
      }

      openDropdownRafId = requestAnimationFrame(trackOpenDropdownPosition)
    }

    const onBvDropdownShown = (vm) => {
      openDropdownVm = vm
      // toggle is bootstrap-vue's own $ref for the trigger element - a plain DOM node for a
      // regular <b-dropdown> (as these all are), but normalized via .$el same as showMenu's own
      // createPopper call does, in case some future usage ever makes it a component instead.
      var toggle = vm.$refs && vm.$refs.toggle
      openDropdownToggleEl = (toggle && toggle.$el) || toggle || null
      openDropdownLastRect = openDropdownToggleEl ? openDropdownToggleEl.getBoundingClientRect() : null
      // menu is bootstrap-vue's own $ref for the dropdown menu itself - normalized the same way.
      var menu = vm.$refs && vm.$refs.menu
      openDropdownMenuEl = (menu && menu.$el) || menu || null
      openDropdownLastMenuHeight = openDropdownMenuEl ? openDropdownMenuEl.offsetHeight : null

      if (openDropdownToggleEl && null === openDropdownRafId) {
        openDropdownRafId = requestAnimationFrame(trackOpenDropdownPosition)
      }
    }

    const onBvDropdownHidden = (vm) => {
      if (openDropdownVm !== vm) return
      openDropdownVm = null
      openDropdownToggleEl = null
      openDropdownMenuEl = null
      openDropdownLastRect = null
      openDropdownLastMenuHeight = null
    }

    const startColumnResize = (event, widthKey) => {
      event.preventDefault()

      const th = event.target.closest('th')
      const startX = event.clientX
      const startWidth = th ? th.offsetWidth : 100

      const onMouseMove = (moveEvent) => {
        columnWidths.value = {
          ...columnWidths.value,
          [widthKey]: Math.max(40, startWidth + (moveEvent.clientX - startX))
        }
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }

    // Backs the Variance Transaction Details table's per-column search boxes
    // (HEADER_SEARCH_SORT_FIELD_KEYS above) - keyed by field key, same shape as
    // columnWidths. Matches against each row's raw (unformatted) value, not
    // whatever formatCurrency/formatDate render it as.
    const SEARCH_QUERY_PREFIX = 'search_'

    // Range columns (RANGE_FILTER_FIELD_KEYS: min/max) and date-range columns
    // (DATE_RANGE_FILTER_FIELD_KEYS: from/to) each persist as two separate query params instead
    // of one flat string - their {min,max}/{from,to} value can't round-trip through a single
    // query value the way a plain search string can (see the query-building effect below, which
    // writes these same paired params back out).
    const COLUMN_SEARCH_BOUND_SUFFIXES = ['min', 'max', 'from', 'to']

    const columnSearch = ref(
      Object.keys(initialQuery)
        .filter((key) => key.startsWith(SEARCH_QUERY_PREFIX))
        .reduce((acc, key) => {
          const value = initialQuery[key]
          const rawValue = Array.isArray(value) ? value[0] : value
          const suffix = key.slice(SEARCH_QUERY_PREFIX.length)
          const bound = COLUMN_SEARCH_BOUND_SUFFIXES.find((b) => suffix.endsWith(`_${b}`))

          if (bound) {
            const fieldKey = suffix.slice(0, -1 * bound.length - 1)
            acc[fieldKey] = { ...(acc[fieldKey] || {}), [bound]: rawValue }
          } else {
            acc[suffix] = rawValue
          }

          return acc
        }, {})
    )

    const setColumnSearch = (key, text) => {
      columnSearch.value = { ...columnSearch.value, [key]: text }
    }

    // The column-search popover's own open/closed/position state, and the portal-to-<body>/
    // outside-click/scroll/Escape handling around it, live entirely inside RecColumnSearchMenu.vue
    // now - NOT as refs here. See columnSearchMenuRef's own template usage (a method call on the
    // child, not a reactive write here) for why: this table's ~15 #cell() scoped slots aren't
    // "stable" to bootstrap-vue's b-table, so a ref written here would force a full re-render of
    // every row/cell on every open/close (profiled at 7-10s over ~100 rows) even though nothing in
    // the table's own data changed. columnSearch (the actual per-column search text) still lives
    // here, since typing IS supposed to narrow displayedVarianceRows below.
    const columnSearchMenuRef = ref(null)

    // Same reasoning as columnSearchMenuRef above, for the Variance Type/Admin Approval cells'
    // own dropdown (see RecInlineOptionsMenu.vue) - a method call on the child, never a ref here.
    const inlineOptionsMenuRef = ref(null)

    // Bound to <b-table>'s sort-by/sort-desc via .sync so the header's own sort
    // icons (rather than bootstrap-vue's default click-the-header-label
    // handling, which isn't used here) drive which column the table is sorted
    // by. Defaults match the table's own previous static sort-by/sort-desc.
    const sortBy = ref(initialQuery.sort_by || 'variance_amount')
    const sortDesc = ref('asc' !== initialQuery.sort_dir)

    // Shared by Variance/BDX/Cashbook's own toggleXColumnSort below - each table has its own
    // independent sortBy/sortDesc pair (a sort caret click in one table never affects another).
    const makeColumnSortToggle = (sortByRef, sortDescRef) => (key) => {
      if (sortByRef.value === key) {
        sortDescRef.value = !sortDescRef.value
      } else {
        sortByRef.value = key
        sortDescRef.value = false
      }
    }

    // Whether the caller has ever actually clicked a Variance Transaction Details sort caret (vs.
    // still being in the initial/untouched or just-reset state) - drives group_by_umr on
    // /variance-details (see varianceDetailsFilterParams below): untouched means the table stays
    // grouped by UMR (the backend's own default ordering, service.sort_variance_df); the first
    // click switches to a plain per-column sort (service.sort_details_df) and stays that way until
    // a full filter reset. Seeded true if the initial URL already carried an explicit sort_by -
    // sharing/reloading a link with a sort baked in should keep that plain sort, not silently
    // revert to grouped-by-UMR.
    const varianceSortTouched = ref(Boolean(initialQuery.sort_by))

    const toggleVarianceColumnSort = makeColumnSortToggle(sortBy, sortDesc)
    const toggleColumnSort = (key) => {
      varianceSortTouched.value = true
      toggleVarianceColumnSort(key)
    }

    // BDX Detail by Transaction's own sort state - real server-side sort (see BdxDetailsRequest's
    // sort_by/sort_dir), unlike Variance's own sortBy/sortDesc above which ALSO drive a real
    // server-side sort but happen to share this exact same shape by precedent. Defaults match
    // BdxDetailsRequest's own defaults (report_month_date, desc) so an untouched table matches
    // what the backend would already return with no sort params at all.
    const bdxSortBy = ref('report_month_date')
    const bdxSortDesc = ref(true)
    const toggleBdxColumnSort = makeColumnSortToggle(bdxSortBy, bdxSortDesc)

    // Cashbook Details by Transaction counterpart to the BDX sort state above - same reasoning.
    const cashbookSortBy = ref('report_month_date')
    const cashbookSortDesc = ref(true)
    const toggleCashbookColumnSort = makeColumnSortToggle(cashbookSortBy, cashbookSortDesc)

    // BDX Detail by Transaction's own per-column search state - real server-side search (see
    // BDX_HEADER_SEARCH_FIELD_KEYS above), NOT client-side like Variance's columnSearch. Shape
    // matches columnSearch exactly (plain string for text columns, { min, max } for range columns,
    // { from, to } for date-range columns) so rec-column-search-menu (built for Variance) can be
    // reused here unchanged - it only ever reads/writes whichever key its own open popover is
    // anchored to, with no assumption about which table that key belongs to.
    const bdxColumnSearch = ref({})
    const setBdxColumnSearch = (key, text) => {
      bdxColumnSearch.value = { ...bdxColumnSearch.value, [key]: text }
    }
    // Own popover instance (not a shared one with Variance's columnSearchMenuRef) - BDX/Cashbook/
    // Variance can have field keys in common (e.g. total_paid_bdx exists on both Variance and BDX),
    // and columnSearch/bdxColumnSearch/cashbookColumnSearch are three SEPARATE objects, so a single
    // shared popover instance would have no way to know which table's state a given key belongs to.
    const bdxColumnSearchMenuRef = ref(null)
    const bdxColumnSearchTypeFor = (key) => {
      if (BDX_CHECKLIST_FIELD_KEYS.includes(key)) return 'checklist'
      if (BDX_RANGE_FILTER_FIELD_KEYS.includes(key)) return 'range'
      if (BDX_DATE_RANGE_FILTER_FIELD_KEYS.includes(key)) return 'date-range'
      return 'text'
    }

    // TPA/Coverholder column checklists (LIQ-2402). The ticked names are bdxColumnSearch.tpa/
    // .coverholder (arrays), so the BDX watcher below refetches on a change exactly as it does for
    // every other BDX column search. Options come from /bdx-details' own filter_options, each
    // already cross-filtered by every other filter but not its own selection - see fetchBdxDetails.
    const bdxFilterOptions = ref({ tpa: [], coverholder: [] })
    const bdxChecklistSearch = ref({ tpa: '', coverholder: '' })
    const bdxChecklistSelected = (key) => (Array.isArray(bdxColumnSearch.value[key]) ? bdxColumnSearch.value[key] : [])
    // A ticked name the latest options no longer include (e.g. after a period change) stays listed,
    // so it can still be unticked rather than silently narrowing the grid from out of sight.
    const bdxChecklistOptions = (key) => {
      const options = bdxFilterOptions.value[key] || []
      const optionSet = new Set(options)
      return [...options, ...bdxChecklistSelected(key).filter((name) => !optionSet.has(name))]
    }
    // Matched against the displayed (Demo Mode-masked) name - same reasoning as filteredUmrOptions.
    const filteredBdxChecklistOptions = (key) => {
      const query = (bdxChecklistSearch.value[key] || '').trim().toLowerCase()
      const options = bdxChecklistOptions(key)
      return query ? options.filter((name) => maskCellValue(key, name).toLowerCase().includes(query)) : options
    }
    const isBdxChecklistSelected = (key, name) => bdxChecklistSelected(key).includes(name)
    const allBdxChecklistSelected = (key) => {
      const options = bdxChecklistOptions(key)
      const selected = new Set(bdxChecklistSelected(key))
      return options.length > 0 && options.every((name) => selected.has(name))
    }
    const toggleAllBdxChecklist = (key) => {
      setBdxColumnSearch(key, allBdxChecklistSelected(key) ? [] : [...bdxChecklistOptions(key)])
    }
    const toggleBdxChecklist = (key, name) => {
      const selected = bdxChecklistSelected(key)
      setBdxColumnSearch(key, selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name])
    }
    // Enter in the checklist's own search box ticks every name it currently shows - same as
    // selectAllFilteredUmrs.
    const selectAllFilteredBdxChecklist = (key) => {
      const shown = filteredBdxChecklistOptions(key)
      if (!shown.length) return
      setBdxColumnSearch(key, Array.from(new Set([...bdxChecklistSelected(key), ...shown])))
    }

    // Cashbook Details by Transaction counterpart to the three BDX search refs above - same
    // reasoning, own separate state/popover instance.
    const cashbookColumnSearch = ref({})
    const setCashbookColumnSearch = (key, text) => {
      cashbookColumnSearch.value = { ...cashbookColumnSearch.value, [key]: text }
    }
    const cashbookColumnSearchMenuRef = ref(null)
    const cashbookColumnSearchTypeFor = (key) => (CASHBOOK_RANGE_FILTER_FIELD_KEYS.includes(key) ? 'range' : 'text')

    // Whether a generic #head() fallback column's own search/range/date-range value currently
    // holds a real filter - drives that column's search-icon swap to filterAppliedIcon the same
    // way isPeriodFilterActive/isUmrFilterActive/etc. already drive it for the checklist columns.
    // Those never needed this: each keeps its own dedicated ref (umrFilter, claimFilter, ...)
    // rather than a per-key map like columnSearch/bdxColumnSearch/cashbookColumnSearch, so their
    // own "is it active" check is just that ref's own truthiness, not a shape-dependent read out
    // of a shared map.
    const isColumnSearchValueActive = (value, type) => {
      if ('checklist' === type) return Array.isArray(value) && value.length > 0
      if ('range' === type) return !!(value && (value.min || value.max))
      if ('date-range' === type) return !!(value && (value.from || value.to))
      return !!(value && String(value).trim())
    }
    const isBdxColumnSearchActive = (key) =>
      isColumnSearchValueActive(bdxColumnSearch.value[key], bdxColumnSearchTypeFor(key))
    const isCashbookColumnSearchActive = (key) =>
      isColumnSearchValueActive(cashbookColumnSearch.value[key], cashbookColumnSearchTypeFor(key))

    // BDX Detail by Transaction's own Report Period column dropdown - own independent expand/
    // collapse state (NOT shared with Column A's own expandedYears, Variance's own
    // periodDropdownExpandedYears, or REC UMRs' own umrPanelPeriodDropdownExpandedYears), same
    // reasoning as each of those: opening/closing a year in one tree must never visibly shift a
    // different, unrelated tree. Reuses selectedPeriods/periodTree/isYearSelected/etc. wholesale
    // otherwise - the exact same state backing every other Report Period tree on this page (see
    // #head(report_month_year) below).
    const bdxPeriodDropdownExpandedYears = ref([])
    const isBdxPeriodDropdownYearExpanded = (year) => bdxPeriodDropdownExpandedYears.value.includes(year)
    const toggleBdxPeriodDropdownYearExpanded = (year) => {
      bdxPeriodDropdownExpandedYears.value = isBdxPeriodDropdownYearExpanded(year)
        ? bdxPeriodDropdownExpandedYears.value.filter((y) => y !== year)
        : [...bdxPeriodDropdownExpandedYears.value, year]
    }

    // Cashbook Details by Transaction counterpart to the BDX expand state above - same reasoning.
    const cashbookPeriodDropdownExpandedYears = ref([])
    const isCashbookPeriodDropdownYearExpanded = (year) => cashbookPeriodDropdownExpandedYears.value.includes(year)
    const toggleCashbookPeriodDropdownYearExpanded = (year) => {
      cashbookPeriodDropdownExpandedYears.value = isCashbookPeriodDropdownYearExpanded(year)
        ? cashbookPeriodDropdownExpandedYears.value.filter((y) => y !== year)
        : [...cashbookPeriodDropdownExpandedYears.value, year]
    }

    const varianceToggleLabel = computed(() => {
      if (true === varianceFilter.value) return 'Variance (Yes)'
      if (false === varianceFilter.value) return 'Variance (No)'

      return 'Variance ?'
    })

    const sourceDropdownLabel = computed(() => {
      if ('bdx' === sourceFilter.value) return 'BDX Only'
      if ('cashbook' === sourceFilter.value) return 'CB Only'
      if ('both' === sourceFilter.value) return 'Both'

      return 'All'
    })

    // Passed directly as RecInlineOptionsMenu's onSelect (see #head(source) below) - maps its "All"
    // option's "" sentinel value back to sourceFilter's own null, same convention
    // SOURCE_FILTER_MENU_OPTIONS' own comment describes.
    const setSourceFilter = (choice) => {
      sourceFilter.value = '' === choice ? null : choice
    }

    // Drives the Source column's own search icon "active" styling (see #head(source) below) -
    // same purpose as isVarianceTypeFilterActive/isApprovalStatusFilterActive, just for
    // sourceFilter. A second, independent access point onto the exact same sourceFilter the
    // toolbar's own Source dropdown already drives - both stay in sync since they share this one
    // ref.
    const isSourceFilterActive = computed(() => null !== sourceFilter.value)

    const fcpToggleLabel = computed(() => {
      if (true === fcpFilter.value) return 'FCP (Yes)'
      if (false === fcpFilter.value) return 'FCP (No)'

      return 'FCP ?'
    })

    const reviewStatusOptions = REVIEW_STATUS_OPTIONS

    // Multi-select, same nullable-array shape as umrFilter/accountNameFilter -
    // null renders every checkbox unchecked (no restriction, same as never
    // having checked anything); an explicit array restricts to rows whose
    // variance_status is in it.
    const isReviewStatusSelected = (status) =>
      Array.isArray(approvalStatusFilter.value) && approvalStatusFilter.value.includes(status)

    const allReviewStatusSelected = computed(
      () =>
        Array.isArray(approvalStatusFilter.value) &&
        approvalStatusFilter.value.length === REVIEW_STATUS_OPTIONS.length
    )

    const toggleAllReviewStatus = () => {
      approvalStatusFilter.value = allReviewStatusSelected.value ? [] : [...REVIEW_STATUS_OPTIONS]
    }

    const reviewStatusDropdownLabel = computed(() => {
      if (!approvalStatusFilter.value || !approvalStatusFilter.value.length) return 'All'

      return `${approvalStatusFilter.value.length} selected`
    })

    // Starting from null (nothing explicitly picked yet), checking a single
    // status isolates to just that one status in one click, instead of
    // expanding to "every status except this one" the way toggling within an
    // explicit selection does.
    const toggleReviewStatus = (status) => {
      const currentlySelected = null === approvalStatusFilter.value ? [] : approvalStatusFilter.value

      approvalStatusFilter.value = currentlySelected.includes(status)
        ? currentlySelected.filter((s) => s !== status)
        : [...currentlySelected, status]
    }

    // Drives the Status? column's own search icon "active" styling (see #head(variance_status)
    // below) - same purpose as isVarianceTypeFilterActive, just for approvalStatusFilter. This is
    // a second, independent access point onto the exact same approvalStatusFilter the toolbar's
    // own Review Status dropdown already drives (explicit decision to add rather than migrate,
    // unlike Variance Type) - both stay in sync automatically since they share this one ref.
    const isApprovalStatusFilterActive = computed(
      () => Array.isArray(approvalStatusFilter.value) && approvalStatusFilter.value.length > 0
    )

    // Ref to RecStatusFilterMenu's own instance - a method call on it (see #head(variance_status)
    // below), never a ref written here, for the same reason columnSearchMenuRef/
    // inlineOptionsMenuRef aren't either (see their own comments).
    const statusFilterMenuRef = ref(null)

    const varianceFields = computed(() => [
      { key: 'variance_status', label: 'Status?' },
      { key: 'report_month_year', label: 'Report Period', class: 'text-left' },
      { key: 'source', label: 'Source', class: 'text-left' },
      { key: 'umr', label: 'UMR', class: 'text-left' },
      { key: 'claim', label: 'Claim Reference', class: 'text-left' },
      { key: 'total_paid_bdx', label: 'Total Paid (BDX)', class: 'text-right' },
      { key: 'total_paid_fcp', label: 'Total Paid (REC)', class: 'text-right' },
      { key: 'variance_amount', label: 'Variance', class: 'text-right' },
      ...(canEditVariance.value || canEditAdminReview.value
        ? [{ key: '__select', label: 'Edit', class: 'fcp-variance-select-cell' }]
        : []),
      { key: 'variance_type', label: 'Variance Type', class: 'text-left' },
      { key: 'submit_variance_comment', label: 'Submitter Comments', class: 'text-left' },
      { key: 'admin_approval', label: 'Admin Approval' },
      { key: 'admin_variance_comment', label: 'Admin Comments', class: 'text-left' },
      { key: 'submission_date', label: 'Submit Date' },
      { key: 'admin_variance_date', label: 'Admin Date' },
      { key: 'account_name', label: 'Account Name', class: 'text-left' }
    ])

    // TPA/Coverholder sit right after Report Period, as requested in LIQ-2402. downloadBdxExcel
    // builds the export from this same list, so they are exported too.
    const bdxFields = [
      { key: 'report_month_year', label: 'Report Period' },
      { key: 'tpa', label: 'TPA' },
      { key: 'coverholder', label: 'Coverholder' },
      { key: 'umr', label: 'UMR' },
      { key: 'section_number', label: 'Section #' },
      { key: 'claim', label: 'Claim Reference', class: 'text-left' },
      { key: 'total_paid_bdx', label: 'Total Paid', class: 'text-right' },
      { key: 'paid_tpa_fees', label: 'Paid TPA Fees', class: 'text-right' },
      { key: 'fcp_start_date', label: 'REC Start Date' },
      { key: 'account_name', label: 'Account Name' },
      { key: 'limeiq_key', label: 'LimeIQ Key' }
    ]

    const cashbookFields = [
      { key: 'report_month_year', label: 'Report Period' },
      { key: 'umr', label: 'UMR' },
      { key: 'section_number', label: 'Section #' },
      { key: 'claim', label: 'Claim Reference', class: 'text-left' },
      { key: 'debit', label: 'Debit', class: 'text-right' },
      { key: 'credit', label: 'Credit', class: 'text-right' },
      { key: 'narrative', label: 'Narrative' },
      { key: 'account_name', label: 'Account Name' }
    ]

    // Guards against out-of-order responses: if the user changes the period selection again
    // before an in-flight request resolves, a stale response arriving after a newer one must not
    // clobber it. Incremented per call, compared against on resolution/rejection.
    let fetchRequestId = 0

    // Set right before this component itself reassigns selectedPeriods (to sync the checkbox tree
    // to the backend's resolved default below) - the watcher checks this so that internal sync
    // doesn't trigger its own redundant refetch of data this same response already delivered.
    let suppressNextPeriodsFetch = false

    // fetchReportPeriodRange and fetchReport now fire concurrently (see onMounted) rather than
    // sequentially, so their two independent selectedPeriods writes can arrive in either order:
    // fetchReportPeriodRange seeds "everything selected" (once it resolves), fetchReport's own
    // success handler narrows that down to whatever periods the backend actually defaulted to
    // (once IT resolves) - see wasRequestingDefault below. If fetchReport's narrower, more
    // accurate write happened FIRST, fetchReportPeriodRange resolving afterward must not clobber
    // it back to "everything selected" (which would leave every checkbox ticked while the table
    // only shows one year's worth of rows). Set the moment fetchReport applies its own echo;
    // checked by fetchReportPeriodRange before it seeds "everything selected" at all.
    let initialDefaultPeriodResolved = false
    // Snapshot of whatever the backend resolved as its own default period(s) the FIRST time
    // wasRequestingDefault resolved (see below) - Clear Filters restores to this instead of
    // "every period selected" (per explicit request: it should land back on the same view a
    // fresh page load shows, not a maximally-broad one), without needing a second round trip to
    // ask the backend again for the same answer it already gave once this session.
    let initialDefaultPeriods = null

    // One axios.CancelToken source per fetch function below (fetchMyRole/fetchReportPeriodRange/
    // fetchReport/fetchPartyGroupOptions/fetchVarianceDetails), so a still-in-flight call of a
    // given function gets cancelled the moment a newer call to that SAME function starts, instead
    // of continuing to run and compete for the browser's per-origin connection limit. Cancelling
    // doesn't guarantee the backend stops computing the response too (that depends on the ASGI
    // server/DB driver noticing the client disconnected), but it does free up the client-side
    // connection immediately and guarantees the stale response, whenever it does arrive, is never
    // acted on. Complements (doesn't replace) the existing fetchRequestId/varianceFetchRequestId
    // staleness guards, which only discard a stale RESPONSE - this discards the stale REQUEST
    // itself. Set to null on cancel/reject too, not left dangling, so a later cancel() call can't
    // fire against an already-settled source.
    let myRoleCancelSource = null
    let reportPeriodRangeCancelSource = null
    let productionCancelSource = null
    let partyGroupsCancelSource = null
    let varianceDetailsCancelSource = null

    // Resolves roleScope (§ above) from GET /my-role - a convenience lookup only, never verified
    // by the data endpoints themselves (see fetchReportPeriodRange/fetchReport/
    // fetchPartyGroupOptions - none of them take role/role_value as a request param). Still needed
    // client-side for leadFcpKeys/canEditAdminReview and for displaying "viewing as X (Y)" -
    // fired alongside the other three on mount (see onMounted), not awaited before them.
    const fetchMyRole = async () => {
      if (myRoleCancelSource) myRoleCancelSource.cancel()
      myRoleCancelSource = axios.CancelToken.source()
      const cancelToken = myRoleCancelSource.token

      try {
        const response = await ReconciliationService.getFCPReportV2MyRole({ cancelToken })
        const roles = (response && response.data && response.data.roles) || []

        if (!roles.length) {
          error.value =
            'Your account is not mapped to a TPA, Broker, Underwriter, or Coverholder group - contact an administrator.'
          return
        }

        // A company name can legitimately map to more than one (Party Type, Group Name) pair
        // (e.g. "Tysers" is both a Broker and a Coverholder) - the first is used as this report's
        // scope since there's no picker UI for choosing between them yet.
        roleScope.value = roles[0]
      } catch (err) {
        if (axios.isCancel(err)) return

        error.value = requestErrorMessage(err, 'Failed to resolve your access role.')
      }
    }

    // Resolves reportPeriodYearMonths (§ above) from GET /report-period-range, then seeds
    // selectedPeriods to "everything selected" - the same role reportPeriodYearMonths/
    // selectedPeriods play here that ALL_PERIOD_KEYS/`ref([...ALL_PERIOD_KEYS])` used to play
    // before this was made per-caller. Scoped server-side from the caller's own JWT (see
    // getFCPReportV2ReportPeriodRange's own comment) - doesn't need roleScope resolved client-side
    // at all, so this fires immediately on mount rather than waiting on GET /my-role first (see
    // onMounted below).
    const fetchReportPeriodRange = async () => {
      if (reportPeriodRangeCancelSource) reportPeriodRangeCancelSource.cancel()
      reportPeriodRangeCancelSource = axios.CancelToken.source()
      const cancelToken = reportPeriodRangeCancelSource.token

      try {
        const response = await ReconciliationService.getFCPReportV2ReportPeriodRange({ cancelToken })
        const data = (response && response.data) || {}
        reportPeriodYearMonths.value = buildReportPeriodYearMonths(data.earliest_report_period)
      } catch (err) {
        // A cancelled call means a newer one superseded it - that one will run the rest of this
        // function itself, so skip it here entirely rather than let a stale call race a fresher
        // one through the selectedPeriods-seeding logic below.
        if (axios.isCancel(err)) return
        reportPeriodYearMonths.value = []
      }

      expandedYears.value = reportPeriodYears.value.length ? [reportPeriodYears.value[0]] : []

      // Skipped when the URL already named explicit periods (see
      // hadExplicitPeriodsInUrl) - a shared/bookmarked link should load with
      // exactly the periods it named, not get silently widened back out to
      // "everything selected". Also skipped once fetchReport's own echo has already narrowed
      // selectedPeriods to the backend's actual default (see initialDefaultPeriodResolved's own
      // comment) - this call resolving after that would otherwise overwrite the correct, narrower
      // selection back to "everything".
      if (hadExplicitPeriodsInUrl || initialDefaultPeriodResolved) return

      // Seeding selectedPeriods here would otherwise trigger the watch(selectedPeriods, ...)
      // below and fire a redundant fetchReportDebounced() - suppressed the same way the
      // backend's own default-period echo already does elsewhere in this file.
      suppressNextPeriodsFetch = true
      selectedPeriods.value = [...allPeriodKeys.value]
    }

    // forceDefaultPeriods: only ever passed by clearFilters (see its own comment) - lets THAT
    // one caller ask for the backend's default even when hadExplicitPeriodsInUrl would otherwise
    // permanently block wasRequestingDefault below for the rest of this session.
    const fetchReport = async ({ forceDefaultPeriods = false } = {}) => {
      const requestId = ++fetchRequestId
      if (productionCancelSource) productionCancelSource.cancel()
      productionCancelSource = axios.CancelToken.source()
      const cancelToken = productionCancelSource.token

      // selectedPeriods starts at [] on mount (unless the URL pinned explicit periods) and only
      // becomes "everything selected" once fetchReportPeriodRange resolves and seeds it - which,
      // now that fetchReport no longer waits on that call (see onMounted), can still be pending
      // when this fires. That's "not decided yet", not "the user explicitly unchecked everything"
      // (the one real empty-selection case this guard exists for) - only the latter should skip
      // the fetch. periodsNotYetResolved distinguishes them: reportPeriodYearMonths is still empty
      // and the URL never pinned anything, so there's nothing FOR the user to have unchecked yet.
      const periodsNotYetResolved = !hadExplicitPeriodsInUrl && !reportPeriodYearMonths.value.length

      // No periods selected at all ("Select all" toggled off with nothing re-picked) has no
      // backend equivalent - omitting report_periods means "every period", the opposite of what
      // an empty selection means here. Show nothing without a network round-trip.
      if (!selectedPeriods.value.length && !periodsNotYetResolved) {
        // Each panel's own fetch handles an empty selection itself: it cancels its request still
        // in flight (which would otherwise land afterward and refill the panel with the previous
        // selection's data), empties the panel and clears its loading flags. LIQ-2403: this used
        // to call only fetchRecUmrRollup, so the other panels kept stale data - and `loading`, left
        // on by the /production call cancelled above (whose finally can no longer clear it, its
        // requestId is stale), kept the Variance table showing "Loading..." for good.
        loading.value = false
        error.value = null
        fetchVarianceDetails({ append: false })
        fetchVarianceFilterOptions()
        fetchRecUmrRollup()
        fetchBdxDetails({ append: false })
        fetchCashbookDetails({ append: false })
        return
      }

      // Unlike selectedPeriods, dcaFilter has no "explicitly show nothing" state at all - DCA is
      // an optional, additional narrowing filter, not a primary navigation control the way Report
      // Period is (which has its own explicit "Select All"). null (nothing touched) and an empty
      // array (every DCA checkbox unchecked, e.g. via toggling "Select all" off) mean the exact
      // same thing here: no DCA narrowing active, fall back to whatever role/role_value + Report
      // Period already produce. getFCPReportV2Production/reconciliationService.js already omits
      // dca_selections entirely whenever the array is empty - no guard needed here to special-case
      // it (an earlier version of this guard wrongly treated an emptied-out selection the same as
      // "filter to zero rows", which isn't what unchecking every DCA box actually means).

      // periodsNotYetResolved also forces the "omit report_periods, let the backend pick its own
      // latest-available default" path below, the same as allPeriodsSelected does BEFORE the
      // initial default has been resolved once - this is what lets the very first call fire
      // without waiting on fetchReportPeriodRange at all (see onMounted), and covers the seeding
      // race where fetchReportPeriodRange's "everything selected" seed lands before fetchReport's
      // own narrower echo does.
      //
      // Guarded by !initialDefaultPeriodResolved so this ONLY applies pre-first-resolution - once
      // the initial default has already been echoed back once, allPeriodsSelected becoming true
      // again only happens because the user explicitly clicked "Select all" themselves, which must
      // NOT be treated the same as "not decided yet, give me your default": doing so previously
      // omitted report_periods, got back the backend's single-latest-month default, and the
      // response handler below then overwrote the user's own "select all" right back down to that
      // one month - the exact bug this guard fixes. Post-initial-resolution, "all periods
      // selected" now sends every period explicitly instead.
      //
      // ALSO guarded by !hadExplicitPeriodsInUrl, independently of initialDefaultPeriodResolved:
      // when the page loads with periods already named in the URL (a reload or shared link, not a
      // blank mount), initialDefaultPeriodResolved can never organically become true at all - it's
      // only ever set inside the "we asked for the default and got one back" branch below, which
      // periodsNotYetResolved (and so wasRequestingDefault) never reaches while
      // hadExplicitPeriodsInUrl holds. Without this clause, the FIRST "Select all" click in such a
      // session still hit the exact bug described above, since !initialDefaultPeriodResolved was
      // (permanently) true. hadExplicitPeriodsInUrl means periods were already explicitly decided
      // by the URL itself - there's no "backend default" to defer to in the first place.
      const wasRequestingDefault =
        forceDefaultPeriods ||
        (!hadExplicitPeriodsInUrl &&
          (periodsNotYetResolved || (!initialDefaultPeriodResolved && allPeriodsSelected.value)))

      // Reset before firing anything else this call - fetchVarianceDetails below sets
      // varianceLoadingInitial synchronously (as part of just being called, before its own first
      // await), so the overlay's :show condition can already be true by the next line. Leaving
      // isColdHit at whatever a PREVIOUS load left it as would show that stale value for this
      // load's entire (possibly much shorter, warm) duration until /production's own response
      // (which now carries is_cold - see below) lands and corrects it - so every load starts from
      // "assume warm" and only upgrades to the cold message once that response genuinely confirms
      // it, instead of inheriting an unrelated past load's answer.
      isColdHit.value = false

      // Fires variance-details CONCURRENTLY with the production request below, not after it,
      // whenever the periods to ask for are already known (the common case - explicit URL/user
      // selection, matching most page loads and every explicit checkbox interaction). Only the
      // wasRequestingDefault case genuinely has to wait: report_periods is omitted from the
      // production call then, so selectedPeriods isn't settled until THAT response echoes back
      // which period the backend actually defaulted to (see below) - variance-details needs that
      // same resolved period, so it can't fire until it's known. When periods are already
      // explicit, nothing about production's own response is needed to know what variance-details
      // should ask for, so making it wait was pure unnecessary latency - its own preflight+request
      // time was stacking on top of production's instead of overlapping it.
      if (!wasRequestingDefault) {
        fetchVarianceDetails({ append: false })
        fetchVarianceFilterOptions()
        fetchRecUmrRollup()
        fetchBdxDetails({ append: false })
        fetchCashbookDetails({ append: false })
      }

      loading.value = true
      error.value = null

      try {
        const response = await ReconciliationService.getFCPReportV2Production({
          // Omit entirely when every period is selected (the default/initial state) - the backend
          // now resolves this itself to the caller's latest available year rather than full
          // history (see docs/rec-report-frontend-integration.md), echoing back exactly which
          // periods it used in the response below. dca_selections is no longer sent here at all
          // (LIQ-2403) - this endpoint stopped returning row data entirely, and dca_selections
          // never affected default-period resolution itself (see
          // app.reports.rec.router.get_fcp_report_v2_production's own HISTORY OF THIS DECISION).
          report_periods: wasRequestingDefault ? null : selectedPeriods.value,
          cancelToken
        })

        if (requestId !== fetchRequestId) return

        const data = (response && response.data) || {}

        generatedAt.value = data.generated_at || null
        // GET /cache-status used to be its own separate, concurrent, fire-and-forget call purely
        // for this one boolean (see reconciliationService.js's own history note on
        // getFCPReportV2Production) - folded into this same response once /production got fast
        // enough to carry it just as early, removing one whole request (and its own preflight)
        // from the batch this load fires concurrently.
        isColdHit.value = !!data.is_cold

        // We asked for "everything" (omitted report_periods) but the backend applied its own
        // latest-year default - sync the checkbox tree to match what was actually loaded, and
        // expand that year, so the UI doesn't show every box checked while the table only has one
        // year's worth of rows.
        if (wasRequestingDefault && data.report_periods && data.report_periods.length) {
          initialDefaultPeriodResolved = true
          suppressNextPeriodsFetch = true
          if (!initialDefaultPeriods) initialDefaultPeriods = [...data.report_periods]
          selectedPeriods.value = data.report_periods
          const resolvedYear = data.report_periods[0].split(' ').pop()
          if (resolvedYear && !expandedYears.value.includes(resolvedYear)) {
            expandedYears.value = [resolvedYear]
          }
        }

        // Variance/BDX/Cashbook Details are each fetched separately (see the PAGINATION comment
        // above varianceDetailRows/bdxDetailRows) - triggered from here too, not just the
        // filter-change watchers below, since this is the one path that covers BOTH selectedPeriods
        // and dcaFilter changes (both already route through fetchReport) without needing their own
        // duplicate watchers for them. Only fetched HERE (after production resolves) for the
        // wasRequestingDefault case - the !wasRequestingDefault case already fired these
        // concurrently with the production request itself, before this try block even started (see
        // the comment above wasRequestingDefault), so firing them again here would just be a
        // redundant duplicate call for that case.
        if (wasRequestingDefault) {
          fetchVarianceDetails({ append: false })
          fetchVarianceFilterOptions()
          fetchRecUmrRollup()
          fetchBdxDetails({ append: false })
          fetchCashbookDetails({ append: false })
        }
      } catch (err) {
        // A cancelled call always means a newer one superseded it (requestId reflects that too,
        // via the same ++fetchRequestId a newer call already bumped before cancelling this one) -
        // isCancel is checked explicitly anyway as the more direct signal, and to cover
        // onBeforeUnmount's own cancellation, which doesn't touch fetchRequestId at all.
        if (axios.isCancel(err) || requestId !== fetchRequestId) return

        error.value = requestErrorMessage(err, 'Failed to load the REC report.')
      } finally {
        if (requestId === fetchRequestId) loading.value = false
      }
    }

    // Rapid period-checkbox clicks (several months in a row) would otherwise fire a fresh ~3.5s
    // full-history-shaped request per click - debounced so only the settled selection is fetched.
    const fetchReportDebounced = debounce(fetchReport, 400)

    const periodTree = computed(() =>
      reportPeriodYearMonths.value.map(({ year, months }) => ({
        year,
        // December-to-January (reverse of the Jan-first order months is built in), and
        // labelled with the year so it reads the same as the "Month Year" period
        // key it toggles, not just the bare month name.
        months: [...months].reverse().map((monthName) => ({
          key: `${monthName} ${year}`,
          label: `${monthName} ${year}`
        }))
      }))
    )

    const expandedYears = ref([])

    const isYearExpanded = (year) => expandedYears.value.includes(year)

    const toggleYearExpanded = (year) => {
      expandedYears.value = isYearExpanded(year)
        ? expandedYears.value.filter((y) => y !== year)
        : [...expandedYears.value, year]
    }

    // Report Period column dropdown's OWN expand/collapse state (see #head(report_month_year)
    // below) - deliberately separate from Column A's own expandedYears above, unlike
    // selectedPeriods/periodTree/etc. which the dropdown intentionally shares wholesale.
    // Reported bug: sharing expandedYears too meant opening/closing a year in this dropdown also
    // opened/closed it in Column A's own box, visibly shifting Column A's layout even though the
    // user was only interacting with the dropdown - selection state should stay in sync, but
    // which years happen to be expanded is pure per-tree UI state, not a real filter.
    const periodDropdownExpandedYears = ref([])

    const isPeriodDropdownYearExpanded = (year) => periodDropdownExpandedYears.value.includes(year)

    const togglePeriodDropdownYearExpanded = (year) => {
      periodDropdownExpandedYears.value = isPeriodDropdownYearExpanded(year)
        ? periodDropdownExpandedYears.value.filter((y) => y !== year)
        : [...periodDropdownExpandedYears.value, year]
    }

    const allPeriodsSelected = computed(
      () => 0 < allPeriodKeys.value.length && selectedPeriods.value.length === allPeriodKeys.value.length
    )

    // Drives the Report Period column's own search icon "active" styling (see
    // #head(report_month_year) below) - same purpose as isAccountNameFilterActive, just for
    // selectedPeriods. NOT a computed off selectedPeriods itself (tried first, reported bug: the
    // backend's own resolved default is almost never literally "every period ever" - typically
    // just the latest year - so "narrower than everything available" was true on a completely
    // untouched page load, showing this icon permanently active/orange when every other column's
    // stays neutral until the user actually does something). A plain flag instead: starts false,
    // flips true only from an actual user click in toggleAllPeriods/toggleYear/togglePeriod below
    // (NOT the backend-echo auto-narrowing in fetchReport, which isn't a user action), and back to
    // false in clearFilters, alongside every other filter's own reset.
    const isPeriodFilterActive = ref(false)

    // report_periods is the only filter that's actually server-side under the current (minimal)
    // wiring - every other toggle/dropdown still filters the already-fetched arrays client-side.
    // selectedPeriods is always reassigned to a new array (never mutated in place - see
    // toggleAllPeriods/toggleYear/togglePeriod below), so a shallow watch is sufficient.
    watch(selectedPeriods, () => {
      // LIQ-2405: the Party lists only offer parties with records in the selected periods, so they
      // follow every period change - including the backend's own default-period echo and Clear
      // Filters' restore, which skip the report refetch below but do still change the periods.
      fetchPartyGroupOptionsDebounced()
      if (suppressNextPeriodsFetch) {
        suppressNextPeriodsFetch = false
        return
      }
      fetchReportDebounced()
    })

    const isYearSelected = (group) =>
      group.months.every((month) => selectedPeriods.value.includes(month.key))

    // Drives the year checkbox's own .indeterminate (see the template - needs the .prop
    // modifier there, same as isPartyTypePartiallySelected below/its own comment, since
    // :indeterminate is a live DOM property, not a real HTML attribute) - true when SOME but
    // not all of this year's months are selected, mirroring the Party dropdown's own
    // Party-Type-row dot exactly.
    const isYearPartiallySelected = (group) => {
      const selectedCount = group.months.filter((month) => selectedPeriods.value.includes(month.key)).length

      return 0 < selectedCount && selectedCount < group.months.length
    }

    const toggleAllPeriods = () => {
      isPeriodFilterActive.value = true
      selectedPeriods.value = allPeriodsSelected.value ? [] : [...allPeriodKeys.value]
    }

    const toggleYear = (group) => {
      isPeriodFilterActive.value = true
      const keys = group.months.map((month) => month.key)
      const allSelected = keys.every((key) => selectedPeriods.value.includes(key))

      selectedPeriods.value = allSelected
        ? selectedPeriods.value.filter((key) => !keys.includes(key))
        : Array.from(new Set([...selectedPeriods.value, ...keys]))
    }

    const togglePeriod = (key) => {
      isPeriodFilterActive.value = true
      selectedPeriods.value = selectedPeriods.value.includes(key)
        ? selectedPeriods.value.filter((k) => k !== key)
        : [...selectedPeriods.value, key]
    }

    // Account Name/Claim Reference/Source/UMR's own cross-filtered option lists are all computed
    // server-side now (see fetchVarianceFilterOptions/varianceFilterOptions above, backed by
    // /variance-details/filter-options) - LIQ-2403. Used to each be derived here client-side from
    // a full, unpaginated per-claim fetch (matchesBaseFilters/preUmrFilterRows, since removed),
    // which contributed directly to /production's own reported "select all report periods" freeze.
    //
    // Hides (not just disables) whichever Source options would return zero rows given every other
    // active filter - per explicit request, an option with nothing behind it shouldn't even be
    // listed, not just grayed out. 'All' is always available - it never adds its own restriction,
    // so it can't itself be the reason nothing matches. varianceFilterOptions.source is the exact
    // same VALID_SOURCE_FILTERS strings ('No CB Entry'/'No BDX Entry'/'Both') already used
    // elsewhere in this file's own source param-building (see varianceDetailsFilterParams).
    const isBdxSourceOptionAvailable = computed(() => varianceFilterOptions.value.source.includes('No CB Entry'))
    const isCashbookSourceOptionAvailable = computed(() => varianceFilterOptions.value.source.includes('No BDX Entry'))
    const isBothSourceOptionAvailable = computed(() => varianceFilterOptions.value.source.includes('Both'))

    // Same option set/labels as SOURCE_FILTER_MENU_OPTIONS, filtered down by the exact same
    // availability computeds the toolbar's own Source dropdown uses (isBdxSourceOptionAvailable/
    // isCashbookSourceOptionAvailable/isBothSourceOptionAvailable above) - so the Source column's
    // header search dropdown (#head(source) below) cross-filters identically instead of always
    // showing all four options regardless of what the other active filters leave available.
    const sourceFilterMenuOptions = computed(() =>
      SOURCE_FILTER_MENU_OPTIONS.filter(({ value }) => {
        if ('bdx' === value) return isBdxSourceOptionAvailable.value
        if ('cashbook' === value) return isCashbookSourceOptionAvailable.value
        if ('both' === value) return isBothSourceOptionAvailable.value
        return true
      })
    )

    const varianceTypeOptions = VARIANCE_TYPE_OPTIONS

    // Narrows which checkboxes are rendered in the dropdown's own list, purely
    // client-side - unrelated to varianceTypeFilter (what's actually applied to
    // the table). Typing here doesn't change the selection, just what's visible
    // to pick from.
    const varianceTypeSearch = ref('')
    const filteredVarianceTypeOptions = computed(() => {
      const query = varianceTypeSearch.value.trim().toLowerCase()

      return query
        ? varianceTypeOptions.filter((type) => type.toLowerCase().includes(query))
        : varianceTypeOptions
    })

    const isVarianceTypeSelected = (type) =>
      Array.isArray(varianceTypeFilter.value) && varianceTypeFilter.value.includes(type)

    const allVarianceTypesSelected = computed(
      () =>
        Array.isArray(varianceTypeFilter.value) &&
        varianceTypeFilter.value.length === VARIANCE_TYPE_OPTIONS.length
    )

    const toggleAllVarianceTypes = () => {
      varianceTypeFilter.value = allVarianceTypesSelected.value ? [] : [...VARIANCE_TYPE_OPTIONS]
    }

    // Drives the search icon's "active" styling in #head(variance_type) now that
    // there's no toolbar button label ("3 selected") left to show that state.
    const isVarianceTypeFilterActive = computed(
      () => Array.isArray(varianceTypeFilter.value) && varianceTypeFilter.value.length > 0
    )

    // Starting from null (nothing explicitly picked yet), checking a single type
    // isolates to just that one type in one click, instead of expanding to
    // "every type except this one" the way toggling within an explicit
    // selection does.
    const toggleVarianceType = (type) => {
      const currentlySelected = null === varianceTypeFilter.value ? [] : varianceTypeFilter.value

      varianceTypeFilter.value = currentlySelected.includes(type)
        ? currentlySelected.filter((t) => t !== type)
        : [...currentlySelected, type]
    }

    // Enter in this checklist's own search box (the b-dropdown-form's @submit.prevent - see that
    // fix's own comment for why Enter needs handling here at all) selects every option the search
    // currently narrows the list to, rather than doing nothing: type enough of the name(s) to
    // isolate the right ones, then confirm with one keystroke instead of hunting down and clicking
    // each individual checkbox. Merges into whatever's already checked (doesn't replace it) - same
    // non-destructive reasoning togglePartyType's own comment gives for DCA's per-section "select
    // all" checkbox, just applied to Enter instead of a checkbox click.
    const selectAllFilteredVarianceTypes = () => {
      if (!filteredVarianceTypeOptions.value.length) return
      const currentlySelected = Array.isArray(varianceTypeFilter.value) ? varianceTypeFilter.value : []
      varianceTypeFilter.value = Array.from(
        new Set([...currentlySelected, ...filteredVarianceTypeOptions.value])
      )
    }

    // Cross-filtered server-side (see varianceFilterOptions/fetchVarianceFilterOptions above) -
    // e.g. an Account Name selection narrows this list down to only the UMRs that actually occur
    // under it. Used to be derived client-side from a full, unpaginated per-claim fetch
    // (preUmrFilterRows, since removed) - LIQ-2403.
    const umrOptions = computed(() => varianceFilterOptions.value.umr)

    // Same client-side, selection-independent narrowing as varianceTypeSearch above. Matches against
    // maskUmr(umr) - what the checklist actually displays - so in Demo Mode a search for an
    // on-screen masked UMR finds it (and a real one doesn't); outside Demo Mode maskUmr is a no-op.
    const umrSearch = ref('')
    const filteredUmrOptions = computed(() => {
      const query = umrSearch.value.trim().toLowerCase()

      return query
        ? umrOptions.value.filter((umr) => maskUmr(umr).toLowerCase().includes(query))
        : umrOptions.value
    })

    // null renders every checkbox unchecked (nothing explicitly picked yet),
    // not "all checked" - only an explicit array checks its own entries. A Set, not
    // umrFilter.includes: that scanned the whole selection once per checkbox (LIQ-2403).
    const selectedUmrSet = computed(() => new Set(Array.isArray(umrFilter.value) ? umrFilter.value : []))
    const isUmrSelected = (umr) => selectedUmrSet.value.has(umr)

    const allUmrsSelected = computed(
      () => Array.isArray(umrFilter.value) && umrFilter.value.length === umrOptions.value.length
    )

    const toggleAllUmrs = () => {
      umrFilter.value = allUmrsSelected.value ? [] : [...umrOptions.value]
    }

    const umrDropdownLabel = computed(() => {
      if (!umrFilter.value || !umrFilter.value.length) return 'All'

      return `${umrFilter.value.length} selected`
    })

    // Starting from null (nothing explicitly picked yet), checking a single UMR
    // isolates to just that one UMR in one click, instead of expanding to
    // "every UMR except this one" the way toggling within an explicit
    // selection does.
    const toggleUmr = (umr) => {
      const currentlySelected = null === umrFilter.value ? [] : umrFilter.value

      umrFilter.value = currentlySelected.includes(umr)
        ? currentlySelected.filter((u) => u !== umr)
        : [...currentlySelected, umr]
    }

    // Same "select all currently displayed/filtered options" Enter handler as
    // selectAllFilteredVarianceTypes above, for UMR's own checklist.
    const selectAllFilteredUmrs = () => {
      if (!filteredUmrOptions.value.length) return
      const currentlySelected = Array.isArray(umrFilter.value) ? umrFilter.value : []
      umrFilter.value = Array.from(new Set([...currentlySelected, ...filteredUmrOptions.value]))
    }

    // Drives the UMR column's own search icon "active" styling (see #head(umr) below) - same
    // purpose as isAccountNameFilterActive, just for umrFilter. A second, independent access
    // point onto the exact same umrFilter the toolbar's own UMR dropdown already drives - both
    // stay in sync automatically since they share this one ref.
    const isUmrFilterActive = computed(() => Array.isArray(umrFilter.value) && umrFilter.value.length > 0)

    // Server-computed now (see varianceFilterOptions/fetchVarianceFilterOptions) - already
    // cross-filtered by every other active filter, including a UMR selection, and already sorted
    // (service.available_account_name_options).
    const accountNameOptions = computed(() => varianceFilterOptions.value.account_name)

    // Same client-side, selection-independent narrowing as varianceTypeSearch above - matched against
    // the displayed (Demo Mode-masked) name, same as filteredUmrOptions.
    const accountNameSearch = ref('')
    const filteredAccountNameOptions = computed(() => {
      const query = accountNameSearch.value.trim().toLowerCase()

      return query
        ? accountNameOptions.value.filter((name) => maskAccountName(name).toLowerCase().includes(query))
        : accountNameOptions.value
    })

    // null = no restriction ("all"), matching the old single-select's default
    // — kept as the sentinel rather than eagerly listing every option, since
    // accountNameOptions is derived from loaded data and may still be empty
    // when this ref is first created. Renders every checkbox unchecked (not
    // pre-selected) - only an explicit array checks its own entries. A Set, not
    // accountNameFilter.includes: that scanned the whole selection once per checkbox (LIQ-2403).
    const selectedAccountNameSet = computed(
      () => new Set(Array.isArray(accountNameFilter.value) ? accountNameFilter.value : [])
    )
    const isAccountNameSelected = (name) => selectedAccountNameSet.value.has(name)

    const allAccountNamesSelected = computed(
      () =>
        Array.isArray(accountNameFilter.value) &&
        accountNameFilter.value.length === accountNameOptions.value.length
    )

    const toggleAllAccountNames = () => {
      accountNameFilter.value = allAccountNamesSelected.value ? [] : [...accountNameOptions.value]
    }

    const accountNameDropdownLabel = computed(() => {
      if (!accountNameFilter.value || !accountNameFilter.value.length) return 'All'

      return `${accountNameFilter.value.length} selected`
    })

    // Starting from null (nothing explicitly picked yet), checking a single
    // account name isolates to just that one name in one click, instead of
    // expanding to "every name except this one" the way toggling within an
    // explicit selection does.
    const toggleAccountName = (name) => {
      const currentlySelected = null === accountNameFilter.value ? [] : accountNameFilter.value

      accountNameFilter.value = currentlySelected.includes(name)
        ? currentlySelected.filter((n) => n !== name)
        : [...currentlySelected, name]
    }

    // Same "select all currently displayed/filtered options" Enter handler as
    // selectAllFilteredVarianceTypes above, for Account Name's own checklist (shared by all four
    // of its own instances - toolbar, Variance/BDX/Cashbook table headers - since they all read/
    // write this same accountNameFilter/accountNameSearch pair).
    const selectAllFilteredAccountNames = () => {
      if (!filteredAccountNameOptions.value.length) return
      const currentlySelected = Array.isArray(accountNameFilter.value) ? accountNameFilter.value : []
      accountNameFilter.value = Array.from(
        new Set([...currentlySelected, ...filteredAccountNameOptions.value])
      )
    }

    // Drives the Account Name column's own search icon "active" styling (see
    // #head(account_name) below) - same purpose as isVarianceTypeFilterActive, just for
    // accountNameFilter. A second, independent access point onto the exact same accountNameFilter
    // the toolbar's own Matched Account Name dropdown already drives - both stay in sync
    // automatically since they share this one ref.
    const isAccountNameFilterActive = computed(
      () => Array.isArray(accountNameFilter.value) && accountNameFilter.value.length > 0
    )

    // Server-computed now (see varianceFilterOptions/fetchVarianceFilterOptions) - already
    // cross-filtered by every other active filter, including a UMR or Account Name selection, and
    // already sorted (service.available_claim_options).
    const claimOptions = computed(() => varianceFilterOptions.value.claim)

    // Same client-side, selection-independent narrowing as varianceTypeSearch above - matched against
    // the displayed (Demo Mode-masked) claim, same as filteredUmrOptions.
    const claimSearch = ref('')
    const filteredClaimOptions = computed(() => {
      const query = claimSearch.value.trim().toLowerCase()

      return query
        ? claimOptions.value.filter((claim) => maskClaim(claim).toLowerCase().includes(query))
        : claimOptions.value
    })

    // LIQ-2403: drawing every match froze the page - "select all report periods" returns ~19K
    // claims, one checkbox each. Only the first CLAIM_OPTIONS_DISPLAY_LIMIT matches are drawn;
    // "Select all" and the Enter shortcut still act on every match (claimOptions/filteredClaimOptions).
    const CLAIM_OPTIONS_DISPLAY_LIMIT = 300
    const displayedClaimOptions = computed(() => filteredClaimOptions.value.slice(0, CLAIM_OPTIONS_DISPLAY_LIMIT))
    const hiddenClaimCount = computed(() => Math.max(0, filteredClaimOptions.value.length - CLAIM_OPTIONS_DISPLAY_LIMIT))

    // null renders every checkbox unchecked (nothing explicitly picked yet), not
    // "all checked" - only an explicit array checks its own entries. A Set, not
    // claimFilter.includes: that scanned the whole selection once per checkbox.
    const selectedClaimSet = computed(() => new Set(Array.isArray(claimFilter.value) ? claimFilter.value : []))
    const isClaimSelected = (claim) => selectedClaimSet.value.has(claim)

    const allClaimsSelected = computed(
      () => Array.isArray(claimFilter.value) && claimFilter.value.length === claimOptions.value.length
    )

    const toggleAllClaims = () => {
      claimFilter.value = allClaimsSelected.value ? [] : [...claimOptions.value]
    }

    const claimDropdownLabel = computed(() => {
      if (!claimFilter.value || !claimFilter.value.length) return 'All'

      return `${claimFilter.value.length} selected`
    })

    // Starting from null (nothing explicitly picked yet), checking a single claim
    // reference isolates to just that one in one click, instead of expanding to
    // "every claim reference except this one" the way toggling within an explicit
    // selection does.
    const toggleClaim = (claim) => {
      const currentlySelected = null === claimFilter.value ? [] : claimFilter.value

      claimFilter.value = currentlySelected.includes(claim)
        ? currentlySelected.filter((c) => c !== claim)
        : [...currentlySelected, claim]
    }

    // Same "select all currently displayed/filtered options" Enter handler as
    // selectAllFilteredVarianceTypes above, for Claim Reference's own checklist.
    const selectAllFilteredClaims = () => {
      if (!filteredClaimOptions.value.length) return
      const currentlySelected = Array.isArray(claimFilter.value) ? claimFilter.value : []
      claimFilter.value = Array.from(new Set([...currentlySelected, ...filteredClaimOptions.value]))
    }

    // Drives the Claim Reference column's own search icon "active" styling (see #head(claim)
    // below) - same purpose as isAccountNameFilterActive, just for claimFilter.
    const isClaimFilterActive = computed(() => Array.isArray(claimFilter.value) && claimFilter.value.length > 0)

    // Fixed display order (rather than however the API's dict keys happen to iterate) - per
    // explicit decision from a live demo call (docs/rec-report-rls-replication.md decision 9),
    // this is the business's own standard ordering for these four party types, not alphabetical.
    const DCA_PARTY_TYPE_ORDER = ['Underwriter', 'Broker', 'TPA', 'Coverholder']

    // The "Underwriter" section is relabeled "Lead Underwriter" for display only - per decision 9,
    // it only ever contains lead underwriters now (never follow), so the label says so explicitly
    // rather than leaving that implicit. The underlying Party Type stays "Underwriter" everywhere
    // else (dcaFilter's composite keys, dca_selections, VALID_PARTY_TYPES) - this is a display-only
    // rename, not a new Party Type value threaded through the wire format.
    const dcaSectionLabel = (partyType) => ('Underwriter' === partyType ? 'Lead Underwriter' : partyType)

    // { party_type: [group names] } from GET /party-groups - unscoped (every Party Type/Group Name
    // pair in the system, not just this caller's own), fetched once on mount (§ onMounted below).
    // Not report data, so it isn't behind loading/error the way the report's own paginated grids
    // are - a failure here just leaves the dropdown empty rather than blocking the rest of the page.
    const partyGroupOptions = ref({})

    // lead_fcp_keys is a new field on GET /my-role's response (Underwriter callers only, empty
    // for every other role) - the exact fcp_keys this caller LEADS, not follows. Replaces the old
    // account-wide isLeadUnderwriter gate, which checked whether the caller's role_value showed up
    // ANYWHERE in partyGroupOptions' Underwriter section (meant to be lead-only per decision 9) and
    // then, if so, unlocked editing for every row in scope - including rows led by a DIFFERENT
    // underwriter the caller merely follows. Reported bug: an Atrium user who leads elsewhere could
    // still edit/approve Brit-led rows, since Brit was one of Atrium's own follow relationships, not
    // a genuinely separate check. See rowPassesLeadGate/isRowLeadUnderwriter below for the per-row
    // replacement.
    const leadFcpKeys = computed(() => new Set((roleScope.value && roleScope.value.lead_fcp_keys) || []))

    // Variance-details rows have no fcp_key field of their own - verified directly against a live
    // response (Network tab): row.umr + "_" + row.account_name reconstructs the exact string
    // lead_fcp_keys entries use (e.g. lead_fcp_keys has
    // "B0572NA22SG69_ATR_609_2023_1_572_VANGUARD_B0572NA23SG69_...", which is exactly this same
    // row's umr "B0572NA22SG69" and account_name "ATR_609_2023_1_572_VANGUARD_B0572NA23SG69_..."
    // concatenated) - not a documented contract, just this row shape's own composite identity.
    const isRowLeadUnderwriter = (row) => leadFcpKeys.value.has(`${row.umr}_${row.account_name}`)

    // Layered onto canEditVariance/canEditAdminReview by isRowCheckable/isRowEditableBySubmitter/
    // isRowEditableByAdmin below - true for anyone except an Admin checking a row they don't
    // personally lead (Superuser/User were never gated by lead status at all, only Admin is - see
    // canEditVariance's own comment).
    const rowPassesLeadGate = (row) => 'Admin' !== fcpRole.value || isRowLeadUnderwriter(row)

    // Selection is a composite "PartyType::GroupName" key, not a bare group name - some group
    // names legitimately repeat across Party Types (e.g. "Brit Property Combined" is both a TPA
    // and a Coverholder, "Tysers" is both a Broker and a Coverholder), so the name alone can't
    // disambiguate which one was picked. null = nothing explicitly picked yet (renders every
    // checkbox unchecked), matching umrFilter/accountNameFilter/varianceTypeFilter above.
    const dcaFilter = ref(parseArrayQueryParam(initialQuery.dca))
    const dcaSearch = ref('')

    // Empties every checklist dropdown's search box - run whenever one of those dropdowns closes, so
    // the next one always opens blank. Needed for all of them at once, not just the one closing: the
    // same search text backs several dropdowns (umrSearch alone is shared by the toolbar UMR filter,
    // three table headers and the REC UMRs panel), so text typed in one used to reappear in the next.
    const resetChecklistSearches = () => {
      dcaSearch.value = ''
      umrSearch.value = ''
      claimSearch.value = ''
      accountNameSearch.value = ''
      varianceTypeSearch.value = ''
    }

    // Flyout state for the DCA dropdown's per-Party-Type sections - clicking a Party
    // Type row (TPA/Broker/Underwriter/Coverholder) pops its Group Names out in a
    // side panel next to that row, rather than expanding inline underneath it (the
    // old accordion behaviour). Only one open at a time - a second row's flyout
    // wouldn't compose visually with the first still open elsewhere in the list -
    // so this is a single value (partyType/top/left) rather than an array like
    // expandedYears above.
    const dcaFlyout = ref(null)

    // The DCA dropdown menu is Popper-positioned (positionFixed/strategy: 'fixed'
    // below) with GPU-accelerated transforms, which makes it a containing block
    // for any position: fixed descendant - so a flyout nested inside it would be
    // clipped to the menu's own bounds instead of escaping to the real viewport
    // (the .fcp-row-tooltip trick works because .table-responsive has no
    // transform; this menu does). Moved to <body> on mount instead, same fix
    // BDXPreviewPopover.vue already uses for the same class of problem - see the
    // onMounted/onBeforeUnmount below.
    const dcaFlyoutRef = ref(null)

    // Report Period (Column A) has no natural height of its own to match Variance Transaction
    // Details (Column B) with - the two sit in separate Bootstrap columns behind completely
    // different preceding siblings (pill/divider-bars/dropdowns vs toolbar-row/REC-BDX-Cashbook
    // row), so nothing about their CSS box models ties them together already. Measuring Variance
    // Transaction Details' actual rendered height via ResizeObserver and feeding it back as
    // Report Period's own max-height (see its template binding) is what makes them visually align,
    // per explicit request - kept as a max-height (not a hard height) so .select-report-period's
    // existing flex: 1/min-height: 0 can still shrink it further if Column A's own available space
    // is ever smaller than this, rather than overflowing the column.
    const variancePanelRef = ref(null)
    const variancePanelHeight = ref(null)
    let variancePanelResizeObserver = null

    // LIQ-2571: while the Account Name Reconciliation panel is shown in Variance Transaction
    // Details' place, VTD is hidden and measures 0, so Report Period follows the panel instead -
    // measured the same way (content box of the same .fcp-panel box, in the same slot), so Column A
    // keeps exactly the height it had. Until the panel's first measurement arrives, VTD's last
    // visible height stands in (the variance observer ignores the 0 it reports while hidden), so
    // Column A doesn't jump on open.
    const accountNamePanelRef = ref(null)
    const accountNamePanelHeight = ref(0)
    let accountNamePanelResizeObserver = null

    watch(isAccountNamePanelShown, async (shown) => {
      if (accountNamePanelResizeObserver) {
        accountNamePanelResizeObserver.disconnect()
        accountNamePanelResizeObserver = null
      }
      accountNamePanelHeight.value = 0
      if (!shown) return
      await nextTick()
      const el = accountNamePanelRef.value && accountNamePanelRef.value.$el
      if (!el || !('ResizeObserver' in window)) return
      accountNamePanelResizeObserver = new ResizeObserver((entries) => {
        accountNamePanelHeight.value = entries[0].contentRect.height
      })
      accountNamePanelResizeObserver.observe(el)
    })

    const reportPeriodMaxHeight = computed(
      () => (isAccountNamePanelShown.value && accountNamePanelHeight.value) || variancePanelHeight.value || null
    )

    const DCA_FLYOUT_GAP = 4
    // Keeps the flyout fully on-screen regardless of viewport size - the
    // minimum breathing room kept between the flyout's own edge and the
    // viewport's, once clamped against an edge.
    const DCA_FLYOUT_VIEWPORT_MARGIN = 8

    const isPartyTypeExpanded = (partyType) =>
      !!dcaFlyout.value && dcaFlyout.value.partyType === partyType

    // Two-pass: an initial guess (to the row's right, top-aligned) is set
    // first so the flyout actually renders with this Party Type's real
    // content - only then can its true size be measured, since height varies
    // with how many Group Names it lists (12 for Broker vs. 93 for
    // Coverholder, say) and isn't knowable up front. That measured size is
    // what the second pass clamps against the viewport with, rather than
    // guessing at a width/height that has to be kept in sync with the CSS by
    // hand - this is what makes the position genuinely dynamic rather than a
    // fixed nudge that only happens to work for some rows/screen sizes.
    const togglePartyTypeFlyout = async (partyType, event) => {
      if (isPartyTypeExpanded(partyType)) {
        dcaFlyout.value = null
        return
      }

      // currentTarget, not target - the row can be clicked anywhere across its
      // chevron/checkbox/label, but positioning is always relative to the row
      // itself, not whichever child happened to receive the click.
      const rect = event.currentTarget.getBoundingClientRect()

      dcaFlyout.value = { partyType, top: rect.top, left: rect.right + DCA_FLYOUT_GAP }

      await nextTick()

      // Bail if the flyout closed, or moved on to a different Party Type,
      // while waiting for the DOM to update above (e.g. rapid clicks) - this
      // stale response shouldn't clobber whatever's current now.
      if (!dcaFlyoutRef.value || !dcaFlyout.value || dcaFlyout.value.partyType !== partyType) return

      const flyoutRect = dcaFlyoutRef.value.getBoundingClientRect()
      const margin = DCA_FLYOUT_VIEWPORT_MARGIN

      let left = rect.right + DCA_FLYOUT_GAP
      if (left + flyoutRect.width + margin > window.innerWidth) {
        left = rect.left - flyoutRect.width - DCA_FLYOUT_GAP
      }
      left = Math.min(Math.max(margin, left), window.innerWidth - flyoutRect.width - margin)

      // window.innerHeight alone isn't the real usable bottom boundary - the app shell's fixed
      // footer (AppFooter.vue, #main-footer) sits below the report and can still be within the
      // viewport, so clamping against window.innerHeight let a tall flyout (e.g. Coverholder's
      // 118 options) render UNDERNEATH/over the footer instead of stopping above it. Clamps
      // against the footer's own top edge instead, when it's actually present on screen.
      const footerEl = document.getElementById('main-footer')
      const viewportBottom = footerEl
        ? Math.min(window.innerHeight, footerEl.getBoundingClientRect().top)
        : window.innerHeight

      let top = rect.top
      if (top + flyoutRect.height + margin > viewportBottom) {
        top = viewportBottom - flyoutRect.height - margin
      }
      top = Math.max(margin, top)

      dcaFlyout.value = { partyType, top, left }
    }

    const closeDcaFlyout = () => {
      dcaFlyout.value = null
    }

    const dcaOptionKeys = computed(() =>
      DCA_PARTY_TYPE_ORDER.flatMap((partyType) =>
        (partyGroupOptions.value[partyType] || []).map((name) => `${partyType}::${name}`)
      )
    )

    // Same client-side, selection-independent narrowing as varianceTypeSearch/umrSearch/
    // accountNameSearch above - grouped by Party Type so the dropdown reads like the Report
    // Period tree (a header per group, options underneath), not one long flat list.
    const filteredDcaPartyGroups = computed(() => {
      const query = dcaSearch.value.trim().toLowerCase()

      return DCA_PARTY_TYPE_ORDER.map((partyType) => ({
        partyType,
        names: (partyGroupOptions.value[partyType] || []).filter(
          (name) => !query || name.toLowerCase().includes(query)
        )
      })).filter((group) => group.names.length)
    })

    // The single portaled flyout element (see dcaFlyoutRef) renders whichever
    // Party Type is currently open, looked up here rather than threaded through
    // as a prop - it lives outside the v-for that owns `group` once portaled.
    const dcaFlyoutGroup = computed(() => {
      if (!dcaFlyout.value) return null

      return (
        filteredDcaPartyGroups.value.find(
          (group) => group.partyType === dcaFlyout.value.partyType
        ) || null
      )
    })

    const isDcaGroupSelected = (partyType, name) =>
      Array.isArray(dcaFilter.value) && dcaFilter.value.includes(`${partyType}::${name}`)

    // Section-level "select all" checkbox next to each Party Type's accordion header - mirrors
    // isYearSelected/toggleYear for the Report Period tree. Operates on `group.names` as passed
    // in (the search-filtered list from filteredDcaPartyGroups), so checking it only
    // selects/deselects whatever's currently visible under an active search, same as the
    // Report Period tree's own toggleYear operating on whatever months that year actually has.
    const isPartyTypeSelected = (group) =>
      0 < group.names.length && group.names.every((name) => isDcaGroupSelected(group.partyType, name))

    // Drives the Party Type row's own checkbox .indeterminate (see the template - :indeterminate
    // isn't a real HTML attribute, only a live DOM property, so it needs the .prop modifier
    // there rather than a plain :indeterminate binding) - true when SOME but not all of this
    // group's names are selected, e.g. checking a single name in the Lead Underwriter flyout
    // shows a dash/dot on "Lead Underwriter" itself instead of leaving it looking untouched.
    // Per explicit decision, clicking the checkbox in this state still selects ALL of the
    // group's names (see togglePartyType below, unchanged) rather than clearing the one already
    // picked - same "isn't fully selected yet, so select everything" behavior an unchecked
    // parent already has.
    const isPartyTypePartiallySelected = (group) => {
      const selectedCount = group.names.filter((name) => isDcaGroupSelected(group.partyType, name)).length

      return 0 < selectedCount && selectedCount < group.names.length
    }

    const togglePartyType = (group) => {
      const keys = group.names.map((name) => `${group.partyType}::${name}`)
      const currentlySelected = null === dcaFilter.value ? [] : dcaFilter.value
      const allSelected = keys.every((key) => currentlySelected.includes(key))

      dcaFilter.value = allSelected
        ? currentlySelected.filter((key) => !keys.includes(key))
        : Array.from(new Set([...currentlySelected, ...keys]))
    }

    const allDcaGroupsSelected = computed(
      () => Array.isArray(dcaFilter.value) && dcaFilter.value.length === dcaOptionKeys.value.length
    )

    const toggleAllDcaGroups = () => {
      dcaFilter.value = allDcaGroupsSelected.value ? [] : [...dcaOptionKeys.value]
    }

    const dcaDropdownLabel = computed(() => {
      if (!dcaFilter.value || !dcaFilter.value.length) return 'All'

      return `${dcaFilter.value.length} selected`
    })

    // Starting from null (nothing explicitly picked yet), checking a single group isolates to
    // just that one in one click, instead of expanding to "every group except this one" the way
    // toggling within an explicit selection does.
    const toggleDcaGroup = (partyType, name) => {
      const key = `${partyType}::${name}`
      const currentlySelected = null === dcaFilter.value ? [] : dcaFilter.value

      dcaFilter.value = currentlySelected.includes(key)
        ? currentlySelected.filter((k) => k !== key)
        : [...currentlySelected, key]
    }

    // Same "select all currently displayed/filtered options" Enter handler as
    // selectAllFilteredVarianceTypes above, for the Party/DCA dropdown's own search box - flattens
    // every group's own filtered names (filteredDcaPartyGroups, already narrowed by dcaSearch)
    // into their composite "PartyType::Name" keys, same shape toggleDcaGroup/togglePartyType use.
    const selectAllFilteredDcaGroups = () => {
      const keys = filteredDcaPartyGroups.value.flatMap((group) =>
        group.names.map((name) => `${group.partyType}::${name}`)
      )
      if (!keys.length) return
      const currentlySelected = Array.isArray(dcaFilter.value) ? dcaFilter.value : []
      dcaFilter.value = Array.from(new Set([...currentlySelected, ...keys]))
    }

    const fetchPartyGroupOptions = async () => {
      // Scoped server-side from the caller's own JWT (see getFCPReportV2PartyGroups's own
      // comment) - doesn't need roleScope resolved client-side, so this fires immediately on
      // mount rather than waiting on GET /my-role first (see onMounted).
      //
      // dca_selections sends the DCA dropdown's OWN currently-checked selections so each party
      // type's own option list is cross-filtered by every OTHER currently-selected party type
      // (see getFCPReportV2PartyGroups's own comment) - refetched (debounced, see
      // fetchPartyGroupOptionsDebounced below) whenever dcaFilter changes, not just once on mount.
      //
      // report_periods (LIQ-2405): each list only offers parties with records in the selected
      // periods, so this also refetches on every period change (see the selectedPeriods watcher).
      // A party already ticked stays in its list even with no records there - the backend keeps
      // it - so the pruning below still only drops a pick that no longer co-occurs with the other
      // party picks, never one that merely has no records in the new periods.
      // umr (LIQ-2401): same treatment - the UMR checkbox-list filter also narrows the Party
      // dropdown now, refetched whenever umrFilter changes (see the watch() below).
      if (partyGroupsCancelSource) partyGroupsCancelSource.cancel()
      partyGroupsCancelSource = axios.CancelToken.source()
      const cancelToken = partyGroupsCancelSource.token

      try {
        const response = await ReconciliationService.getFCPReportV2PartyGroups({
          dca_selections: dcaFilter.value && dcaFilter.value.length ? dcaFilter.value : undefined,
          report_periods: selectedPeriods.value.length ? selectedPeriods.value : undefined,
          umr: umrFilter.value && umrFilter.value.length ? umrFilter.value : undefined,
          cancelToken,
        })
        partyGroupOptions.value = (response && response.data && response.data.party_group_options) || {}

        // Prune now-invalid selections: a selection that was valid before this narrower response
        // (e.g. a Broker checked before a since-selected TPA turned out not to co-occur with it)
        // no longer has a matching entry here, and nothing else reconciles dcaFilter against
        // partyGroupOptions - left unpruned, it would keep silently narrowing the report by a
        // value the user can no longer see or uncheck (matches Power BI's own slicer behavior: a
        // selection outside the current cross-filtered domain is dropped, not silently kept).
        // Only reassigns when something actually changed, so this can't re-trigger the dcaFilter
        // watcher below forever - once nothing more needs pruning, re-running this same filter
        // over an already-consistent dcaFilter is a no-op.
        if (Array.isArray(dcaFilter.value) && dcaFilter.value.length) {
          const validKeys = new Set(
            Object.entries(partyGroupOptions.value).flatMap(([partyType, names]) =>
              (names || []).map((name) => `${partyType}::${name}`)
            )
          )
          const pruned = dcaFilter.value.filter((key) => validKeys.has(key))
          if (pruned.length !== dcaFilter.value.length) dcaFilter.value = pruned
        }
      } catch (err) {
        if (axios.isCancel(err)) return

        // Non-fatal: the DCA dropdown just stays empty ("No matches") rather than blocking the
        // rest of the report, which doesn't depend on this data at all.
        partyGroupOptions.value = {}
      }
    }

    const fetchPartyGroupOptionsDebounced = debounce(fetchPartyGroupOptions, 400)

    // Unlike report_periods (client-side-only until a fetch is triggered), dcaFilter now drives a
    // real server-side narrowing (see fetchReport's dca_selections param and its own "explicitly
    // empty means show nothing" guard above) - so a change here needs the same debounced refetch
    // selectedPeriods gets. It also now cross-filters the DCA dropdown's own options among
    // themselves (see fetchPartyGroupOptions' own comment) - refetch those too on the same
    // trigger. dcaFilter is always reassigned wholesale (never mutated in place - see
    // toggleDcaGroup/togglePartyType/toggleAllDcaGroups above, and fetchPartyGroupOptions' own
    // pruning step), so a shallow watch is sufficient, same reasoning as the selectedPeriods
    // watcher.
    watch(dcaFilter, () => {
      fetchReportDebounced()
      fetchPartyGroupOptionsDebounced()
    })

    // LIQ-2401: umrFilter now also narrows the Party dropdown (see fetchPartyGroupOptions' own
    // comment) - refetch it the same way dcaFilter's own watcher above already does. Doesn't
    // touch fetchReportDebounced here - umrFilter already drives the report's own row-scope via
    // a separate path (see effectiveUmrAndClaimForVarianceDetails/the umrFilter watcher this one
    // sits alongside), this only adds the Party dropdown refetch that was missing for it.
    watch(umrFilter, () => {
      fetchPartyGroupOptionsDebounced()
    })

    // Every other filter/sort dimension the Variance Transaction Details table's server-side
    // fetch depends on (selectedPeriods/dcaFilter are covered above, via fetchReport itself - see
    // its own comment) - a change to any of these resets pagination back to the first page and
    // refetches, same as changing a filter in any paginated UI would. selectedUmrRow and
    // varianceNarrowingFromTransaction are the two REC UMRs panel/cross-table drill-down sources;
    // see effectiveUmrAndClaimForVarianceDetails for how they combine with umrFilter/claimFilter.
    watch(
      [
        umrFilter,
        accountNameFilter,
        claimFilter,
        varianceTypeFilter,
        approvalStatusFilter,
        varianceFilter,
        sourceFilter,
        fcpFilter,
        selectedUmrRow,
        varianceNarrowingFromTransaction,
        sortBy,
        sortDesc,
        // Flips group_by_umr (see varianceDetailsFilterParams) - included explicitly rather than
        // relying on sortBy/sortDesc's own changes to carry it: a reset that happens to restore
        // sortBy/sortDesc to values they already held (e.g. resetting right after clicking the
        // Variance column's own caret, already at the default variance_amount/desc) would
        // otherwise flip this ref with no other change in the watch list to trigger a refetch.
        varianceSortTouched,
        // Total Paid (BDX)/(REC)/Variance's own min/max now filter server-side (see
        // varianceDetailsFilterParams below) - watching the whole columnSearch object (not just
        // those three keys) also refetches on the OTHER, still-client-side-only columns' own text/
        // date-range edits, same harmless-but-slightly-wasteful tradeoff BDX/Cashbook's own
        // bdxColumnSearch/cashbookColumnSearch watchers already accept.
        columnSearch
      ],
      () => {
        fetchVarianceDetailsDebounced()
      }
    )

    // Narrower than the watcher above - fetchVarianceFilterOptions only needs to re-run when a
    // dimension /variance-details/filter-options' own cross-filtering actually reads changes
    // (report_periods/umr/variance_flag/source/review_status/variance_type/account_name/claim/
    // fcp_status/dca_selections - see varianceDetailsFilterParams). sortBy/sortDesc/
    // varianceSortTouched/columnSearch (the four extra entries the watcher above includes) never
    // change which options are available, so re-fetching on those would just be a wasted network
    // call returning the exact same lists. selectedUmrRow/varianceNarrowingFromTransaction ARE
    // included - both feed into effectiveUmrAndClaimForVarianceDetails' own umr/claim, which DOES
    // change what these lists cross-filter against.
    watch(
      [
        umrFilter,
        accountNameFilter,
        claimFilter,
        varianceTypeFilter,
        approvalStatusFilter,
        varianceFilter,
        sourceFilter,
        fcpFilter,
        selectedUmrRow,
        varianceNarrowingFromTransaction
      ],
      () => {
        fetchVarianceFilterOptionsDebounced()
      }
    )

    // fetchRecUmrRollup's own trigger set - every toolbar filter fcpUmrList has always respected,
    // MINUS selectedUmrRow/varianceNarrowingFromTransaction (deliberately excluded, unlike the
    // watcher above - see recUmrRollupFilterParams' own comment for why this panel has never
    // wanted the cross-table drill-down narrowing those two feed into for other tables).
    watch(
      [
        umrFilter,
        accountNameFilter,
        claimFilter,
        varianceTypeFilter,
        approvalStatusFilter,
        varianceFilter,
        sourceFilter,
        fcpFilter
      ],
      () => {
        fetchRecUmrRollupDebounced()
      }
    )

    // BDX Detail by Transaction's own server-side fetch depends on a narrower set of exact-match
    // dimensions than Variance's above (only umr/account_name/claim exist at this grain - see
    // BdxCashbookDetailsRequestBase's own docstring), PLUS its own sort/search state
    // (bdxSortBy/bdxSortDesc/bdxColumnSearch - real server-side sort/search, unlike Variance's own
    // sortBy/sortDesc/columnSearch, where only sortBy/sortDesc trigger a refetch and columnSearch
    // stays purely client-side, see HEADER_SEARCH_SORT_FIELD_KEYS' own comment).
    watch(
      [
        umrFilter,
        accountNameFilter,
        claimFilter,
        selectedUmrRow,
        bdxNarrowingFromTransaction,
        bdxSortBy,
        bdxSortDesc,
        bdxColumnSearch
      ],
      () => {
        fetchBdxDetailsDebounced()
      }
    )

    // Cashbook Details by Transaction counterpart to the BDX watcher above - same reasoning.
    watch(
      [
        umrFilter,
        accountNameFilter,
        claimFilter,
        selectedUmrRow,
        cashbookNarrowingFromTransaction,
        cashbookSortBy,
        cashbookSortDesc,
        cashbookColumnSearch
      ],
      () => {
        fetchCashbookDetailsDebounced()
      }
    )

    // fcpAccountCount ("FCP Accounts" KPI tile) and fcpUmrList (REC UMRs panel's per-UMR/period
    // status rollup) both used to be computed here client-side, from a full, unpaginated per-claim
    // fetch (rawRows, since removed) - the reported "select all report periods" freeze. Both are
    // now fetched together from POST /rec-umrs (see fetchRecUmrRollup below), which computes the
    // exact same numbers server-side, over the exact same filter scope, on an already-filtered
    // dataframe it builds once - see service.rec_umr_rollup's own docstring for the full history,
    // including why the "worst status wins" merge logic moved there unchanged.
    const fcpAccountCount = ref(0)
    const fcpUmrList = ref([])
    // LIQ-2403 acceptance criterion ("loading indicator reflects actual processing status"): /rec-umrs
    // can take seconds at "select all report periods" scale, and without this the KPI tile and the
    // REC UMRs panel kept showing the PREVIOUS selection's numbers the whole time, as if current.
    const recUmrLoading = ref(false)
    // Demo Mode's masked view of fcpAccountCount. Rounded because maskAmount scales by 0.5x-1.5x
    // and this tile shows a bare integer. null (failed fetch) passes through so the tile still shows '—'.
    const displayedFcpAccountCount = computed(() =>
      null === fcpAccountCount.value || !demoMode.value
        ? fcpAccountCount.value
        : Math.round(maskAmount(fcpAccountCount.value))
    )

    // REC UMRs panel's own search/sort - per explicit request, reusing the exact same shared
    // filter state as Variance Transaction Details' own Report Period/Status?/UMR columns
    // (selectedPeriods, approvalStatusFilter, umrFilter respectively) rather than new, separate
    // filters - this panel's own fcpUmrList above is already filtered by all three via
    // preUmrFilterRows/matchesBaseFilters, so no new filtering logic is needed here at all, only a
    // second UI access point onto each. Client-side sort only (this is a plain computed array, not
    // a paginated one like the three b-table panels, so there's no "Load More" ordering concern to
    // worry about the way there would be there).
    const umrPanelSortBy = ref('report_month_year')
    const umrPanelSortDesc = ref(false)

    const toggleUmrPanelSort = (key) => {
      if (umrPanelSortBy.value === key) {
        umrPanelSortDesc.value = !umrPanelSortDesc.value
      } else {
        umrPanelSortBy.value = key
        umrPanelSortDesc.value = false
      }
    }

    // report_month_year is "MonthName Year" (e.g. "June 2026") - not chronologically sortable as
    // a plain string (e.g. "August" would sort before "June" alphabetically) - MONTH_NAMES' own
    // index plus year gives a real, comparable chronological value instead.
    const reportPeriodSortValue = (reportMonthYear) => {
      const [monthName, year] = (reportMonthYear || '').split(' ')
      return Number(year || 0) * 100 + MONTH_NAMES.indexOf(monthName)
    }

    const displayedFcpUmrList = computed(() => {
      const key = umrPanelSortBy.value
      const direction = umrPanelSortDesc.value ? -1 : 1

      return [...fcpUmrList.value].sort((a, b) => {
        const aValue = 'report_month_year' === key ? reportPeriodSortValue(a[key]) : a[key] || ''
        const bValue = 'report_month_year' === key ? reportPeriodSortValue(b[key]) : b[key] || ''

        if (aValue < bValue) return -1 * direction
        if (aValue > bValue) return direction
        return 0
      })
    })

    // LIQ-2403: drawing every row at once (~3,000 at "select all report periods", inside a ~200px
    // scroll box) is the same unbounded-render pattern that froze the page on the Claim filter.
    // Rows are drawn REC_UMR_RENDER_BATCH at a time, more as the list is scrolled near its end;
    // displayedFcpUmrList stays the full sorted list (the Excel export reads it).
    const REC_UMR_RENDER_BATCH = 300
    const recUmrRenderLimit = ref(REC_UMR_RENDER_BATCH)
    const renderedFcpUmrList = computed(() => displayedFcpUmrList.value.slice(0, recUmrRenderLimit.value))
    // New data or a new sort starts again from the first batch.
    watch(displayedFcpUmrList, () => {
      recUmrRenderLimit.value = REC_UMR_RENDER_BATCH
    })
    const onRecUmrListScroll = (event) => {
      if (recUmrRenderLimit.value >= displayedFcpUmrList.value.length) return
      const list = event.target
      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 50) {
        recUmrRenderLimit.value += REC_UMR_RENDER_BATCH
      }
    }

    // Own independent expand/collapse state for this panel's own Report Period dropdown - NOT
    // shared with Column A's own expandedYears or Variance Transaction Details' own
    // periodDropdownExpandedYears, same reasoning as that one's own comment (opening/closing a
    // year in one tree must not visibly shift a completely different one).
    const umrPanelPeriodDropdownExpandedYears = ref([])

    const isUmrPanelPeriodDropdownYearExpanded = (year) =>
      umrPanelPeriodDropdownExpandedYears.value.includes(year)

    const toggleUmrPanelPeriodDropdownYearExpanded = (year) => {
      umrPanelPeriodDropdownExpandedYears.value = isUmrPanelPeriodDropdownYearExpanded(year)
        ? umrPanelPeriodDropdownExpandedYears.value.filter((y) => y !== year)
        : [...umrPanelPeriodDropdownExpandedYears.value, year]
    }

    // PAGINATION (per explicit decision - see docs/rec-report-variance-details-endpoint.md):
    // this table used to be derived entirely client-side from filteredRows (itself built from
    // /production's full, unpaginated variance_rows array) - once fcp_status stopped being
    // filtered out at the DB base (docs/rec-report-rls-replication.md), that array roughly
    // doubled (45K -> 96K rows) and shipping/rendering all of it on every load became the
    // reported slowness. Now sourced from GET /variance-details instead: every filter that used
    // to narrow filteredRows/hasVarianceOrIsApproved/matchesSelectedTransaction client-side is
    // sent as a real query param (service.apply_variance_details_filters +
    // apply_materiality_floor mirror hasVarianceOrIsApproved/hasMaterialVariance exactly), and the
    // server does the sorting (service.sort_variance_df's own compound UMR-then-secondary sort,
    // ported from this file's old client-side sort below) and pagination.
    //
    // Initial load fetches VARIANCE_INITIAL_PAGE_SIZE rows; "Load more" appends
    // VARIANCE_LOAD_MORE_SIZE more (plain append, not virtual-scrolled - per explicit decision,
    // fine for the common case of a few clicks, not intended to stay smooth if a caller loads a
    // large fraction of a filtered set numbering in the tens of thousands). Column-header search
    // (HEADER_SEARCH_SORT_FIELD_KEYS) deliberately stays client-side-only below, narrowing just
    // the rows already loaded - promoting it to a real server-side search per column was judged
    // out of scope for this pass.
    // HISTORY: briefly lowered to 50 as a diagnostic test while chasing a scroll freeze -
    // confirmed via a real Performance profile that the freeze's actual cause was
    // @row-hovered/mouseenter forcing a full re-render on every row crossed under a stationary
    // cursor while scrolling (see showRowTooltip's own comment), not the row count itself. Fixed
    // there (rowTooltipEnabled = false) and restored to 100 (per explicit decision) now that the
    // real cause no longer applies.
    const VARIANCE_INITIAL_PAGE_SIZE = 100
    const VARIANCE_LOAD_MORE_SIZE = 500

    const varianceDetailRows = ref([])
    const varianceTotalRows = ref(0)
    const varianceServerTotals = ref({ total_paid_bdx: 0, total_paid_fcp: 0, variance_amount: 0 })
    // Total Paid (BDX)/(REC)/Variance's own real, full-dataset range-slider bounds - see
    // varianceDetailsFilterParams/fetchVarianceDetails. Starts as {} (no key present for any
    // column), same reasoning as bdxRangeBounds/cashbookRangeBounds - RecColumnSearchMenu's own
    // hasRealRangeBounds check then correctly treats "haven't fetched yet" as "no real bounds to
    // preview" rather than showing a misleading 0/0.
    const varianceRangeBounds = ref({})
    const varianceLoadingInitial = ref(false)
    const varianceLoadingMore = ref(false)
    let varianceFetchRequestId = 0

    // The old client-side filteredRows chain intersected THREE independent UMR-narrowing sources
    // (the UMR checkbox list, the REC UMRs panel's row click, and the cross-table transaction-row
    // click when it didn't originate in this table) - replicated here so the server-side fetch
    // narrows exactly the same way. An empty intersection (e.g. umrFilter=['X'] while the REC
    // UMRs panel has a different UMR selected) can't be expressed as umr=[] server-side
    // (apply_umr_filter treats an empty list as "no restriction", not "match nothing") - callers
    // check `isEmpty` and skip the fetch entirely in that case instead.
    const effectiveUmrAndClaimForVarianceDetails = () => {
      let umrCandidates = null
      if (umrFilter.value && umrFilter.value.length) umrCandidates = new Set(umrFilter.value)
      if (selectedUmrRow.value) {
        umrCandidates = umrCandidates
          ? umrCandidates.has(selectedUmrRow.value)
            ? new Set([selectedUmrRow.value])
            : new Set()
          : new Set([selectedUmrRow.value])
      }

      let claimCandidates = null
      if (claimFilter.value && claimFilter.value.length) claimCandidates = new Set(claimFilter.value)
      if (selectedTransaction.value && 'variance' !== selectedTransaction.value.sourceTable) {
        const { umr, claim } = selectedTransaction.value
        umrCandidates = umrCandidates
          ? umrCandidates.has(umr)
            ? new Set([umr])
            : new Set()
          : new Set([umr])
        claimCandidates = claimCandidates
          ? claimCandidates.has(claim)
            ? new Set([claim])
            : new Set()
          : new Set([claim])
      }

      return {
        umr: umrCandidates ? Array.from(umrCandidates) : undefined,
        claim: claimCandidates ? Array.from(claimCandidates) : undefined,
        isEmpty: (umrCandidates && 0 === umrCandidates.size) || (claimCandidates && 0 === claimCandidates.size)
      }
    }

    const varianceDetailsFilterParams = () => {
      const { umr, claim } = effectiveUmrAndClaimForVarianceDetails()

      return {
        report_periods: selectedPeriods.value,
        umr,
        claim,
        variance_flag: null === varianceFilter.value ? undefined : varianceFilter.value ? 'Yes' : 'No',
        source:
          'bdx' === sourceFilter.value
            ? 'No CB Entry'
            : 'cashbook' === sourceFilter.value
            ? 'No BDX Entry'
            : 'both' === sourceFilter.value
            ? 'Both'
            : undefined,
        review_status:
          approvalStatusFilter.value && approvalStatusFilter.value.length
            ? approvalStatusFilter.value
            : undefined,
        variance_type:
          varianceTypeFilter.value && varianceTypeFilter.value.length ? varianceTypeFilter.value : undefined,
        account_name:
          accountNameFilter.value && accountNameFilter.value.length ? accountNameFilter.value : undefined,
        fcp_status: null === fcpFilter.value ? undefined : fcpFilter.value ? 'Yes' : 'No',
        dca_selections: dcaFilter.value && dcaFilter.value.length ? dcaFilter.value : undefined,
        sort_by: sortBy.value,
        sort_dir: sortDesc.value ? 'desc' : 'asc',
        // false once the caller has actually clicked a sort caret (see toggleColumnSort/
        // varianceSortTouched above) - true (the default) keeps the table grouped by UMR
        // server-side (service.sort_variance_df), matching the initial/untouched load.
        group_by_umr: !varianceSortTouched.value,
        // Total Paid (BDX)/(REC)/Variance's own real, server-side numeric range filters - added
        // per reported bug: these three used to stay client-side-only (narrowing just whatever
        // page(s) "Load More" had already fetched, same as submit_variance_comment/admin_variance_
        // comment/submission_date/admin_variance_date still do), so both the actual filtering AND
        // the slider's own preview bounds only ever reflected the loaded subset, not the true
        // full-dataset range/matches. columnSearch's own value shape for these keys is
        // { min, max } (see RANGE_FILTER_FIELD_KEYS), same as BDX/Cashbook's own numeric columns.
        total_paid_bdx_min: (columnSearch.value.total_paid_bdx || {}).min || undefined,
        total_paid_bdx_max: (columnSearch.value.total_paid_bdx || {}).max || undefined,
        total_paid_fcp_min: (columnSearch.value.total_paid_fcp || {}).min || undefined,
        total_paid_fcp_max: (columnSearch.value.total_paid_fcp || {}).max || undefined,
        variance_amount_min: (columnSearch.value.variance_amount || {}).min || undefined,
        variance_amount_max: (columnSearch.value.variance_amount || {}).max || undefined
      }
    }

    let varianceFilterOptionsCancelSource = null
    // Only the latest call may switch varianceFilterOptionsLoading off - a superseded call's own
    // finally would otherwise hide the spinner while its replacement is still running.
    let varianceFilterOptionsRequestId = 0

    // Backs varianceFilterOptions above (Account Name/Claim Reference/Source's own cross-filtered
    // dropdown option lists) - reuses varianceDetailsFilterParams() wholesale rather than a second,
    // parallel param-builder: /variance-details/filter-options' own request schema
    // (VarianceFilterOptionsRequest) only reads a subset of those fields (report_periods/umr/
    // variance_flag/source/review_status/variance_type/account_name/claim/fcp_status/
    // dca_selections) and silently ignores the rest (sort_by/page/offset/the six numeric ranges/
    // etc.) - same "extra fields ignored" behavior already relied on throughout this backend. The
    // requestId only guards the loading spinner - a stale response landing after a newer one is
    // already prevented by canceling the prior in-flight call outright, and these are just
    // dropdown option lists, not data whose staleness would corrupt anything if one slipped through.
    const fetchVarianceFilterOptions = async () => {
      const requestId = ++varianceFilterOptionsRequestId
      if (varianceFilterOptionsCancelSource) varianceFilterOptionsCancelSource.cancel()
      varianceFilterOptionsCancelSource = axios.CancelToken.source()
      const cancelToken = varianceFilterOptionsCancelSource.token

      if (!selectedPeriods.value.length) {
        varianceFilterOptions.value = { account_name: [], claim: [], source: [], umr: [] }
        varianceFilterOptionsLoading.value = false
        return
      }

      varianceFilterOptionsLoading.value = true

      try {
        const response = await ReconciliationService.getFCPReportV2VarianceFilterOptions({
          ...varianceDetailsFilterParams(),
          cancelToken
        })
        const data = (response && response.data) || {}

        varianceFilterOptions.value = {
          account_name: data.account_name_options || [],
          claim: data.claim_options || [],
          source: data.source_options || [],
          umr: data.umr_options || []
        }
      } catch (err) {
        if (axios.isCancel(err)) return
        // The dropdowns keep their previous options (clearing them would leave nothing to filter
        // by) - LIQ-2403: said out loud now, since they may no longer match what the grids show.
        showToast(
          requestErrorMessage(
            err,
            'Failed to refresh the filter options - the dropdowns may still show options from your previous selection.'
          )
        )
      } finally {
        if (requestId === varianceFilterOptionsRequestId) varianceFilterOptionsLoading.value = false
      }
    }

    const fetchVarianceFilterOptionsDebounced = debounce(fetchVarianceFilterOptions, 400)

    // Deliberately NOT varianceDetailsFilterParams() - that one resolves umr/claim via
    // effectiveUmrAndClaimForVarianceDetails(), which folds in selectedUmrRow/the cross-table
    // transaction-row click (the REC UMRs panel's own drill-down state). fcpUmrList has never
    // wanted that (see its own CORRECTION comment, history preserved on fetchRecUmrRollup below):
    // clicking a UMR in this very panel narrows the OTHER tables, not itself, or picking a
    // different UMR afterward would be impossible. Plain umrFilter/claimFilter only - every other
    // field matches varianceDetailsFilterParams() exactly, since the panel has always otherwise
    // respected every toolbar filter.
    const recUmrRollupFilterParams = () => ({
      report_periods: selectedPeriods.value,
      umr: umrFilter.value && umrFilter.value.length ? umrFilter.value : undefined,
      claim: claimFilter.value && claimFilter.value.length ? claimFilter.value : undefined,
      variance_flag: null === varianceFilter.value ? undefined : varianceFilter.value ? 'Yes' : 'No',
      source:
        'bdx' === sourceFilter.value
          ? 'No CB Entry'
          : 'cashbook' === sourceFilter.value
          ? 'No BDX Entry'
          : 'both' === sourceFilter.value
          ? 'Both'
          : undefined,
      review_status:
        approvalStatusFilter.value && approvalStatusFilter.value.length
          ? approvalStatusFilter.value
          : undefined,
      variance_type:
        varianceTypeFilter.value && varianceTypeFilter.value.length ? varianceTypeFilter.value : undefined,
      account_name:
        accountNameFilter.value && accountNameFilter.value.length ? accountNameFilter.value : undefined,
      fcp_status: null === fcpFilter.value ? undefined : fcpFilter.value ? 'Yes' : 'No',
      dca_selections: dcaFilter.value && dcaFilter.value.length ? dcaFilter.value : undefined
    })

    let recUmrRollupCancelSource = null
    // Same superseded-request guard as fetchBdxDetails' own bdxFetchRequestId - only the latest
    // call may clear recUmrLoading (or write data), otherwise a cancelled request's own finally
    // would switch the spinner off while its replacement is still in flight.
    let recUmrFetchRequestId = 0

    // Backs fcpAccountCount ("FCP Accounts" KPI tile) and fcpUmrList (REC UMRs panel) - see their
    // own declaration comment for the LIQ-2403 history (used to be a client-side reduction over a
    // full, unpaginated per-claim fetch; now a small server-side rollup instead).
    const fetchRecUmrRollup = async () => {
      const requestId = ++recUmrFetchRequestId
      if (recUmrRollupCancelSource) recUmrRollupCancelSource.cancel()
      recUmrRollupCancelSource = axios.CancelToken.source()
      const cancelToken = recUmrRollupCancelSource.token

      if (!selectedPeriods.value.length) {
        fcpAccountCount.value = 0
        fcpUmrList.value = []
        // Cleared here too, not only in finally below - whichever request this call just
        // superseded never reaches its own reset (its requestId is stale now), so returning early
        // without this would leave the spinner on for good.
        recUmrLoading.value = false
        return
      }

      recUmrLoading.value = true

      try {
        const response = await ReconciliationService.getFCPReportV2RecUmrRollup({
          ...recUmrRollupFilterParams(),
          cancelToken
        })
        if (requestId !== recUmrFetchRequestId) return

        const data = (response && response.data) || {}

        fcpAccountCount.value = data.fcp_account_count || 0
        // Object.freeze each row before Vue ever sees it - same reasoning as fetchVarianceDetails/
        // fetchBdxDetails/fetchCashbookDetails's own identical treatment (see their own comments):
        // these rows are write-once/read-many (rendered, never mutated in place), so there's
        // nothing to gain from Vue's usual per-property reactive get/set rewriting, only the cost
        // of doing it for however many (report_month_year, umr) groups are in scope - "all report
        // periods" alone measures 3,131 groups, the same order of magnitude as the 12,088-row case
        // that traced 90% of its own load time to exactly this (see fetchVarianceDetails' own
        // history note) - this was missed when fetchRecUmrRollup was first added (LIQ-2403).
        fcpUmrList.value = (data.rows || []).map(Object.freeze)
      } catch (err) {
        if (axios.isCancel(err) || requestId !== recUmrFetchRequestId) return
        // LIQ-2403: cleared rather than left showing the previous selection's numbers as if they
        // were current (null renders the KPI tile as a dash).
        fcpAccountCount.value = null
        fcpUmrList.value = []
        showToast(requestErrorMessage(err, 'Failed to load the REC UMRs panel and FCP Accounts count.'))
      } finally {
        if (requestId === recUmrFetchRequestId) recUmrLoading.value = false
      }
    }

    const fetchRecUmrRollupDebounced = debounce(fetchRecUmrRollup, 400)

    // Same reasoning as resetBdxDetailsEmpty's own comment: the request this call superseded never
    // reaches its own finally-branch reset, so the loading flags must be cleared here too.
    const resetVarianceDetailsEmpty = () => {
      varianceDetailRows.value = []
      varianceTotalRows.value = 0
      varianceServerTotals.value = { total_paid_bdx: 0, total_paid_fcp: 0, variance_amount: 0 }
      varianceRangeBounds.value = {}
      varianceLoadingInitial.value = false
      varianceLoadingMore.value = false
    }

    const fetchVarianceDetails = async ({ append } = {}) => {
      const requestId = ++varianceFetchRequestId
      if (varianceDetailsCancelSource) varianceDetailsCancelSource.cancel()
      varianceDetailsCancelSource = axios.CancelToken.source()
      const cancelToken = varianceDetailsCancelSource.token

      // Only called once selectedPeriods is already settled (from the URL, or from fetchReport's
      // own default-period echo, which runs before this) - role/role_value aren't request params
      // here either (see varianceDetailsFilterParams/reconciliationService.js), so no roleScope
      // dependency to guard on.
      if (!selectedPeriods.value.length) {
        resetVarianceDetailsEmpty()
        return
      }

      const { umr, claim, isEmpty } = effectiveUmrAndClaimForVarianceDetails()
      if (isEmpty) {
        resetVarianceDetailsEmpty()
        return
      }

      const offset = append ? varianceDetailRows.value.length : 0
      const page_size = append ? VARIANCE_LOAD_MORE_SIZE : VARIANCE_INITIAL_PAGE_SIZE

      if (append) varianceLoadingMore.value = true
      else varianceLoadingInitial.value = true

      try {
        const response = await ReconciliationService.getFCPReportV2VarianceDetails({
          ...varianceDetailsFilterParams(),
          umr,
          claim,
          offset,
          page_size,
          cancelToken
        })
        if (requestId !== varianceFetchRequestId) return

        const data = (response && response.data) || {}
        // Object.freeze each incoming row - these rows are write-once/read-many (filtered/sorted/
        // rendered, mutated only via saveVarianceEdits' own splice-with-a-spread-copy, never in
        // place), so there's nothing to gain from Vue's usual per-property reactive get/set
        // rewriting, only the cost of doing it repeatedly. This endpoint is already paginated
        // (100-500 rows at a time), so the win here is smaller than for a fully unpaginated array,
        // but it's free and consistent to apply the same treatment. Already-frozen rows carried
        // over from a previous page (the append branch) stay frozen when spread into the new
        // array - freezing is a permanent, per-object flag, not something spreading/copying undoes.
        const newRows = (data.rows || []).map(Object.freeze)
        varianceDetailRows.value = append ? [...varianceDetailRows.value, ...newRows] : newRows
        varianceTotalRows.value = data.total_rows || 0
        varianceServerTotals.value =
          data.totals || { total_paid_bdx: 0, total_paid_fcp: 0, variance_amount: 0 }
        varianceRangeBounds.value = data.range_bounds || {}
      } catch (err) {
        if (axios.isCancel(err) || requestId !== varianceFetchRequestId) return
        showToast(requestErrorMessage(err, 'Failed to load variance transaction details.'))
      } finally {
        if (requestId === varianceFetchRequestId) {
          varianceLoadingInitial.value = false
          varianceLoadingMore.value = false
        }
      }
    }

    const fetchVarianceDetailsDebounced = debounce(() => fetchVarianceDetails({ append: false }), 400)
    const loadMoreVarianceDetails = () => fetchVarianceDetails({ append: true })

    const varianceLoadMoreCount = computed(() =>
      Math.min(VARIANCE_LOAD_MORE_SIZE, varianceTotalRows.value - varianceDetailRows.value.length)
    )
    const hasMoreVarianceDetails = computed(
      () => varianceDetailRows.value.length < varianceTotalRows.value
    )

    // BDX Detail by Transaction / Cashbook Details by Transaction pagination - same design as
    // Variance Transaction Details above (POST /bdx-details, POST /cashbook-details mirror
    // /variance-details exactly), applied once shipping/rendering the full, unpaginated bdx_rows/
    // cashbook_rows array from /production on every load became the same reported slowness for
    // these two tables (selecting many/all report_periods could mean tens of thousands of rows in
    // one response - see get_bdx_details' own HISTORY OF THIS DECISION). No column-sort UI and no
    // Export-everything button exist for either table today, so - unlike Variance - there's no
    // sort_by/sort_dir/export param to thread through, and no displayedXRows client-side search
    // layer on top; the fetched page is the table's own :items source directly.
    const BDX_INITIAL_PAGE_SIZE = 100
    const BDX_LOAD_MORE_SIZE = 500
    const CASHBOOK_INITIAL_PAGE_SIZE = 100
    const CASHBOOK_LOAD_MORE_SIZE = 500

    const bdxDetailRows = ref([])
    const bdxTotalRows = ref(0)
    const bdxServerTotals = ref({ total_paid_bdx: 0, paid_tpa_fees: 0 })
    const bdxLoadingInitial = ref(false)
    const bdxLoadingMore = ref(false)
    let bdxFetchRequestId = 0
    let bdxDetailsCancelSource = null

    // Total Paid/Paid TPA Fees' own real, full-dataset range-slider bounds (see
    // RecColumnSearchMenu's own 'range' mode) - comes straight from /bdx-details' own
    // response.range_bounds (service.numeric_column_bounds, cross-filtered server-side against
    // every OTHER active filter) - see fetchBdxDetails below, same as Variance's own
    // varianceRangeBounds. Starts as {} (no key present for any column) rather than a degenerate
    // {min:0,max:0} per column, so RecColumnSearchMenu's own hasRealRangeBounds check correctly
    // treats "haven't fetched yet" as "no real bounds to preview" until the first response
    // actually arrives.
    const bdxRangeBounds = ref({})

    const cashbookDetailRows = ref([])
    const cashbookTotalRows = ref(0)
    const cashbookServerTotals = ref({ debit: 0, credit: 0 })
    const cashbookLoadingInitial = ref(false)
    const cashbookLoadingMore = ref(false)
    let cashbookFetchRequestId = 0
    let cashbookDetailsCancelSource = null

    // Cashbook counterpart to bdxRangeBounds above - same reasoning, for Debit/Credit.
    const cashbookRangeBounds = ref({})

    // Same three-way UMR/claim merge as effectiveUmrAndClaimForVarianceDetails (umrFilter +
    // selectedUmrRow + the cross-table transaction-row click when it didn't originate in THIS
    // table) - sourceTable excludes 'bdx'/'cashbook' respectively here instead of 'variance',
    // matching matchesSelectedTransaction's own per-table exclusion exactly.
    const effectiveUmrAndClaimExcluding = (excludedSourceTable) => {
      let umrCandidates = null
      if (umrFilter.value && umrFilter.value.length) umrCandidates = new Set(umrFilter.value)
      if (selectedUmrRow.value) {
        umrCandidates = umrCandidates
          ? umrCandidates.has(selectedUmrRow.value)
            ? new Set([selectedUmrRow.value])
            : new Set()
          : new Set([selectedUmrRow.value])
      }

      let claimCandidates = null
      if (claimFilter.value && claimFilter.value.length) claimCandidates = new Set(claimFilter.value)
      if (selectedTransaction.value && excludedSourceTable !== selectedTransaction.value.sourceTable) {
        const { umr, claim } = selectedTransaction.value
        umrCandidates = umrCandidates
          ? umrCandidates.has(umr)
            ? new Set([umr])
            : new Set()
          : new Set([umr])
        claimCandidates = claimCandidates
          ? claimCandidates.has(claim)
            ? new Set([claim])
            : new Set()
          : new Set([claim])
      }

      return {
        umr: umrCandidates ? Array.from(umrCandidates) : undefined,
        claim: claimCandidates ? Array.from(claimCandidates) : undefined,
        isEmpty: (umrCandidates && 0 === umrCandidates.size) || (claimCandidates && 0 === claimCandidates.size)
      }
    }

    // Shared fields both /bdx-details and /cashbook-details take - see BdxCashbookDetailsRequestBase.
    const sharedBdxCashbookDetailsFilterParams = () => ({
      report_periods: selectedPeriods.value,
      account_name: accountNameFilter.value && accountNameFilter.value.length ? accountNameFilter.value : undefined,
      dca_selections: dcaFilter.value && dcaFilter.value.length ? dcaFilter.value : undefined
    })

    // BDX Detail by Transaction's own filter params - the shared three above, plus sort_by/sort_dir
    // and this grid's own search fields (section_number/limeiq_key/total_paid_bdx/paid_tpa_fees/
    // fcp_start_date - see BdxDetailsRequest). bdxColumnSearch's own value shape per key matches
    // columnSearch's (plain string / { min, max } / { from, to } - see BDX_RANGE_FILTER_FIELD_KEYS/
    // BDX_DATE_RANGE_FILTER_FIELD_KEYS), so each field here just reads the matching shape back out.
    const bdxDetailsFilterParams = () => ({
      ...sharedBdxCashbookDetailsFilterParams(),
      sort_by: bdxSortBy.value,
      sort_dir: bdxSortDesc.value ? 'desc' : 'asc',
      tpa: bdxChecklistSelected('tpa').length ? bdxChecklistSelected('tpa') : undefined,
      coverholder: bdxChecklistSelected('coverholder').length ? bdxChecklistSelected('coverholder') : undefined,
      section_number: bdxColumnSearch.value.section_number || undefined,
      limeiq_key: bdxColumnSearch.value.limeiq_key || undefined,
      total_paid_bdx_min: (bdxColumnSearch.value.total_paid_bdx || {}).min || undefined,
      total_paid_bdx_max: (bdxColumnSearch.value.total_paid_bdx || {}).max || undefined,
      paid_tpa_fees_min: (bdxColumnSearch.value.paid_tpa_fees || {}).min || undefined,
      paid_tpa_fees_max: (bdxColumnSearch.value.paid_tpa_fees || {}).max || undefined,
      fcp_start_date_from: (bdxColumnSearch.value.fcp_start_date || {}).from || undefined,
      fcp_start_date_to: (bdxColumnSearch.value.fcp_start_date || {}).to || undefined
    })

    // Cashbook Details by Transaction counterpart to bdxDetailsFilterParams above - same reasoning,
    // this grid's own search fields instead (section_number/narrative/debit/credit).
    const cashbookDetailsFilterParams = () => ({
      ...sharedBdxCashbookDetailsFilterParams(),
      sort_by: cashbookSortBy.value,
      sort_dir: cashbookSortDesc.value ? 'desc' : 'asc',
      section_number: cashbookColumnSearch.value.section_number || undefined,
      narrative: cashbookColumnSearch.value.narrative || undefined,
      debit_min: (cashbookColumnSearch.value.debit || {}).min || undefined,
      debit_max: (cashbookColumnSearch.value.debit || {}).max || undefined,
      credit_min: (cashbookColumnSearch.value.credit || {}).min || undefined,
      credit_max: (cashbookColumnSearch.value.credit || {}).max || undefined
    })

    // Only ever called from fetchBdxDetails/fetchCashbookDetails' own early returns (no periods
    // selected, or umr/claim narrowing leaves nothing to show). The loading flags are cleared here
    // too - reported bug: whichever request that call just superseded never reaches its own
    // finally-branch reset (its requestId is stale), so a request still in flight when the user,
    // say, unchecked every period left this table's spinner on for good.
    const resetBdxDetailsEmpty = () => {
      bdxDetailRows.value = []
      bdxTotalRows.value = 0
      bdxServerTotals.value = { total_paid_bdx: 0, paid_tpa_fees: 0 }
      bdxRangeBounds.value = {}
      bdxLoadingInitial.value = false
      bdxLoadingMore.value = false
    }

    const resetCashbookDetailsEmpty = () => {
      cashbookDetailRows.value = []
      cashbookTotalRows.value = 0
      cashbookServerTotals.value = { debit: 0, credit: 0 }
      cashbookRangeBounds.value = {}
      cashbookLoadingInitial.value = false
      cashbookLoadingMore.value = false
    }

    const fetchBdxDetails = async ({ append } = {}) => {
      const requestId = ++bdxFetchRequestId
      if (bdxDetailsCancelSource) bdxDetailsCancelSource.cancel()
      bdxDetailsCancelSource = axios.CancelToken.source()
      const cancelToken = bdxDetailsCancelSource.token

      if (!selectedPeriods.value.length) {
        resetBdxDetailsEmpty()
        return
      }

      const { umr, claim, isEmpty } = effectiveUmrAndClaimExcluding('bdx')
      if (isEmpty) {
        resetBdxDetailsEmpty()
        return
      }

      const offset = append ? bdxDetailRows.value.length : 0
      const page_size = append ? BDX_LOAD_MORE_SIZE : BDX_INITIAL_PAGE_SIZE

      if (append) bdxLoadingMore.value = true
      else bdxLoadingInitial.value = true

      try {
        const response = await ReconciliationService.getFCPReportV2BdxDetails({
          ...bdxDetailsFilterParams(),
          umr,
          claim,
          offset,
          page_size,
          cancelToken
        })
        if (requestId !== bdxFetchRequestId) return

        const data = (response && response.data) || {}
        const newRows = (data.rows || []).map(Object.freeze)
        bdxDetailRows.value = append ? [...bdxDetailRows.value, ...newRows] : newRows
        bdxTotalRows.value = data.total_rows || 0
        bdxServerTotals.value = data.totals || { total_paid_bdx: 0, paid_tpa_fees: 0 }
        bdxRangeBounds.value = data.range_bounds || {}
        bdxFilterOptions.value = data.filter_options || { tpa: [], coverholder: [] }
      } catch (err) {
        if (axios.isCancel(err) || requestId !== bdxFetchRequestId) return
        showToast(requestErrorMessage(err, 'Failed to load BDX detail transactions.'))
      } finally {
        if (requestId === bdxFetchRequestId) {
          bdxLoadingInitial.value = false
          bdxLoadingMore.value = false
        }
      }
    }

    const fetchCashbookDetails = async ({ append } = {}) => {
      const requestId = ++cashbookFetchRequestId
      if (cashbookDetailsCancelSource) cashbookDetailsCancelSource.cancel()
      cashbookDetailsCancelSource = axios.CancelToken.source()
      const cancelToken = cashbookDetailsCancelSource.token

      if (!selectedPeriods.value.length) {
        resetCashbookDetailsEmpty()
        return
      }

      const { umr, claim, isEmpty } = effectiveUmrAndClaimExcluding('cashbook')
      if (isEmpty) {
        resetCashbookDetailsEmpty()
        return
      }

      const offset = append ? cashbookDetailRows.value.length : 0
      const page_size = append ? CASHBOOK_LOAD_MORE_SIZE : CASHBOOK_INITIAL_PAGE_SIZE

      if (append) cashbookLoadingMore.value = true
      else cashbookLoadingInitial.value = true

      try {
        const response = await ReconciliationService.getFCPReportV2CashbookDetails({
          ...cashbookDetailsFilterParams(),
          umr,
          claim,
          offset,
          page_size,
          cancelToken
        })
        if (requestId !== cashbookFetchRequestId) return

        const data = (response && response.data) || {}
        const newRows = (data.rows || []).map(Object.freeze)
        cashbookDetailRows.value = append ? [...cashbookDetailRows.value, ...newRows] : newRows
        cashbookTotalRows.value = data.total_rows || 0
        cashbookServerTotals.value = data.totals || { debit: 0, credit: 0 }
        cashbookRangeBounds.value = data.range_bounds || {}
      } catch (err) {
        if (axios.isCancel(err) || requestId !== cashbookFetchRequestId) return
        showToast(requestErrorMessage(err, 'Failed to load cashbook detail transactions.'))
      } finally {
        if (requestId === cashbookFetchRequestId) {
          cashbookLoadingInitial.value = false
          cashbookLoadingMore.value = false
        }
      }
    }

    const fetchBdxDetailsDebounced = debounce(() => fetchBdxDetails({ append: false }), 400)
    const loadMoreBdxDetails = () => fetchBdxDetails({ append: true })
    const fetchCashbookDetailsDebounced = debounce(() => fetchCashbookDetails({ append: false }), 400)
    const loadMoreCashbookDetails = () => fetchCashbookDetails({ append: true })

    const bdxLoadMoreCount = computed(() =>
      Math.min(BDX_LOAD_MORE_SIZE, bdxTotalRows.value - bdxDetailRows.value.length)
    )
    const hasMoreBdxDetails = computed(() => bdxDetailRows.value.length < bdxTotalRows.value)
    const cashbookLoadMoreCount = computed(() =>
      Math.min(CASHBOOK_LOAD_MORE_SIZE, cashbookTotalRows.value - cashbookDetailRows.value.length)
    )
    const hasMoreCashbookDetails = computed(
      () => cashbookDetailRows.value.length < cashbookTotalRows.value
    )

    // submit_variance_comment/admin_variance_comment/submission_date/admin_variance_date still
    // stay client-side, narrowing only what's already been loaded (see the PAGINATION comment
    // above) - Total Paid (BDX)/(REC)/Variance no longer go through here at all (see
    // rowMatchesColumnSearch below), now that they're real server-side filters.
    // Text ('text'), Min/Max ('range' - see RANGE_FILTER_FIELD_KEYS), or From/To ('date-range' -
    // see DATE_RANGE_FILTER_FIELD_KEYS) - drives which popover RecColumnSearchMenu renders for a
    // given column's search icon (see #head() below).
    const columnSearchTypeFor = (key) => {
      if (RANGE_FILTER_FIELD_KEYS.includes(key)) return 'range'
      if (DATE_RANGE_FILTER_FIELD_KEYS.includes(key)) return 'date-range'
      return 'text'
    }

    // Variance's own counterpart to isBdxColumnSearchActive/isCashbookColumnSearchActive above.
    const isColumnSearchActive = (key) =>
      isColumnSearchValueActive(columnSearch.value[key], columnSearchTypeFor(key))

    // Plain "YYYY-MM-DD" string (lexicographic order is chronological order for that format) -
    // MUST go through date-fns' own parse/format (same as formatDate below), not the native Date
    // constructor. Reported bug (found the hard way): date-fns v1's format() parses a bare
    // date-only string ("2026-09-09", no time component) as LOCAL midnight, but the native Date
    // constructor parses that exact same string as UTC midnight per spec - in any timezone behind
    // UTC, those disagree by one calendar day. An earlier version of this helper used `new
    // Date(value)`, which then silently disagreed with what formatDate actually displays for
    // that same value (this filter would exclude/include the "wrong" day relative to what's on
    // screen). Going through date-fns' own parse for both ends guarantees this always agrees with
    // formatDate, for a date-only value or a full timestamp alike.
    const dateOnlyString = (value) => {
      if (!value) return null
      const parsed = parse(value)
      if (!isValid(parsed)) return null

      return format(parsed, 'YYYY-MM-DD')
    }

    const rowMatchesColumnSearch = (row) =>
      HEADER_SEARCH_SORT_FIELD_KEYS.every((key) => {
        // Total Paid (BDX)/(REC)/Variance now filter server-side (see varianceDetailsFilterParams'
        // own total_paid_bdx_min/max/etc.) - varianceDetailRows already only contains rows matching
        // the current min/max, so re-checking here would be redundant. Left IN
        // HEADER_SEARCH_SORT_FIELD_KEYS/RANGE_FILTER_FIELD_KEYS themselves (not removed) - those
        // still drive the search icon's presence on these columns and which popover type it opens
        // (see columnSearchTypeFor) - only the client-side re-filtering is skipped here.
        if (RANGE_FILTER_FIELD_KEYS.includes(key)) return true

        if (DATE_RANGE_FILTER_FIELD_KEYS.includes(key)) {
          const range = columnSearch.value[key] || {}
          if (!range.from && !range.to) return true

          // Compares calendar dates directly rather than building a Date-object cutoff and
          // comparing instants - reported bug: a row dated one day past "To" was still showing. A
          // date-only value parses as UTC midnight, which - in any timezone behind UTC - can
          // still be numerically "before" a LOCAL end-of-day cutoff even though it's already the
          // NEXT calendar day; this sidesteps that entirely.
          const rowDateString = dateOnlyString(row[key])
          if (!rowDateString) return false
          if (range.from && rowDateString < range.from) return false
          if (range.to && rowDateString > range.to) return false
          return true
        }

        const query = (columnSearch.value[key] || '').trim().toLowerCase()
        if (!query) return true

        const rawValue = row[key]
        const stringValue = null === rawValue || undefined === rawValue ? '' : String(rawValue)

        return stringValue.toLowerCase().includes(query)
      })

    const displayedVarianceRows = computed(() =>
      varianceDetailRows.value.filter((row) => rowMatchesColumnSearch(row))
    )

    // Total Paid (BDX)/(REC)/Variance's own range-slider bounds now come straight from
    // /variance-details' own response (varianceRangeBounds - see fetchVarianceDetails), computed
    // server-side over the full filtered dataset (service.numeric_column_bounds) rather than this
    // client-side rangeFilterBounds/rangeFilterBoundsMap, which only ever reflected whatever
    // page(s) had been loaded via "Load More" so far - same class of bug already fixed for
    // /bdx-details'/cashbook-details' own numeric columns.

    // Inline row-level editing for the Variance Transaction Details table: a TPA
    // checks one or more rows to unlock Variance Type/Submitter Comments for just
    // those rows, edits accumulate locally, and Save flushes them all at once.
    // Rows aren't backed by a database id yet (they come from the Power BI-shaped
    // mock data), so edits are keyed by the same natural key (UMR + Claim
    // Reference + Report Period) FCPSubmitVarianceForm already uses to
    // create-or-match a variance record. account_name is a 4th component on top
    // of that - two distinct rows can otherwise share all three (e.g. the same
    // claim posted under separate USD/GBP accounts in the same period), which
    // FCPSubmitVarianceForm never needs to worry about since it addresses its
    // target record directly by id instead.
    const rowKey = (row) => `${row.report_month_year}|${row.umr}|${row.claim}|${row.account_name || ''}`

    const checkedRowKeys = ref(new Set())
    const pendingEdits = ref({})

    // Drives a blanket pointer-events lock on the whole report body while at least one row is
    // checked with unsaved edits (see .fcp-report__backdrop--edit-locked in the .scss) - per
    // explicit decision, while in edit mode a user may only act on the Edit column checkboxes,
    // Variance Type/Submitter Comments/Admin Approval/Admin Comments, and Save/Discard - every
    // other control (toolbar filters, column header search/sort, row drill-down, Load More,
    // Excel export, Report Period tree, Party/DCA dropdown, Clear Filters) becomes inert. Page
    // NAVIGATION away from this component entirely is a separate mechanism (guardUnsavedChanges/
    // beforeRouteLeave in Reconciliation.vue), unaffected by this - that still pops the
    // Save/Discard/Cancel modal rather than being blocked outright.
    const isEditLocked = computed(() => 0 < checkedRowKeys.value.size)

    // Shared :popper-opts for every checklist/search b-dropdown in this report (Report Period,
    // Party/DCA, Review Status, UMR, Claim Reference, Matched Account Name, Source, Variance Type,
    // Admin Approval - all fourteen instances previously repeated the same plain
    // { positionFixed: true, strategy: 'fixed' } literal). Reported bug: opening one of these near
    // the bottom of the page rendered it UNDER the app shell's own fixed footer (AppFooter.vue,
    // #main-footer) - boundary="window" only keeps bootstrap-vue's own popper instance inside the
    // true browser viewport, which has no idea the footer bar is visually sitting on top of that
    // viewport's own bottom edge. bootstrap-vue's own dropdown mixin deep-merges popper-opts INTO
    // (not over) its own internally-built config (see getPopperConfig's own mergeDeep call), so
    // adding preventOverflow.padding here doesn't clobber the boundariesElement the boundary prop
    // already sets - it just shrinks the effective boundary by however much of the viewport's own
    // bottom the footer actually covers, same "clamp against the footer's top edge, not
    // window.innerHeight" fix togglePartyTypeFlyout's own hand-rolled positioning already uses
    // (see its own comment - reused verbatim here), applied via Popper's own supported mechanism
    // instead (these are real <b-dropdown>s, positioned by Popper, not a hand-rolled fixed div).
    //
    // A PLAIN FUNCTION, called from the template (dropdownPopperOpts(), not a bare binding) -
    // NOT a computed. Tried computed() first: with no reactive dependency (a raw DOM query isn't
    // one), it only ever evaluates ONCE and caches that result forever, so whatever the footer's
    // position happened to be at that first read (possibly before layout had even settled) is
    // what every dropdown keeps using from then on, regardless of when one's actually opened.
    // Calling this fresh each render re-measures every time instead.
    const dropdownPopperOpts = () => {
      const footerEl = document.getElementById('main-footer')
      // How much of the viewport's own bottom edge the footer actually covers - NOT its own
      // height, which is only correct if the footer happens to sit flush against that edge with
      // nothing below it. window.innerHeight - its own top edge is the real measure regardless.
      const footerCoverage = footerEl ? window.innerHeight - footerEl.getBoundingClientRect().top : 0

      return {
        positionFixed: true,
        strategy: 'fixed',
        modifiers: {
          preventOverflow: {
            padding: { top: 8, right: 8, bottom: Math.max(8, footerCoverage + 8), left: 8 }
          }
        }
      }
    }

    // Rows currently flagged for a missing required submitter field - set on a
    // failed saveVarianceEdits attempt (Variance Type/Submitter Comments are
    // required), cleared as soon as the user edits that field again (see
    // onVarianceTypeChange/onVarianceTypeOtherInput/onCommentInput below), not
    // just on the next Save click. Drives a red-border highlight on the input
    // itself rather than a toast - see isVarianceTypeInvalid/isCommentInvalid.
    const invalidVarianceTypeKeys = ref(new Set())
    const invalidCommentKeys = ref(new Set())
    // Same treatment, admin side: Admin Comment is only required once an admin actually responds
    // to a variance (Admin Approval changed away from its default "Pending") - see
    // saveVarianceEdits' canEditAdminReview block.
    const invalidAdminCommentKeys = ref(new Set())

    const isVarianceTypeInvalid = (row) => invalidVarianceTypeKeys.value.has(rowKey(row))
    const isCommentInvalid = (row) => invalidCommentKeys.value.has(rowKey(row))
    const isAdminCommentInvalid = (row) => invalidAdminCommentKeys.value.has(rowKey(row))

    const seedPendingEdit = (row) => {
      const rawType = row.variance_type || null
      const isFixedType = rawType && VARIANCE_TYPE_SUBMIT_OPTIONS.includes(rawType)
      // variance_type_other_reason is the dedicated column (see saveVarianceEdits) - falls back
      // to inferring the reason from an unrecognized variance_type value for rows saved before
      // that column existed, or if the rec report endpoint hasn't started returning it yet.
      const otherText = row.variance_type_other_reason || (rawType && !isFixedType ? rawType : '')

      // Admin-side starting state (see canEditAdminReview) - variance_status is the only signal
      // this report's own read data has about a prior admin decision. A Superuser, or an Admin who
      // is this row's lead underwriter, always starts at Approved per explicit request - even on a
      // row that was previously Rejected - so they can check + save with zero typing to approve.
      // Anyone else starts at the row's saved decision.
      const savedAdminChoice =
        'Approved' === row.variance_status
          ? 'Approved'
          : 'Rejected' === row.variance_status
          ? 'Rejected'
          : 'Pending'
      const canApproveRow =
        'Superuser' === fcpRole.value || ('Admin' === fcpRole.value && isRowLeadUnderwriter(row))
      const initialAdminChoice = canApproveRow ? 'Approved' : savedAdminChoice

      return {
        varianceTypeChoice: rawType ? (isFixedType ? rawType : 'Other') : '',
        otherText,
        // An "Other" row carries its saved reason the same way onVarianceTypeOtherInput does, so
        // saving the row untouched sends that reason back instead of a blank one. With just 'Other'
        // here, every later save (an approver's included) wiped the submitter's reason.
        variance_type: 'Other' === rawType ? otherText || 'Other' : rawType,
        submit_variance_comment: row.submit_variance_comment || '',
        submission_date: row.submission_date || null,
        // Saved submission at the moment this row was checked (LIQ-2510) - saveVarianceEdits only
        // records the current user as the submitter when one of these actually changes, so an
        // approver saving a decision leaves the original submitter alone.
        initialVarianceType: rawType,
        initialOtherReason: row.variance_type_other_reason || '',
        initialSubmitComment: row.submit_variance_comment || '',
        adminApprovalChoice: initialAdminChoice,
        // Stable snapshots of this row's SAVED admin state (not the default above), from the
        // moment this row was checked - never mutated afterward (unlike adminApprovalChoice/
        // admin_variance_comment themselves). pendingAdminDate/saveVarianceEdits both compare the
        // CURRENT choice/comment against these to detect genuine in-session activity (a real
        // change, not just reopening an already-decided row and re-saving it untouched, which must
        // NOT restamp admin_variance_date - that was overwriting a row's real prior date with
        // today's just from checking it). Snapshotting the saved state means a pre-filled
        // Approved default on a Pending row still counts as a change, so saving it stamps this
        // approver's own date/user rather than keeping a stale one from an earlier reset to Pending.
        initialAdminApprovalChoice: savedAdminChoice,
        initialAdminComment: row.admin_variance_comment || '',
        admin_variance_comment: row.admin_variance_comment || ''
      }
    }

    // Superuser: any row, unconditionally. User/Admin now route through canEditVariance (see its
    // own comment) rather than a bare role check - a User/Admin who fails hasActiveRecStatus (and
    // for Admin, rowPassesLeadGate too) can't check the row at all, not just fail to see editable
    // fields once checked. This matters beyond cosmetics: saveVarianceEdits pushes every CHECKED
    // row into its save payload regardless of whether canEditVariance/canEditAdminReview end up
    // true for it, so a checkable-but-not-actually-editable row could still fire a real (if
    // field-less) create/update request and come back as a success toast - letting it get checked
    // in the first place was the actual bug, not just a missing field-level lock. Admin can now
    // ALSO submit a variance and approve it in the same action (not just respond to one a User
    // already submitted) - the one restriction that doesn't relax for Admin is re-litigating a
    // closed Approved decision. Rejected rows stay checkable (per explicit request) so Admin can
    // revisit and re-approve one without it being permanently locked out the way Approved is.
    // canEditVariance and canEditAdminReview are equivalent for Admin now (both require
    // hasActiveRecStatus, with rowPassesLeadGate applied separately below per row), so reading
    // canEditVariance alone is enough here.
    const isRowCheckable = (row) => {
      if ('Superuser' === fcpRole.value) return true
      if ('User' === fcpRole.value) return canEditVariance.value
      if ('Admin' === fcpRole.value) return canEditVariance.value && rowPassesLeadGate(row) && 'Approved' !== row.variance_status

      return false
    }

    // Split by role rather than one combined isRowEditable: a checked row
    // unlocks different fields depending on who checked it (Variance Type/
    // Submitter Comments for canEditVariance, Admin Approval/Admin Comments
    // for canEditAdminReview). Both unlock together for Admin and Superuser,
    // who get both field sets on the same checked row - only a plain User
    // gets just the one. rowPassesLeadGate is a no-op for User/Superuser (see its own comment), so
    // this doesn't change anything for them - only an Admin viewing a row they don't lead loses
    // editable fields here, same as they already can't check the row at all (isRowCheckable above).
    const isRowEditableBySubmitter = (row) =>
      canEditVariance.value && rowPassesLeadGate(row) && checkedRowKeys.value.has(rowKey(row))

    const isRowEditableByAdmin = (row) =>
      canEditAdminReview.value && rowPassesLeadGate(row) && checkedRowKeys.value.has(rowKey(row))

    const toggleCheckedRow = (row) => {
      if (!isRowCheckable(row)) return

      const key = rowKey(row)
      const nextChecked = new Set(checkedRowKeys.value)
      const nextEdits = { ...pendingEdits.value }

      if (nextChecked.has(key)) {
        nextChecked.delete(key)
        delete nextEdits[key]
      } else {
        nextChecked.add(key)
        nextEdits[key] = seedPendingEdit(row)
      }

      checkedRowKeys.value = nextChecked
      pendingEdits.value = nextEdits

      // Stale from a previous save attempt on this same row - clear on any
      // toggle (check or uncheck) so a freshly (re-)checked row doesn't show
      // red before the user has tried saving it again.
      if (
        invalidVarianceTypeKeys.value.has(key) ||
        invalidCommentKeys.value.has(key) ||
        invalidAdminCommentKeys.value.has(key)
      ) {
        const nextInvalidType = new Set(invalidVarianceTypeKeys.value)
        const nextInvalidComment = new Set(invalidCommentKeys.value)
        const nextInvalidAdminComment = new Set(invalidAdminCommentKeys.value)
        nextInvalidType.delete(key)
        nextInvalidComment.delete(key)
        nextInvalidAdminComment.delete(key)
        invalidVarianceTypeKeys.value = nextInvalidType
        invalidCommentKeys.value = nextInvalidComment
        invalidAdminCommentKeys.value = nextInvalidAdminComment
      }
    }

    // "Visible" now means "currently loaded" (displayedVarianceRows), not "matching every filter" -
    // a natural consequence of pagination, since rows not yet loaded via "Load more" can't be
    // checked at all.
    const allVisibleRowsChecked = computed(() => {
      const checkableRows = displayedVarianceRows.value.filter(isRowCheckable)

      return 0 < checkableRows.length && checkableRows.every((row) => checkedRowKeys.value.has(rowKey(row)))
    })

    const toggleAllCheckedRows = () => {
      const checkableRows = displayedVarianceRows.value.filter(isRowCheckable)

      if (allVisibleRowsChecked.value) {
        checkedRowKeys.value = new Set()
        pendingEdits.value = {}

        return
      }

      const nextChecked = new Set()
      const nextEdits = {}

      checkableRows.forEach((row) => {
        const key = rowKey(row)
        nextChecked.add(key)
        nextEdits[key] = pendingEdits.value[key] || seedPendingEdit(row)
      })

      checkedRowKeys.value = nextChecked
      pendingEdits.value = nextEdits
    }

    const varianceTypeChoice = (row) => (pendingEdits.value[rowKey(row)] || {}).varianceTypeChoice || ''
    const varianceTypeOtherText = (row) => (pendingEdits.value[rowKey(row)] || {}).otherText || ''
    const pendingComment = (row) => (pendingEdits.value[rowKey(row)] || {}).submit_variance_comment || ''
    const pendingSubmissionDate = (row) => (pendingEdits.value[rowKey(row)] || {}).submission_date || null

    const updatePendingEdit = (row, patch) => {
      const key = rowKey(row)
      const current = pendingEdits.value[key] || seedPendingEdit(row)

      pendingEdits.value = { ...pendingEdits.value, [key]: { ...current, ...patch } }
    }

    // Choice is always a plain string (never null) — the "Select…" placeholder's
    // native <option> has value="", which avoids the DOM's null/string coercion
    // quirks a bound `:value="null"` option would otherwise hit.
    const onVarianceTypeChange = (row, choice) => {
      const current = pendingEdits.value[rowKey(row)] || seedPendingEdit(row)

      updatePendingEdit(row, {
        varianceTypeChoice: choice,
        otherText: 'Other' === choice ? current.otherText : '',
        variance_type: '' === choice ? null : 'Other' === choice ? current.otherText || 'Other' : choice,
        // '' ("Select…") means "wipe this submission back to never-happened" (see
        // saveVarianceEdits' isSuperuserClearingType/backend's own submission_date handling),
        // so the date goes back to null along with it - not preserved, which would leave a
        // stale submission date sitting next to a blank Variance Type.
        submission_date: '' === choice ? null : format(new Date(), DATE_FORMAT)
      })

      if ('' !== choice) {
        const next = new Set(invalidVarianceTypeKeys.value)
        next.delete(rowKey(row))
        invalidVarianceTypeKeys.value = next
      }
    }

    const onVarianceTypeOtherInput = (row, text) => {
      updatePendingEdit(row, { otherText: text, variance_type: text || 'Other' })

      const next = new Set(invalidVarianceTypeKeys.value)
      next.delete(rowKey(row))
      invalidVarianceTypeKeys.value = next
    }

    const onCommentInput = (row, text) => {
      updatePendingEdit(row, { submit_variance_comment: text })

      if (text.trim()) {
        const next = new Set(invalidCommentKeys.value)
        next.delete(rowKey(row))
        invalidCommentKeys.value = next
      }
    }

    // Admin-side counterparts to varianceTypeChoice/pendingComment/
    // onVarianceTypeChange/onCommentInput above. Unlike those, '' is itself a real,
    // meaningful selection here (Superuser's "Select…" - see adminApprovalMenuOptions),
    // not just "nothing typed yet" - a plain `|| 'Pending'` fallback would silently
    // coerce that explicit choice right back to Pending before it ever reaches Save, so
    // only a genuinely un-seeded row (no pendingEdits entry, adminApprovalChoice still
    // undefined) falls through to the 'Pending' default.
    const adminApprovalChoice = (row) => {
      const edit = pendingEdits.value[rowKey(row)]
      return edit && undefined !== edit.adminApprovalChoice ? edit.adminApprovalChoice : 'Pending'
    }
    const pendingAdminComment = (row) =>
      (pendingEdits.value[rowKey(row)] || {}).admin_variance_comment || ''
    // Purely computed (not a stored pendingEdits field) so it stays correct regardless of WHICH
    // action triggered it - picking Approved/Rejected in the dropdown, reverting an already-
    // decided row back to Pending, or just typing a comment - rather than needing each of those
    // call sites to remember to stamp a stored value themselves and risk drifting out of sync.
    // Compares against initialAdminApprovalChoice/initialAdminComment (this row's OWN snapshot
    // from the moment it was checked - see seedPendingEdit), not just the current choice/comment
    // in isolation - reopening an already-decided row and re-saving it UNTOUCHED must keep
    // showing its real prior date (falls through to the template's own value fallback), not
    // today's date, which is what a plain 'final choice isn't Pending' check used to do (reported
    // bug: checking an already-Approved row silently replaced its real admin date with today's).
    const pendingAdminDate = (row) => {
      const edit = pendingEdits.value[rowKey(row)]
      if (!edit) return null

      const choiceChanged = edit.adminApprovalChoice !== edit.initialAdminApprovalChoice
      const commentChanged = (edit.admin_variance_comment || '') !== (edit.initialAdminComment || '')
      // A row with no real prior admin_variance_date at all (never decided before) has nothing
      // to preserve - landing on anything other than Pending (including the untouched Approved
      // default) is itself a genuinely NEW decision about to be made, so it counts even without
      // choiceChanged/commentChanged detecting an explicit in-session edit.
      const isNewDecision = !row.admin_variance_date && 'Pending' !== edit.adminApprovalChoice

      return choiceChanged || commentChanged || isNewDecision ? format(new Date(), DATE_FORMAT) : null
    }

    const onAdminApprovalChange = (row, choice) => {
      updatePendingEdit(row, { adminApprovalChoice: choice })

      // Comment is only required for Rejected now (see saveVarianceEdits' own
      // missingAdminComment) - switching to anything else (Approved or Pending) means Admin
      // Comment is no longer required, so drop any stale red border immediately rather than
      // leaving it until the next save attempt.
      if ('Rejected' !== choice) {
        const next = new Set(invalidAdminCommentKeys.value)
        next.delete(rowKey(row))
        invalidAdminCommentKeys.value = next
      }
    }

    const onAdminCommentInput = (row, text) => {
      // admin_variance_date itself isn't tracked here anymore - pendingAdminDate (see its own
      // comment) derives it live from admin_variance_comment, so just updating the comment is
      // enough for the date preview to follow automatically. The actual saved admin_variance_date
      // is still computed fresh at save time in saveVarianceEdits, as a full ISO datetime the
      // backend's DateTimeField requires (DATE_FORMAT alone doesn't satisfy it - see that
      // function's own comment).
      updatePendingEdit(row, { admin_variance_comment: text })

      if (text.trim()) {
        const next = new Set(invalidAdminCommentKeys.value)
        next.delete(rowKey(row))
        invalidAdminCommentKeys.value = next
      }
    }

    const discardVarianceEdits = () => {
      checkedRowKeys.value = new Set()
      pendingEdits.value = {}
      invalidVarianceTypeKeys.value = new Set()
      invalidCommentKeys.value = new Set()
      invalidAdminCommentKeys.value = new Set()
    }

    // FCPVariance.variance_report_period is a Django DateField ("YYYY-MM-DD"),
    // but this report's own period keys are "Month Year" (e.g. "January 2026")
    // - converted to the LAST day of that month (not the 1st), per how this
    // report's periods represent a full month rather than a specific day.
    // new Date(year, monthIndex + 1, 0) is "day 0 of next month", which JS
    // normalizes to the last day of the target month - correctly handles
    // December (rolls into next year) and leap-year Februaries.
    const reportPeriodToDate = (monthYear) => {
      const [monthName, year] = monthYear.split(' ')
      const monthIndex = MONTH_NAMES.indexOf(monthName)
      if (0 > monthIndex) return null

      const lastDay = new Date(Number(year), monthIndex + 1, 0).getDate()

      return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    }

    // Drives the Save/Discard buttons' disabled state and the Variance Transaction Details
    // panel's own overlay (see the template) - same pattern as varianceLoadingMore/
    // varianceExporting elsewhere in this file, just scoped to this one action.
    const savingVarianceEdits = ref(false)

    const saveVarianceEdits = async () => {
      const payload = []
      // Rows checked by a submitter (canEditVariance) but missing Variance Type
      // and/or Submitter Comments - required fields for a submission, so these
      // are skipped entirely (not sent - the backend would 400 on the missing
      // Variance Type anyway, less specifically) rather than saved half-filled.
      // Surfaced as a red border on the specific empty field (see
      // invalidVarianceTypeKeys/invalidCommentKeys), not a toast - recomputed
      // fresh on every save attempt rather than accumulated across attempts.
      const nextInvalidVarianceTypeKeys = new Set()
      const nextInvalidCommentKeys = new Set()
      const nextInvalidAdminCommentKeys = new Set()
      // Rows blocked specifically because a non-Superuser tried to blank out an
      // already-saved Variance Type/Submitter Comment/Admin Comment, or reset an
      // already-decided Admin Approval back to Pending (LIQ-2444 follow-up:
      // clearing/removing an existing value is Superuser-only - Admin/User can
      // still submit a new one or change an existing one to a different value).
      // Tracked separately from the other invalid* sets above so it can get its
      // own, more specific toast instead of just a silent red border.
      const nextClearRestrictedKeys = new Set()
      // Per row key, whether this save records the current user as the submitter and/or the
      // reviewer (LIQ-2510) - reused after a successful save to update the row's own hover text
      // (submitted_by/reviewed_by) without waiting for a refetch.
      const userStamps = new Map()

      checkedRowKeys.value.forEach((key) => {
        // LIQ-2403: looks up a checked row's data from varianceDetailRows (the Variance
        // Transaction Details table's own, already-loaded/paginated data - the exact same data
        // the checkbox itself was rendered against) instead of a separate full unpaginated fetch
        // (rawRows, since removed - see fetchReport's own HISTORY OF THIS DECISION note). A row
        // checked earlier that no longer matches the CURRENT filters (e.g. a filter changed after
        // it was checked, before Save was clicked) silently can't be found here - same as it
        // already couldn't be found in rawRows after a report_periods/dca_selections change under
        // the old code either; this only widens which filter changes can cause that, not a new
        // category of risk.
        const index = varianceDetailRows.value.findIndex((row) => rowKey(row) === key)
        if (0 > index) return

        const row = varianceDetailRows.value[index]
        const edit = pendingEdits.value[key]

        // Merged rather than either/or: canEditVariance and canEditAdminReview
        // both apply for an Admin or Superuser session, and their edits to the
        // same row need to go out together in one request instead of one
        // clobbering the other.
        const body = {
          key,
          UMR: row.umr,
          Claim_Reference: row.claim,
          variance_report_period: reportPeriodToDate(row.report_month_year),
          account_name: row.account_name
        }

        if (canEditVariance.value) {
          // FCPVariance.variance_type is a fixed-choice field on the backend (one of
          // VARIANCE_TYPE_SUBMIT_OPTIONS) - picking "Other" here stores the user's typed reason
          // directly in variance_type instead (see onVarianceTypeOtherInput), which the real
          // backend rejects with a 400 for anything other than the literal word "Other".
          // variance_type_other_reason (its own dedicated column) is where that typed reason
          // actually goes, keeping the backend happy without folding it into
          // submit_variance_comment (the earlier approach, before that column existed).
          const isOtherReason =
            edit.variance_type && !VARIANCE_TYPE_SUBMIT_OPTIONS.includes(edit.variance_type)

          const variance_type = isOtherReason ? 'Other' : edit.variance_type
          const variance_type_other_reason = isOtherReason ? edit.variance_type : ''
          const submit_variance_comment = edit.submit_variance_comment

          // Submitter Comments is required for a plain User submission, but
          // optional for Admin/Superuser - both can submit a variance and
          // approve it in the same action, and Variance Type alone is enough
          // to say why. A blank comment failing validation used to reject the
          // whole row (admin fields included) even for Superuser, which is
          // exactly why admin_variance_date wasn't making it through on a
          // combined submit+approve.
          const commentRequired = 'User' === fcpRole.value

          const missingType = !variance_type
          const missingComment = commentRequired && !(submit_variance_comment || '').trim()
          const hadType = Boolean(row.variance_type)

          // Only a Superuser may clear (blank out) a Variance Type or Submitter
          // Comment that was already saved against this row - Admin/User can
          // still submit a brand-new one, or change an existing one to a
          // different value, just not erase it outright. Checked before the
          // missingType/missingComment gate below so a blocked clear attempt is
          // its own distinct, explained reason rather than silently falling
          // into "nothing to submit" (which Admin/Superuser are allowed to just
          // skip over - see attemptingSubmission).
          const clearingType = !isSuperuser.value && hadType && missingType
          const clearingComment =
            !isSuperuser.value &&
            Boolean((row.submit_variance_comment || '').trim()) &&
            !(submit_variance_comment || '').trim()

          if (clearingType) nextInvalidVarianceTypeKeys.add(key)
          if (clearingComment) nextInvalidCommentKeys.add(key)
          if (clearingType || clearingComment) nextClearRestrictedKeys.add(key)

          if (clearingType || clearingComment) return

          // A row that already had a Variance Type only ever reaches a blank one here
          // via an intentional Superuser clear (anyone else would have been caught by
          // clearingType above) - never a "forgot to fill this in" submission, so it
          // must skip the attemptingSubmission/missingType requirement entirely.
          // Without this, a row with a leftover (untouched, already-saved) Submitter
          // Comment made attemptingSubmission true purely because that old comment text
          // was still sitting there - not because anything NEW was being submitted -
          // which wrongly forced Variance Type to stay required and bailed the whole
          // row (Admin Approval/Comment edits included, since this `return` skips
          // canEditAdminReview below too) any time a Superuser tried to clear Variance
          // Type on a row that also happened to have an old comment (reported bug -
          // "Select…" on Variance Type silently did nothing).
          const isSuperuserClearingType = isSuperuser.value && hadType && missingType

          // A plain User session is always submitting a variance, so a blank Variance
          // Type is always invalid for them. Admin/Superuser only need one if they're
          // actually entering one (or a comment to go with it) - a row that's never had
          // a variance submitted against it (variance_status "Not Submitted", both
          // row.variance_type and edit.variance_type blank) has nothing to submit, and
          // Admin/Superuser may still just be here for canEditAdminReview below. This
          // used to require Variance Type unconditionally and bail out of the ENTIRE
          // row - including any canEditAdminReview changes further down - any time an
          // Admin/Superuser tried to save a review-only edit on such a row, which is
          // exactly why Superusers couldn't clear/save admin comments & approvals on a
          // row with no prior variance submission (reported bug).
          const attemptingSubmission =
            !isSuperuserClearingType &&
            (commentRequired || Boolean((submit_variance_comment || '').trim()))

          if (attemptingSubmission && missingType) nextInvalidVarianceTypeKeys.add(key)
          if (missingComment) nextInvalidCommentKeys.add(key)

          if ((attemptingSubmission && missingType) || missingComment) return

          // The current user becomes the submitter only when this save gives the row a new
          // Variance Type ("Other" reason included) or Submitter Comment (LIQ-2510). Every
          // Admin/Superuser save re-sends the type and comment below even when untouched, and used
          // to send their own id with them - so just approving a TPA's variance replaced the TPA as
          // its submitter. Left out, the backend keeps the saved one. Clearing back to blank isn't
          // a new submission. Same rule as the backend's FCPVarianceViewSet._changes_submission,
          // which moves the row's submission_date to now on exactly these saves.
          const changedTo = (next, saved) => Boolean(next) && next !== (saved || '').trim()
          const submissionChanged =
            changedTo(variance_type, edit.initialVarianceType) ||
            changedTo(variance_type_other_reason.trim(), edit.initialOtherReason) ||
            changedTo((submit_variance_comment || '').trim(), edit.initialSubmitComment)

          // A Superuser clearing variance_type back to blank (clearingType would have
          // been true above, were it allowed) still needs this block to run so the
          // blank value actually reaches the backend - omitting it here would leave
          // the prior value untouched instead of clearing it.
          if (!missingType || isSuperuserClearingType) {
            Object.assign(body, {
              variance_type,
              variance_type_other_reason,
              submit_variance_comment,
              submission_date: edit.submission_date,
              ...(submissionChanged ? { submit_variance_user: userId.value } : {})
            })
            if (submissionChanged) userStamps.set(key, { ...userStamps.get(key), submitted: true })
          }
        }

        if (canEditAdminReview.value) {
          const admin_variance_comment = edit.admin_variance_comment || ''
          // Comment is only required for Rejected now, not Approved - per explicit request,
          // approving shouldn't need justification the way rejecting does. Approved defaults the
          // dropdown itself now too (see seedPendingEdit), so an admin can check a row and save
          // immediately with zero typing if they're just approving it.
          const missingAdminComment = 'Rejected' === edit.adminApprovalChoice && !admin_variance_comment.trim()

          // Mirrors the submitter-side restriction above: only a Superuser may clear an
          // existing Admin Comment, or reset an already-decided Approved/Rejected row
          // back to Pending. Admin can still flip Approved <-> Rejected or add/change a
          // comment - 'Approved'/'Rejected' are the only two states seedPendingEdit treats
          // as a real prior decision (see its own comment), so only a transition away from
          // one of those back to Pending counts as "clearing" here.
          const clearingAdminComment =
            !isSuperuser.value &&
            Boolean((row.admin_variance_comment || '').trim()) &&
            !admin_variance_comment.trim()
          const clearingApproval =
            !isSuperuser.value &&
            ('Approved' === row.variance_status || 'Rejected' === row.variance_status) &&
            ('Pending' === edit.adminApprovalChoice || '' === edit.adminApprovalChoice)

          if (clearingAdminComment || clearingApproval) nextInvalidAdminCommentKeys.add(key)
          if (clearingAdminComment || clearingApproval) nextClearRestrictedKeys.add(key)

          if (clearingAdminComment || clearingApproval) return

          if (missingAdminComment) nextInvalidAdminCommentKeys.add(key)

          if (missingAdminComment) return

          // Same comparison pendingAdminDate uses (see its own comment) - stamps
          // admin_variance_date/admin_variance_user only on GENUINE in-session activity (the
          // choice or comment actually differs from this row's own snapshot at seed time, or
          // it's a brand new decision on a row with no prior admin_variance_date at all), not
          // just because the row is checked and its current (possibly untouched) state happens
          // to not equal Pending. Reopening an already-decided row and re-saving it UNCHANGED
          // must NOT restamp its real prior date with today's (reported bug).
          const choiceChanged = edit.adminApprovalChoice !== edit.initialAdminApprovalChoice
          const commentChanged = admin_variance_comment !== (edit.initialAdminComment || '')
          const isNewDecision = !row.admin_variance_date && 'Pending' !== edit.adminApprovalChoice
          const hasAdminActivity = choiceChanged || commentChanged || isNewDecision

          // '' ("Select…") is Superuser-only (see adminApprovalMenuOptions) and means "wipe this
          // review back to never-happened" - unlike picking 'Pending' (a genuine new decision to
          // stop reviewing, which still gets dated/attributed like any other decision), '' must
          // NOT stamp admin_variance_date/admin_variance_user at all, and clears them back to null
          // if they were already set - otherwise the row would show today's date/this Superuser's
          // name next to a supposedly-blank review, which defeats the point of "Select…" existing
          // as a distinct option from 'Pending' in the first place.
          const isFullyClearing = '' === edit.adminApprovalChoice

          // admin_variance_status is a nullable BooleanField on the backend -
          // True/False/null for Approved/Rejected/Pending, same convention
          // FCPDownloadButton.vue already uses for this same field.
          Object.assign(body, {
            admin_variance_status:
              'Approved' === edit.adminApprovalChoice
                ? true
                : 'Rejected' === edit.adminApprovalChoice
                ? false
                : null,
            admin_variance_comment,
            // admin_variance_date has no auto_now-style default on the model (unlike
            // submission_date), so it's only set explicitly here, and only once there's actual
            // admin activity to timestamp - never for a still-Pending, still-blank row.
            // admin_variance_date is a DateTimeField on the backend (unlike
            // variance_report_period, a plain DateField) - DRF's datetime
            // validation requires a full ISO datetime with a time component
            // (Django's parse_datetime rejects a bare "YYYY-MM-DD", which is
            // all DATE_FORMAT produces), so this needs toISOString() instead.
            ...(isFullyClearing
              ? { admin_variance_date: null, admin_variance_user: null }
              : hasAdminActivity
              ? { admin_variance_date: new Date().toISOString(), admin_variance_user: userId.value }
              : {})
          })
          if (isFullyClearing || hasAdminActivity) {
            userStamps.set(key, { ...userStamps.get(key), reviewed: isFullyClearing ? 'cleared' : 'set' })
          }
        }

        payload.push(body)
      })

      invalidVarianceTypeKeys.value = nextInvalidVarianceTypeKeys
      invalidCommentKeys.value = nextInvalidCommentKeys
      invalidAdminCommentKeys.value = nextInvalidAdminCommentKeys

      // Unlike the other invalid* reasons (silent red border only, see their own
      // comment above), a blocked clear attempt gets an explicit toast - the
      // affected field(s) look identical to a plain "required field missing"
      // red border otherwise, which wouldn't explain WHY a value that's clearly
      // already filled in still won't save.
      if (nextClearRestrictedKeys.size) {
        showToast(
          `Only a Superuser can clear an existing Variance Type, Submitter Comment, Admin Comment, or Admin Approval. ${nextClearRestrictedKeys.size} row(s) were not saved - change the value instead of blanking it, or ask a Superuser to clear it.`,
          'error'
        )
      }

      const invalidKeys = new Set([
        ...nextInvalidVarianceTypeKeys,
        ...nextInvalidCommentKeys,
        ...nextInvalidAdminCommentKeys
      ])

      // Every checked row failed validation - nothing to send. The red field(s)
      // above already say why (plus the toast above, for a blocked clear
      // attempt specifically), so no separate toast for this branch itself.
      if (!payload.length) {
        checkedRowKeys.value = invalidKeys
        pendingEdits.value = Object.fromEntries(
          Object.entries(pendingEdits.value).filter(([key]) => invalidKeys.has(key))
        )
        return
      }

      savingVarianceEdits.value = true

      let results
      try {
        results = await Promise.allSettled(
          payload.map(({ key, ...body }) => FCPVariance.createOrUpdateFCPVariance(body))
        )
      } finally {
        savingVarianceEdits.value = false
      }

      const succeededKeys = new Set()
      const failedKeys = new Set()

      results.forEach((result, i) => {
        const { key } = payload[i]

        if ('fulfilled' === result.status) succeededKeys.add(key)
        else failedKeys.add(key)
      })

      // Only rows that actually saved get their local row data updated and
      // unchecked - failed ones stay checked (with their typed edits intact)
      // so the user can fix whatever's wrong and retry without re-entering
      // anything.
      checkedRowKeys.value.forEach((key) => {
        if (!succeededKeys.has(key)) return

        // rawRows no longer feeds the Variance Transaction Details table itself (see the
        // PAGINATION comment above varianceDetailRows) - its own displayed data has to be patched
        // directly here, or a saved edit wouldn't show up until the next full refetch. Since
        // rawRows itself is gone (LIQ-2403), this is now the ONLY row data patched after a save -
        // see this same block's own comment further below for why a row absent here after a
        // successful save (filters changed between check and Save) simply has nothing left to
        // patch, harmlessly.
        const index = varianceDetailRows.value.findIndex((row) => rowKey(row) === key)
        if (0 > index) return

        const row = varianceDetailRows.value[index]
        const edit = pendingEdits.value[key]
        // Submit Date / Admin Date hover text (LIQ-2510) for whatever this save recorded the
        // current user as (see userStamps). A new submission is dated today, the same as the
        // backend re-dates it - applied after canEditVariance's own submission_date below.
        const stamp = userStamps.get(key) || {}
        const userFields = {
          ...(stamp.submitted
            ? { submitted_by: userName.value || null, submission_date: format(new Date(), DATE_FORMAT) }
            : {}),
          ...('set' === stamp.reviewed
            ? { reviewed_by: userName.value || null }
            : 'cleared' === stamp.reviewed
            ? { reviewed_by: null }
            : {})
        }
        // Split the same way the save sent them: an "Other" reason travels in edit.variance_type,
        // but the row keeps "Other" plus the reason, as the reconciliation service returns it.
        const isOtherReason = edit.variance_type && !VARIANCE_TYPE_SUBMIT_OPTIONS.includes(edit.variance_type)
        const fieldUpdates = {
          ...(canEditVariance.value
            ? {
                variance_type: isOtherReason ? 'Other' : edit.variance_type,
                variance_type_other_reason: isOtherReason ? edit.variance_type : '',
                submit_variance_comment: edit.submit_variance_comment,
                submission_date: edit.submission_date
              }
            : {}),
          ...(canEditAdminReview.value
            ? {
                // canEditAdminReview being true means canEditVariance is too (they share the
                // same two role branches - Superuser, or Admin with active REC status), so
                // edit.variance_type is always this row's current pending Variance Type state,
                // not just whatever canEditVariance's own half of fieldUpdates above set it to.
                // A blank Variance Type (Superuser cleared it, or it was never submitted) takes
                // priority over whatever the Admin Approval choice happens to say - 'Not
                // Submitted' is what a real row in that state looks like, same as the backend's
                // own derivation, rather than defaulting every non-Approved/Rejected choice
                // (including a full Superuser clear) to 'Pending Approval' as if someone were
                // still waiting on a decision about a variance that no longer exists.
                variance_status:
                  !edit.variance_type
                    ? 'Not Submitted'
                    : 'Approved' === edit.adminApprovalChoice
                    ? 'Approved'
                    : 'Rejected' === edit.adminApprovalChoice
                    ? 'Rejected'
                    : 'Pending Approval',
                admin_variance_comment: edit.admin_variance_comment,
                // Mirrors the same choiceChanged/commentChanged/isNewDecision/isFullyClearing
                // condition saveVarianceEdits' own payload-building loop above uses to decide
                // admin_variance_date (see its own comment) - that value only reaches the
                // backend, never back into varianceDetailRows on its own, so the Admin Date
                // column stayed blank/wrong here until a full refetch pulled the persisted value.
                ...('' === edit.adminApprovalChoice
                  ? { admin_variance_date: null }
                  : edit.adminApprovalChoice !== edit.initialAdminApprovalChoice ||
                    (edit.admin_variance_comment || '') !== (edit.initialAdminComment || '') ||
                    (!row.admin_variance_date && 'Pending' !== edit.adminApprovalChoice)
                  ? { admin_variance_date: new Date().toISOString() }
                  : {})
              }
            : {}),
          ...userFields
        }

        // Vue 2 doesn't observe plain index assignment on arrays — splice is required for
        // varianceDetailRows' own template rendering to react to this change.
        varianceDetailRows.value.splice(index, 1, { ...row, ...fieldUpdates })
      })

      if (failedKeys.size) {
        showToast(
          `Saved ${succeededKeys.size} of ${payload.length} variance row(s) — ${failedKeys.size} failed, still checked for retry.`
        )
      } else {
        showToast(`Saved ${succeededKeys.size} variance row(s).`)
      }

      // invalidKeys never reached the API at all (skipped above), but stay
      // checked with their typed edits intact for retry - same treatment as
      // failedKeys, just for a validation failure instead of a backend one.
      const staysCheckedKeys = new Set([...failedKeys, ...invalidKeys])

      checkedRowKeys.value = staysCheckedKeys
      pendingEdits.value = Object.fromEntries(
        Object.entries(pendingEdits.value).filter(([key]) => staysCheckedKeys.has(key))
      )
    }

    // Warns before actually LEAVING the page (a different route, or the browser tab closing/
    // refreshing) while a row is checked with unsaved edits - per explicit request. Filter/sort/
    // drill-down actions within the page are NOT guarded (reverted per explicit follow-up
    // decision - an earlier version called this from every one of those too, which fired the
    // warning for ordinary in-page clicks, not just for actually navigating away). beforeRouteLeave
    // below is the one real caller left: it stashes `next` (Vue Router's own callback) as the
    // retry closure, pops the 3-way modal, and returns true so navigation bails out WITHOUT
    // proceeding yet - if there's nothing checked, guardUnsavedChanges returns false immediately
    // and `next()` runs normally. warnOnBrowserUnload further below covers the tab-close/refresh
    // case, which bypasses vue-router (and this function) entirely.
    const showUnsavedChangesModal = ref(false)
    const pendingBlockedAction = ref(null)

    const guardUnsavedChanges = (retryFn) => {
      if (!checkedRowKeys.value.size) return false

      pendingBlockedAction.value = retryFn
      showUnsavedChangesModal.value = true

      return true
    }

    const runPendingBlockedAction = () => {
      const action = pendingBlockedAction.value
      pendingBlockedAction.value = null
      if (action) action()
    }

    const confirmSaveAndContinue = async () => {
      showUnsavedChangesModal.value = false
      await saveVarianceEdits()
      // A validation failure (missing Variance Type/Comment) leaves rows checked on purpose (see
      // saveVarianceEdits' own comment) - re-running the blocked action then would discard those
      // in-progress, still-invalid edits out from under the user without them ever seeing why the
      // save didn't go through. Only proceed once every checked row genuinely saved.
      if (!checkedRowKeys.value.size) runPendingBlockedAction()
      else pendingBlockedAction.value = null
    }

    const confirmDiscardAndContinue = () => {
      showUnsavedChangesModal.value = false
      discardVarianceEdits()
      runPendingBlockedAction()
    }

    const cancelUnsavedChangesModal = () => {
      showUnsavedChangesModal.value = false
      pendingBlockedAction.value = null
    }

    // Closing the tab/refreshing/typing a new URL bypasses vue-router entirely (beforeRouteLeave
    // never fires), so it needs its own guard - but browsers deliberately don't allow a custom
    // dialog here (Save/Discard/Cancel buttons, calling saveVarianceEdits, etc. are all
    // impossible) specifically to stop sites from faking a deceptive exit-intent popup. The
    // browser's own generic "Leave site? Changes may not be saved" prompt (Leave/Cancel only) is
    // the most this event can ever do - setting event.returnValue is what triggers it; the exact
    // string assigned to it is ignored by every modern browser, which show their own fixed text.
    const warnOnBrowserUnload = (event) => {
      if (!checkedRowKeys.value.size) return

      event.preventDefault()
      event.returnValue = ''
    }

    // Sums over the FULL server-side filtered set (service.variance_totals), not just whatever
    // page(s) happen to be loaded - a client-side reduce over displayedVarianceRows would only
    // total whatever's been loaded/appended so far, which is wrong once results are paginated.
    const varianceTotals = computed(() => ({
      bdx: varianceServerTotals.value.total_paid_bdx || 0,
      fcp: varianceServerTotals.value.total_paid_fcp || 0,
      variance: varianceServerTotals.value.variance_amount || 0
    }))

    // BDX Detail/Cashbook Details export used to just dump whatever was currently loaded
    // (bdxDetailRows/cashbookDetailRows - post every active server-side filter, but only as much
    // as "Load more" had fetched so far, not the full matching dataset). Now matches Variance
    // Transaction Details' own downloadVarianceExcel exactly - a real export: true fetch (see
    // downloadBdxExcel/downloadCashbookExcel below), so the export reflects every row matching the
    // current filters/search regardless of how much has actually been paged in on screen.
    // Builds a real, valid .xlsx (not export-from-json's 'xls' type, which is
    // actually an HTML table renamed with an .xls extension - Excel accepts
    // that for the legacy extension out of long-standing leniency, but flags
    // it as a format mismatch for .xlsx). Same xlsx (SheetJS) package already
    // used the same way elsewhere in this app, e.g.
    // BDXManagementTableExportButton.vue.
    const downloadExcel = (rows, sheetName, fileName, fields = null) => {
      // When the caller passes its table's own field config, use each field's on-screen label
      // as the column header (e.g. fcp_start_date -> "REC Start Date") instead of a generic
      // startCase(key) guess, which can't know an internal key doesn't match its display label.
      const labelByKey = fields
        ? fields.reduce((acc, { key, label }) => {
            acc[key] = label
            return acc
          }, {})
        : {}
      const humanizedRows = rows.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [labelByKey[key] || startCase(key), value])
        )
      )

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(humanizedRows)

      // Excel sheet names are capped at 31 characters and can't be renamed
      // after the fact without touching the sheet itself.
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31))
      XLSX.writeFile(workbook, `${fileName}.xlsx`)
    }

    // LIQ-2403: the Variance/BDX/Cashbook exports arrive as finished .xlsx files built on the
    // server - building them here from the full export JSON froze the page for seconds at
    // "select all report periods" scale. (REC UMRs' small, already-loaded list still uses
    // downloadExcel above.)
    const saveFile = (blob, fileName) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }

    // With responseType 'blob' an error body arrives as a Blob too - read it back as JSON so
    // requestErrorMessage can still show the backend's own detail message.
    const withJsonErrorBody = async (err) => {
      const body = err && err.response && err.response.data
      if (body instanceof Blob) {
        try {
          err.response.data = JSON.parse(await body.text())
        } catch (parseError) {
          err.response.data = null
        }
      }
      return err
    }

    const varianceExporting = ref(false)

    // Per explicit decision, exports every row matching the current filters, not just whatever's
    // been loaded via "Load more" so far - the server's /variance-details/export runs the same
    // full-dataset query as /variance-details in export mode (see
    // ReconciliationService.exportFCPReportV2VarianceDetails), paid only on this explicit action.
    const downloadVarianceExcel = async () => {
      if (!selectedPeriods.value.length || demoMode.value) return

      const { umr, claim, isEmpty } = effectiveUmrAndClaimForVarianceDetails()
      if (isEmpty) {
        showToast('No rows match the current filters.')
        return
      }

      varianceExporting.value = true
      try {
        // The server builds the finished workbook - columns, the Admin Approval/Source relabels
        // and money/date formats all live in app/reports/rec/excel_export.py now.
        const response = await ReconciliationService.exportFCPReportV2VarianceDetails({
          ...varianceDetailsFilterParams(),
          umr,
          claim
        })
        saveFile(response.data, 'Variance_Transaction_Details.xlsx')
      } catch (err) {
        showToast(requestErrorMessage(await withJsonErrorBody(err), 'Failed to export variance transaction details.'))
      } finally {
        varianceExporting.value = false
      }
    }

    const bdxExporting = ref(false)

    // Same "real export: true fetch" treatment as downloadVarianceExcel above, now that
    // /bdx-details supports it (see BdxCashbookDetailsRequestBase.export in router.py) - reuses
    // this table's own current sort/search state (bdxDetailsFilterParams) plus the same
    // umr/claim merge fetchBdxDetails itself uses, so the export matches exactly what's currently
    // narrowed on screen, not just what "Load More" happened to have already fetched.
    const downloadBdxExcel = async () => {
      if (!selectedPeriods.value.length || demoMode.value) return

      const { umr, claim, isEmpty } = effectiveUmrAndClaimExcluding('bdx')
      if (isEmpty) {
        showToast('No rows match the current filters.')
        return
      }

      bdxExporting.value = true
      try {
        const response = await ReconciliationService.exportFCPReportV2BdxDetails({
          ...bdxDetailsFilterParams(),
          umr,
          claim
        })
        saveFile(response.data, 'BDX_Detail_By_Transaction.xlsx')
      } catch (err) {
        showToast(requestErrorMessage(await withJsonErrorBody(err), 'Failed to export BDX detail transactions.'))
      } finally {
        bdxExporting.value = false
      }
    }

    const cashbookExporting = ref(false)

    // Cashbook counterpart to downloadBdxExcel above - same reasoning.
    const downloadCashbookExcel = async () => {
      if (!selectedPeriods.value.length || demoMode.value) return

      const { umr, claim, isEmpty } = effectiveUmrAndClaimExcluding('cashbook')
      if (isEmpty) {
        showToast('No rows match the current filters.')
        return
      }

      cashbookExporting.value = true
      try {
        const response = await ReconciliationService.exportFCPReportV2CashbookDetails({
          ...cashbookDetailsFilterParams(),
          umr,
          claim
        })
        saveFile(response.data, 'Cashbook_Details_By_Transaction.xlsx')
      } catch (err) {
        showToast(requestErrorMessage(await withJsonErrorBody(err), 'Failed to export cashbook details.'))
      } finally {
        cashbookExporting.value = false
      }
    }

    const umrExporting = ref(false)

    // REC UMRs' own rows (displayedFcpUmrList) are a plain client-side rollup, not a paginated
    // b-table like the three above - there's no "Load More" tail beyond what's already on screen,
    // so unlike downloadVarianceExcel/downloadBdxExcel/downloadCashbookExcel this needs no
    // export: true re-fetch first; what's currently displayed (respecting the panel's own sort
    // and every active filter) already IS the full matching set.
    const downloadUmrExcel = () => {
      if (!displayedFcpUmrList.value.length) {
        showToast('No rows match the current filters.')
        return
      }

      umrExporting.value = true
      try {
        // Mirrors the table's own three-icon treatment (see the tbody's check-lg/flag-fill <td>
        // in the template) as plain text, since an exported cell can't carry an icon.
        const rows = displayedFcpUmrList.value.map((row) => ({
          report_month_year: row.report_month_year,
          umr: maskUmr(row.umr),
          status: row.has_variance ? row.variance_status : 'No Variance'
        }))

        downloadExcel(rows, 'REC UMRs', 'REC_UMRs', [
          { key: 'report_month_year', label: 'Report Period' },
          { key: 'umr', label: 'UMR' },
          { key: 'status', label: 'Status' }
        ])
      } finally {
        umrExporting.value = false
      }
    }

    // Sums over the FULL server-side filtered set (service.bdx_totals/cashbook_totals), not just
    // whatever page(s) happen to be loaded - same reasoning as varianceTotals above, now that these
    // two tables are paginated too.
    const bdxTotal = computed(() => bdxServerTotals.value.total_paid_bdx || 0)
    const paidTpaFeesTotal = computed(() => bdxServerTotals.value.paid_tpa_fees || 0)
    const debitTotal = computed(() => cashbookServerTotals.value.debit || 0)
    const creditTotal = computed(() => cashbookServerTotals.value.credit || 0)

    const selectUmrFromList = (umr) => {
      selectedUmrRow.value = selectedUmrRow.value === umr ? null : umr
    }

    // CORRECTION (found via user testing, not assumed): the template previously compared the bare
    // ref directly (`selectedUmrRow === row.umr`) instead of unwrapping it
    // (`selectedUmrRow.value`), so the active-row class never actually applied even though the
    // filtering itself (which does go through selectedUmrRow.value correctly, e.g. in
    // filteredRows/fcpUmrList) worked fine. Matches the same isPartyTypeExpanded/isDcaGroupSelected
    // pattern already used elsewhere in this file for "is this specific item selected" checks,
    // rather than relying on template ref auto-unwrapping for a comparison inside an object literal.
    const isUmrRowSelected = (umr) => selectedUmrRow.value === umr

    const clearFilters = async () => {
      varianceFilter.value = null
      sourceFilter.value = null
      approvalStatusFilter.value = null
      fcpFilter.value = null
      varianceTypeFilter.value = null
      umrFilter.value = null
      accountNameFilter.value = null
      claimFilter.value = null
      dcaFilter.value = null
      selectedUmrRow.value = null
      selectedTransaction.value = null
      isPeriodFilterActive.value = false
      // Variance Transaction Details' per-column header search boxes and compound sort - same
      // "back to the untouched default" treatment as every filter above, rather than leaving
      // whatever a column search/sort click had set from before the reset.
      columnSearch.value = {}
      sortBy.value = 'variance_amount'
      sortDesc.value = true
      // Back to the grouped-by-UMR default (see varianceSortTouched's own comment) - a reset
      // should behave like a fresh, untouched page load, not silently keep whatever plain-sort
      // state a prior column click left behind.
      varianceSortTouched.value = false

      // REPORTED BUG (requests stalling on Clear Filters): watchers default to flush: 'pre', so the
      // resets above have only QUEUED their callbacks - none of them has called its debounced fetch
      // yet. Cancelling right here was a no-op: the watchers then ran at fetchReport's first await
      // below, scheduled every debounced fetch anyway, and ~400ms later a full duplicate batch fired
      // and aborted the definitive one mid-flight (the backend still finishing the abandoned
      // requests, so the ones that mattered queued behind them). Each watcher's flush was
      // registered on Vue's nextTick queue at assignment time, i.e. before this await, so by the
      // time it resumes every debounce timer is scheduled and the cancels below actually clear them.
      await nextTick()

      // Every debounced fetch the resets above triggered - ALL cancelled so none can ALSO fire,
      // 400ms from now, a second, redundant request racing with (and cancelling, via each fetch's
      // own CancelToken source) the single definitive call below. fetchPartyGroupOptionsDebounced
      // is deliberately left alone: clearing dcaFilter means the Party dropdown's own options
      // genuinely need re-fetching un-narrowed - not a duplicate of anything fired below.
      fetchReportDebounced.cancel()
      fetchVarianceDetailsDebounced.cancel()
      fetchVarianceFilterOptionsDebounced.cancel()
      fetchRecUmrRollupDebounced.cancel()
      fetchBdxDetailsDebounced.cancel()
      fetchCashbookDetailsDebounced.cancel()

      // Restores the same period(s) a fresh page load resolves, not "every period selected" -
      // per explicit request. Cached (initialDefaultPeriods) so this only ever asks the backend
      // for its own default ONCE per session (a normal, direct assignment the very next time -
      // suppressNextPeriodsFetch stops the watcher above from ALSO firing its own redundant
      // fetch once selectedPeriods changes here, same as fetchReport's own response-handling
      // already relies on elsewhere). Not resolved yet this session (see fetchReport's own
      // forceDefaultPeriods comment)? Ask for it directly there instead of guessing "every
      // period" or burning a separate throwaway request purely to learn the answer before firing
      // the real request a moment later.
      if (initialDefaultPeriods) {
        suppressNextPeriodsFetch = true
        selectedPeriods.value = [...initialDefaultPeriods]
        await fetchReport()
      } else {
        await fetchReport({ forceDefaultPeriods: true })
      }
    }

    // Erasers on the top filters: each resets just that one filter to its "All" state - the same
    // value clearFilters above sets it to. The filter's own watcher does the refetch, exactly as if
    // the user had unticked everything by hand. Report Period (always needs a selection) and the
    // Variance/FCP toggles deliberately have no eraser.
    const TOP_FILTERS = {
      party: dcaFilter,
      source: sourceFilter,
      reviewStatus: approvalStatusFilter,
      umr: umrFilter,
      claim: claimFilter,
      accountName: accountNameFilter
    }
    const isTopFilterActive = (name) => {
      const value = TOP_FILTERS[name].value
      return Array.isArray(value) ? value.length > 0 : null !== value && undefined !== value
    }
    const clearTopFilter = (name) => {
      TOP_FILTERS[name].value = null
    }

    // Report Period can't be emptied (it always needs a selection), so "clearing" it means going back
    // to the default periods - the same reset clearFilters above does for it.
    const resetReportPeriods = () => {
      isPeriodFilterActive.value = false
      if (initialDefaultPeriods) selectedPeriods.value = [...initialDefaultPeriods]
      else fetchReport({ forceDefaultPeriods: true })
    }

    // A table's eraser clears every filter set from that table's column headers: its own column
    // searches, plus the shared filters its headers also show (keys of TOP_FILTERS above, and Report
    // Period, which every table's header has). Those shared ones are the same state as the top filters,
    // so clearing them here clears the matching top filter too and changes every table.
    const SHARED_HEADER_FILTERS = {
      variance: ['umr', 'claim', 'accountName', 'source', 'reviewStatus'],
      bdx: ['umr', 'claim', 'accountName'],
      cashbook: ['umr', 'claim', 'accountName'],
      recUmrs: ['umr', 'reviewStatus']
    }
    const isSharedHeaderFilterActive = (table) =>
      isPeriodFilterActive.value || SHARED_HEADER_FILTERS[table].some((name) => isTopFilterActive(name))
    const clearSharedHeaderFilters = (table) => {
      if (isPeriodFilterActive.value) resetReportPeriods()
      SHARED_HEADER_FILTERS[table].forEach((name) => clearTopFilter(name))
    }

    const isVarianceTableFilterActive = computed(
      () =>
        isSharedHeaderFilterActive('variance') ||
        isVarianceTypeFilterActive.value ||
        Object.keys(columnSearch.value).some(isColumnSearchActive)
    )
    const clearVarianceTableFilters = () => {
      clearSharedHeaderFilters('variance')
      columnSearch.value = {}
      varianceTypeFilter.value = null
    }
    const isBdxTableFilterActive = computed(
      () => isSharedHeaderFilterActive('bdx') || Object.keys(bdxColumnSearch.value).some(isBdxColumnSearchActive)
    )
    const clearBdxTableFilters = () => {
      clearSharedHeaderFilters('bdx')
      bdxColumnSearch.value = {}
    }
    const isCashbookTableFilterActive = computed(
      () =>
        isSharedHeaderFilterActive('cashbook') ||
        Object.keys(cashbookColumnSearch.value).some(isCashbookColumnSearchActive)
    )
    const clearCashbookTableFilters = () => {
      clearSharedHeaderFilters('cashbook')
      cashbookColumnSearch.value = {}
    }
    const isRecUmrsTableFilterActive = computed(() => isSharedHeaderFilterActive('recUmrs'))
    const clearRecUmrsTableFilters = () => {
      clearSharedHeaderFilters('recUmrs')
    }

    // Mirror of every filter/sort ref above, serialized back to the URL so the
    // current view can be bookmarked/shared - the read side is initialQuery at
    // the top of setup(). A key is only included when it differs from that
    // filter's own default/"no restriction" state, so an untouched report has a
    // clean URL with no query string at all. Array-valued filters rely on
    // vue-router's own repeated-key stringification (?umr=A&umr=B) - this is
    // purely the browser's own address bar, entirely separate from how these
    // same values reach the backend (a JSON POST body, not query params - see
    // getFCPReportV2Production's own comment in reconciliationService.js), so
    // this can keep using repeated keys for shareability even though the
    // request itself doesn't - see parseArrayQueryParam above for the read
    // side's counterpart.
    //
    // Several of these filters (periods/status/type/umr/account/claim/dca) use `[]`
    // to mean "every checkbox explicitly unchecked", distinct from `null`
    // ("untouched"/no restriction) - both serialize to the same "key absent
    // entirely" URL (an empty array has no entries to repeat the key for), so
    // that distinction doesn't survive a reload from a bookmarked link. Judged
    // not worth a sentinel value for how rarely "explicitly filter to zero
    // rows" is itself the state someone wants to bookmark.
    const urlQuery = computed(() => {
      const query = {}

      if (!allPeriodsSelected.value) query.periods = selectedPeriods.value
      if (null !== varianceFilter.value) query.variance = varianceFilter.value ? 'yes' : 'no'
      if (null !== fcpFilter.value) query.fcp = fcpFilter.value ? 'yes' : 'no'
      if (null !== sourceFilter.value) query.source = sourceFilter.value
      if (approvalStatusFilter.value && approvalStatusFilter.value.length) {
        query.status = approvalStatusFilter.value
      }
      if (varianceTypeFilter.value && varianceTypeFilter.value.length) {
        query.type = varianceTypeFilter.value
      }
      // Demo Mode (see its own comment further below) - these five all carry a real UMR/Claim/
      // Account Name/DCA Party group name straight into the address bar, the one place masking
      // the on-screen table/dropdowns/exports wouldn't otherwise reach (reported live: a real UMR
      // was still visible in the URL with demo mode on). Suppressed entirely rather than written
      // out masked - a masked value round-tripped back through initialQuery on a reload/bookmark
      // would seed the filter with a fake value matching no real row, silently breaking it;
      // dropping the key instead just reloads to "no restriction" on this one filter, same as
      // demoMode itself already not surviving a reload (see its own comment).
      if (umrFilter.value && umrFilter.value.length && !demoMode.value) query.umr = umrFilter.value
      if (accountNameFilter.value && accountNameFilter.value.length && !demoMode.value) {
        query.account = accountNameFilter.value
      }
      if (claimFilter.value && claimFilter.value.length && !demoMode.value) query.claim = claimFilter.value
      if (dcaFilter.value && dcaFilter.value.length && !demoMode.value) query.dca = dcaFilter.value
      if (selectedUmrRow.value && !demoMode.value) query.umr_row = selectedUmrRow.value
      if ('variance_amount' !== sortBy.value) query.sort_by = sortBy.value
      if (!sortDesc.value) query.sort_dir = 'asc'

      Object.entries(columnSearch.value).forEach(([key, value]) => {
        if (RANGE_FILTER_FIELD_KEYS.includes(key)) {
          if (value && value.min) query[`${SEARCH_QUERY_PREFIX}${key}_min`] = value.min
          if (value && value.max) query[`${SEARCH_QUERY_PREFIX}${key}_max`] = value.max
        } else if (DATE_RANGE_FILTER_FIELD_KEYS.includes(key)) {
          if (value && value.from) query[`${SEARCH_QUERY_PREFIX}${key}_from`] = value.from
          if (value && value.to) query[`${SEARCH_QUERY_PREFIX}${key}_to`] = value.to
        } else if (value) {
          query[`${SEARCH_QUERY_PREFIX}${key}`] = value
        }
      })

      return query
    })

    // router.replace (not push) so tweaking a filter doesn't spam the browser's
    // back button with one history entry per checkbox click - only the final
    // state before navigating away needs to be reachable. Duplicate-navigation
    // rejections (replacing with the exact query already present, e.g. right
    // after initialQuery seeded these same refs) are expected and harmless.
    watch(urlQuery, (query) => {
      router.replace({ query }).catch(() => {})
    })

    // ---- Demo Mode (superuser only - see the toolbar toggle below) ----
    // Replaces every UMR/Claim Reference/Account Name/dollar figure on this page with a made-up
    // substitute, for showing the report live without exposing real client data. Per explicit
    // decision: randomly generated but session-consistent - the first time a real value is seen,
    // its fake replacement is generated and cached for the rest of the page's lifetime (not
    // derived mathematically from the real value), so the same real UMR/Claim/Account/amount
    // shows the same fake one everywhere: all four tables, every dropdown, and Excel exports.
    // Covers the whole dataset matching the current filters, not just what's loaded on screen -
    // the checklist dropdowns already list every matching value via the server-side filter-
    // options endpoints (independent of "Load More" pagination), so masking those same labels
    // naturally covers values not yet scrolled into view too.
    const demoMode = ref(false)

    // Plain (non-reactive) caches, not refs - same reasoning as RecColumnSearchMenu.vue's own
    // triggerEl/anchorRect: nothing renders directly off these Maps existing/growing, only off
    // demoMode itself, and their own identity never needs to trigger a re-render.
    const umrFakeMap = new Map()
    const claimFakeMap = new Map()
    const accountNameFakeMap = new Map()
    const limeiqKeyFakeMap = new Map()
    const amountFakeMap = new Map()

    const randomDigits = (count) => Array.from({ length: count }, () => Math.floor(10 * Math.random())).join('')
    const randomLetters = (count) =>
      Array.from({ length: count }, () => String.fromCharCode(65 + Math.floor(26 * Math.random()))).join('')

    // Mimics the real UMR/Claim Reference shape (e.g. "B0142AA2103217" / "BAM-0000164") closely
    // enough to read as plausible at a glance, without being derived from the real value at all.
    const generateFakeUmr = () => `${randomLetters(1)}${randomDigits(4)}${randomLetters(2)}${randomDigits(7)}`
    const generateFakeClaim = () => `${randomLetters(3)}-${randomDigits(7)}`
    // BDX Detail's own LimeIQ Key column (see BDX_HEADER_SEARCH_FIELD_KEYS) - no fixed real-world
    // format documented backend-side, so this just reads as a plausible generic key.
    const generateFakeLimeiqKey = () => `${randomLetters(2)}${randomDigits(8)}`

    // Per explicit request - plain random digits rather than a made-up business name.
    const generateFakeAccountName = () => randomDigits(8)

    // Shared cache-or-generate: passes the real value through untouched whenever demo mode is
    // off (or there's nothing to mask), so every call site stays correct regardless of demoMode.
    const maskWith = (map, real, generate) => {
      if (!demoMode.value || !real) return real

      if (!map.has(real)) map.set(real, generate())
      return map.get(real)
    }

    const maskUmr = (real) => maskWith(umrFakeMap, real, generateFakeUmr)
    const maskClaim = (real) => maskWith(claimFakeMap, real, generateFakeClaim)
    const maskAccountName = (real) => maskWith(accountNameFakeMap, real, generateFakeAccountName)
    const maskLimeiqKey = (real) => maskWith(limeiqKeyFakeMap, real, generateFakeLimeiqKey)

    // Party/DCA flyout's own Group Names (see dcaFlyoutGroup.names in the template) - per
    // explicit request, a sequential "<PartyType> N" label (e.g. "Underwriter 1", "TPA 1",
    // "TPA 2", ...) instead of maskAccountName's random-digit treatment, numbered separately per
    // party type. partyGroupCounters tracks the next number to hand out for each party type;
    // partyGroupFakeMap caches the assignment per (partyType, real name) pair, keyed by the same
    // composite key the template's own :key/isDcaGroupSelected already use, since the same real
    // name could in principle appear under more than one party type.
    const partyGroupFakeMap = new Map()
    const partyGroupCounters = new Map()
    const maskPartyGroupName = (partyType, real) => {
      if (!demoMode.value || !real) return real

      const cacheKey = `${partyType}::${real}`
      if (!partyGroupFakeMap.has(cacheKey)) {
        const next = (partyGroupCounters.get(partyType) || 0) + 1
        partyGroupCounters.set(partyType, next)
        partyGroupFakeMap.set(cacheKey, `${partyType} ${next}`)
      }
      return partyGroupFakeMap.get(cacheKey)
    }

    // Amounts key the cache off the real NUMBER (not a string) - two different rows sharing the
    // exact same real figure (e.g. both $0.00, common for "no variance" rows) get the same
    // cached fake figure, which is fine/expected for a demo. Scaled to a random 0.5x-1.5x
    // multiple of the real magnitude (not a fixed range), so a small figure doesn't turn into a
    // wildly large fake one or vice versa - still just as fake, but doesn't look absurd next to
    // real-shaped totals.
    const maskAmount = (real) => {
      if (!demoMode.value) return real

      const amount = Number(real) || 0
      if (!amountFakeMap.has(amount)) amountFakeMap.set(amount, amount * (0.5 + Math.random()))
      return amountFakeMap.get(amount)
    }

    // Dispatches to the right mask*() by field key - shared by Variance/BDX/Cashbook's own
    // generic #cell() fallback slot below (none of the three has a dedicated #cell(umr)/
    // #cell(claim)/#cell(account_name) override of its own, so this is the one place each
    // table's on-screen cell for these three columns actually renders).
    const maskCellValue = (fieldKey, value) => {
      if ('umr' === fieldKey) return maskUmr(value)
      if ('claim' === fieldKey) return maskClaim(value)
      if ('account_name' === fieldKey) return maskAccountName(value)
      if ('limeiq_key' === fieldKey) return maskLimeiqKey(value)
      // BDX's TPA/Coverholder columns hold the same party names the Party dropdown lists, so they're
      // masked under the dropdown's own party types (DCA_PARTY_TYPE_ORDER) - the same real TPA gets
      // the same fake name ("TPA 3") in both places.
      if ('tpa' === fieldKey) return maskPartyGroupName('TPA', value)
      if ('coverholder' === fieldKey) return maskPartyGroupName('Coverholder', value)
      return value
    }

    const formatCurrency = (value) => {
      const amount = Number(value) || 0
      const formatted = Math.abs(amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

      return 0 > amount ? `(${formatted})` : formatted
    }

    // Per explicit request - Total Paid (BDX)/(REC)/Variance, Total Paid/Paid TPA Fees, and
    // Debit/Credit stay real even in Demo Mode; only UMR/Claim Reference/Account Name/LimeIQ Key
    // (plus the REC Accounts KPI, still routed through maskAmount directly) get masked.
    const formatCurrencyNoSymbol = (value) => {
      const amount = Number(value) || 0
      const formatted = Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })

      return 0 > amount ? `(${formatted})` : formatted
    }

    const formatDate = (value) => (value ? format(value, 'MM/DD/YYYY') : '')
    const formatDateTime = (value) => (value ? format(value, 'MM/DD/YYYY h:mm A') : '')

    // Submit Date / Admin Date hover text (LIQ-2510): who submitted the variance and who reviewed
    // it. Plain title attributes, not a tooltip component - nothing runs on mouseenter, so
    // scrolling the grid past these cells costs nothing. Built from the row's SAVED values only,
    // and none at all until there's a saved date - or while the reconciliation service doesn't
    // return submitted_by/reviewed_by yet (missing, as opposed to null for "not recorded").
    const recordedUser = (name) => (name ? maskPartyGroupName('User', name) : 'User not recorded')

    const submitDateTitle = (row) =>
      row.submission_date && undefined !== row.submitted_by ? `Submitted by: ${recordedUser(row.submitted_by)}` : null

    const adminDateTitle = (row) => {
      if (!row.admin_variance_date || undefined === row.reviewed_by) return null

      const action =
        'Approved' === row.variance_status ? 'Approved' : 'Rejected' === row.variance_status ? 'Rejected' : 'Reviewed'
      return `${action} by: ${recordedUser(row.reviewed_by)}`
    }

    const CURRENCY_FIELD_KEYS = [
      'total_paid_bdx',
      'total_paid_fcp',
      'variance_amount',
      'paid_tpa_fees',
      'debit',
      'credit'
    ]
    const DATE_FIELD_KEYS = ['submission_date', 'fcp_start_date', 'admin_variance_date']
    // Shown as their own columns already — left out of the row-hover tooltip
    // specifically so it doesn't repeat the same figures.
    const TOOLTIP_EXCLUDED_FIELD_KEYS = ['total_paid_bdx', 'total_paid_fcp', 'variance_amount']

    // Backs the Variance Transaction Details row-hover tooltip (BDX/Cashbook
    // don't have it) — formats a field's value the same way its own #cell()
    // template would, so the tooltip matches what's rendered in the table.
    const hoveredRowTooltip = ref(null)
    // The row currently under the cursor, kept around so mousemove can rebuild
    // the tooltip as the cursor crosses into/out of the BDX/FCP/Variance
    // columns without waiting for a fresh row-hovered (which only fires once
    // per row, not per cell).
    const hoveredRowItem = ref(null)

    // Disabled for now, per explicit decision - even leading-edge-debounced, a stationary cursor
    // still triggers a mouseenter/mouseleave (and so a showRowTooltip call) on every row that
    // scrolls past it, and that was still enough of a per-render cost on this heavily-templated
    // table to be noticeable. Not removed - the tooltip's own logic/template stay fully intact
    // below, just gated off. Flip to true to re-enable.
    const rowTooltipEnabled = false

    const formatTooltipFieldValue = (row, field) => {
      const rawValue = row[field.key]

      if (null === rawValue || undefined === rawValue || '' === rawValue) {
        return { text: '(Blank)', isBlank: true }
      }

      if (CURRENCY_FIELD_KEYS.includes(field.key)) return { text: formatCurrency(rawValue), isBlank: false }
      if (DATE_FIELD_KEYS.includes(field.key)) return { text: formatDate(rawValue), isBlank: false }

      return { text: String(rawValue), isBlank: false }
    }

    // Column under the cursor decides which one (if any) of the otherwise-
    // excluded BDX/FCP/Variance fields gets included this time — hovering
    // Total Paid (BDX) shows only that one alongside the rest, not all three.
    const fieldUnderCursor = (event) => {
      const td = event.target.closest('td')
      const colIndex = td ? Array.prototype.indexOf.call(td.parentElement.children, td) : -1

      return -1 < colIndex ? varianceFields.value[colIndex] : null
    }

    const tooltipSpecialFieldKey = (event) => {
      const field = fieldUnderCursor(event)

      return field && TOOLTIP_EXCLUDED_FIELD_KEYS.includes(field.key) ? field.key : null
    }

    // These columns are either directly editable inline (variance_type,
    // submit_variance_comment) or already show their full value in the cell
    // itself — the row-summary tooltip doesn't add anything there and can
    // get in the way (e.g. covering the Other free-text box while typing).
    const TOOLTIP_SUPPRESSED_FIELD_KEYS = [
      'variance_type',
      'submission_date',
      'submit_variance_comment',
      'admin_approval',
      'admin_variance_comment',
      'admin_variance_date'
    ]

    const renderRowTooltip = (item, event) => {
      const hoveredField = fieldUnderCursor(event)

      if (hoveredField && TOOLTIP_SUPPRESSED_FIELD_KEYS.includes(hoveredField.key)) {
        hoveredRowTooltip.value = null
        return
      }

      const specialFieldKey = tooltipSpecialFieldKey(event)

      hoveredRowTooltip.value = {
        x: event.clientX + 12,
        y: event.clientY + 12,
        entries: varianceFields.value
          .filter((field) => '__select' !== field.key)
          .filter((field) => !TOOLTIP_EXCLUDED_FIELD_KEYS.includes(field.key) || field.key === specialFieldKey)
          .map((field) => ({ label: field.label, ...formatTooltipFieldValue(item, field) }))
      }
    }

    // CORRECTION (found via a real Performance profile, not assumed): the actual freeze is
    // caused by @row-hovered (native mouseenter), not @mousemove - confirmed by a profile showing
    // ~5.8s of "Scripting" attributed directly to a "mouseenter" event, with Rendering at ~0.
    // mouseenter/mouseleave fire even with a completely STATIONARY cursor whenever the DOM
    // element underneath it changes - which happens continuously while scrolling, as rows shift
    // past a fixed pointer position. Every crossing called showRowTooltip, whose reactive writes
    // (hoveredRowItem/hoveredRowTooltip) force a full re-render of this whole heavily-templated
    // table (many conditional per-cell selects/inputs). This is also why cutting the page size
    // 200 -> 50 made no difference: the cost is per row crossed under the cursor during the
    // scroll gesture, not per row in the underlying array, which stays roughly constant
    // regardless of total row count. Leading-edge debounce (fires immediately on the first call,
    // then ignores repeats for 150ms) rather than trailing-edge - a genuine single hover shows
    // the tooltip instantly (no perceptible delay), while a burst of row-crossings from scrolling
    // still only pays for one render per 150ms window instead of one per row crossed.
    const showRowTooltip = debounce(
      (item, event) => {
        if (!rowTooltipEnabled) return
        hoveredRowItem.value = item
        renderRowTooltip(item, event)
      },
      150,
      { leading: true, trailing: false }
    )

    // Throttled - @mousemove.native fires on every raw pointer-move event (can easily be 60+/sec)
    // for a cursor that's actually moving (unlike mouseenter/mouseleave above, this one needs
    // real motion) - kept throttled as a reasonable precaution even though the profile pointed at
    // mouseenter as the actual cause here, not this handler.
    const moveRowTooltip = throttle((event) => {
      if (!rowTooltipEnabled) return
      if (hoveredRowItem.value) renderRowTooltip(hoveredRowItem.value, event)
    }, 50)

    const hideRowTooltip = () => {
      showRowTooltip.cancel()
      hoveredRowTooltip.value = null
      hoveredRowItem.value = null
    }

    // The shared page layout (.router-view, outside this component) centers its
    // content with align-items-center, which leaves a big empty gap above/below
    // this report instead of letting it fill the page. Tag it for the duration
    // this component is mounted so a global style (below) can override just that.
    //
    // reportShell (.router-view's own direct parent - the App.vue-level flex/overlay wrapper
    // that also contains AppNavbar/MaintenanceBanner/AppFooter as siblings) gets a second tag
    // for the same duration - this used to be handled by calcReportHeight(), which measured
    // document.body/#main-navbar/#main-footer with getElementById(...).clientHeight once on
    // mount and froze the result as this component's own inline pixel height. That one-shot JS
    // measurement doesn't track window resizes (no resize listener ever re-ran it), and - the
    // reason it's gone now - is inherently sensitive to sub-pixel/font-metric differences
    // between browser engines: the exact same page measured in Chrome vs Edge could freeze a
    // slightly different pixel number, which is what was causing the reported cross-browser
    // misalignment (content sitting differently, more of .fcp-report__backdrop's own gradient
    // showing through). Tagging reportShell as a flex column (see the global style below) instead
    // lets the browser's own flex algorithm size .router-view to exactly fill the leftover space
    // between the navbar/footer - recomputed natively on every resize/zoom/font load, with no
    // JS measurement or frozen number involved at all.
    let routerView = null
    let reportShell = null

    onMounted(() => {
      // All four of these used to be sequenced (await fetchMyRole, then await
      // fetchReportPeriodRange, only then fire fetchReport/fetchPartyGroupOptions) on the
      // assumption that /production, /report-period-range, and /party-groups needed roleScope's
      // role/role_value as request params. Verified directly against the live router.py function
      // signatures (not just docstrings) that none of them take role/role_value at all anymore -
      // decision 8 (docs/rec-report-rls-replication.md) moved every rec data endpoint to
      // resolving access scope itself, server-side, from the caller's JWT. So none of these three
      // actually depend on fetchMyRole resolving first - fired together instead, each starting its
      // own round trip immediately rather than queued behind /my-role's.
      //
      // fetchMyRole itself still runs (roleScope still matters client-side - leadFcpKeys/
      // canEditAdminReview - and GET /my-role is still how the frontend discovers it), just not as
      // a blocking prerequisite for the other three anymore.
      //
      // fetchReport's own remaining dependency (knowing whether to omit report_periods and let the
      // backend pick its own default vs. send an explicit selection) is handled inside fetchReport
      // itself now (periodsNotYetResolved) rather than by waiting here.
      fetchMyRole()
      fetchReportPeriodRange()
      fetchReport()
      // LIQ-2405: the Party lists depend on the selected periods. When the URL named none, they
      // aren't known yet - the selectedPeriods watcher fetches the lists once fetchReport's
      // default-period echo (or fetchReportPeriodRange's seeding) sets them, rather than this
      // first fetching an every-period list that would just be replaced.
      if (selectedPeriods.value.length) fetchPartyGroupOptions()
      fetchAccountNameSummary()

      routerView = document.querySelector('.router-view')
      if (routerView) routerView.classList.add('fcp-report-active')

      // See reportShell's own comment above - its direct parent, tagged the same way routerView
      // itself is just above.
      reportShell = routerView && routerView.parentElement
      if (reportShell) reportShell.classList.add('fcp-report-shell-active')

      // Portal: see dcaFlyoutRef's own comment for why this needs to live at
      // <body>, not inside the DCA dropdown menu.
      if (dcaFlyoutRef.value) document.body.appendChild(dcaFlyoutRef.value)

      // See variancePanelRef's own comment above - tracks Variance Transaction Details' real
      // rendered height (window resize, rows loading in/out, filters changing row count, ...) so
      // Report Period's max-height binding stays in sync rather than a one-off measurement taken
      // at mount.
      if (variancePanelRef.value && 'ResizeObserver' in window) {
        variancePanelResizeObserver = new ResizeObserver((entries) => {
          // 0 = hidden behind the Account Name Reconciliation panel (LIQ-2571) - keep the last
          // real height rather than collapse Report Period.
          const height = entries[0].contentRect.height
          if (height > 0) variancePanelHeight.value = height
        })
        variancePanelResizeObserver.observe(variancePanelRef.value)
      }

      // See warnOnBrowserUnload's own comment - beforeRouteLeave (in-app navigation) and this
      // (leaving the browser tab/window entirely) are two genuinely separate escape hatches, so
      // both need their own guard.
      window.addEventListener('beforeunload', warnOnBrowserUnload)

      // See openDropdownVm's own comment above - tracks whichever column-header checklist
      // <b-dropdown> is currently open (via bootstrap-vue's own root event bus) so
      // trackOpenDropdownPosition's rAF loop knows which one to keep following.
      proxy.$root.$on('bv::dropdown::shown', onBvDropdownShown)
      proxy.$root.$on('bv::dropdown::hidden', onBvDropdownHidden)
    })

    onBeforeUnmount(() => {
      if (routerView) routerView.classList.remove('fcp-report-active')
      if (reportShell) reportShell.classList.remove('fcp-report-shell-active')

      if (dcaFlyoutRef.value && document.body.contains(dcaFlyoutRef.value)) {
        document.body.removeChild(dcaFlyoutRef.value)
      }

      if (variancePanelResizeObserver) variancePanelResizeObserver.disconnect()
      if (accountNamePanelResizeObserver) accountNamePanelResizeObserver.disconnect()

      proxy.$root.$off('bv::dropdown::shown', onBvDropdownShown)
      proxy.$root.$off('bv::dropdown::hidden', onBvDropdownHidden)
      if (null !== openDropdownRafId) cancelAnimationFrame(openDropdownRafId)

      window.removeEventListener('beforeunload', warnOnBrowserUnload)

      // Cancel anything still in flight - navigating away shouldn't leave orphaned requests
      // running (each would otherwise still occupy a browser connection slot, and their
      // responses, if they ever arrived, would find every ref they'd write to already torn down).
      const pendingCancelSources = [
        myRoleCancelSource,
        reportPeriodRangeCancelSource,
        productionCancelSource,
        partyGroupsCancelSource,
        varianceDetailsCancelSource,
        varianceFilterOptionsCancelSource,
        recUmrRollupCancelSource,
        bdxDetailsCancelSource,
        cashbookDetailsCancelSource
      ]
      pendingCancelSources.forEach((source) => source && source.cancel())
    })

    return {
      canReconcileAccountNames,
      accountNamePanelOpen,
      isAccountNamePanelShown,
      accountNameOutstandingCount,
      accountNameToggleTitle,
      accountNamePanelRef,
      reportPeriodMaxHeight,
      toggleAccountNamePanel,
      onAccountNamesCounted,
      loading,
      isColdHit,
      error,
      generatedAt,
      filterAppliedIcon,

      selectedPeriods,
      noReportPeriodSelected,
      varianceFilterOptionsLoading,
      varianceFilter,
      setVarianceFilter,
      advanceVarianceFilter,
      sourceFilter,
      varianceToggleLabel,
      sourceDropdownLabel,
      sourceFilterMenuOptions,
      setSourceFilter,
      isSourceFilterActive,
      isBdxSourceOptionAvailable,
      isCashbookSourceOptionAvailable,
      isBothSourceOptionAvailable,
      fcpToggleEnabled,
      fcpFilter,
      setFcpFilter,
      advanceFcpFilter,
      fcpToggleLabel,
      reviewStatusOptions,
      isReviewStatusSelected,
      allReviewStatusSelected,
      toggleAllReviewStatus,
      reviewStatusDropdownLabel,
      toggleReviewStatus,
      approvalStatusFilter,
      isApprovalStatusFilterActive,
      statusFilterMenuRef,
      varianceTypeFilter,
      umrFilter,
      accountNameFilter,
      claimFilter,
      selectedUmrRow,
      columnStyle,
      startColumnResize,

      HEADER_SEARCH_SORT_FIELD_KEYS,
      RANGE_FILTER_FIELD_KEYS,
      DATE_RANGE_FILTER_FIELD_KEYS,
      columnSearchTypeFor,
      isColumnSearchActive,
      varianceRangeBounds,
      columnSearch,
      setColumnSearch,
      columnSearchMenuRef,
      sortBy,
      sortDesc,
      toggleColumnSort,

      BDX_HEADER_SEARCH_FIELD_KEYS,
      bdxColumnSearch,
      setBdxColumnSearch,
      bdxColumnSearchMenuRef,
      bdxColumnSearchTypeFor,
      isBdxColumnSearchActive,
      BDX_CHECKLIST_FIELD_KEYS,
      bdxChecklistSearch,
      filteredBdxChecklistOptions,
      isBdxChecklistSelected,
      allBdxChecklistSelected,
      toggleAllBdxChecklist,
      toggleBdxChecklist,
      selectAllFilteredBdxChecklist,
      bdxRangeBounds,
      bdxSortBy,
      bdxSortDesc,
      toggleBdxColumnSort,

      CASHBOOK_HEADER_SEARCH_FIELD_KEYS,
      cashbookColumnSearch,
      setCashbookColumnSearch,
      cashbookColumnSearchMenuRef,
      cashbookColumnSearchTypeFor,
      isCashbookColumnSearchActive,
      cashbookRangeBounds,
      cashbookSortBy,
      cashbookSortDesc,
      toggleCashbookColumnSort,
      isBdxPeriodDropdownYearExpanded,
      toggleBdxPeriodDropdownYearExpanded,
      isCashbookPeriodDropdownYearExpanded,
      toggleCashbookPeriodDropdownYearExpanded,

      varianceFields,
      bdxFields,
      cashbookFields,

      periodTree,
      expandedYears,
      isYearExpanded,
      toggleYearExpanded,
      periodDropdownExpandedYears,
      isPeriodDropdownYearExpanded,
      togglePeriodDropdownYearExpanded,
      allPeriodsSelected,
      isPeriodFilterActive,
      isYearSelected,
      isYearPartiallySelected,
      toggleAllPeriods,
      toggleYear,
      togglePeriod,

      varianceTypeOptions,
      varianceTypeSearch,
      filteredVarianceTypeOptions,
      isVarianceTypeSelected,
      allVarianceTypesSelected,
      toggleAllVarianceTypes,
      toggleVarianceType,
      selectAllFilteredVarianceTypes,
      isVarianceTypeFilterActive,
      umrOptions,
      umrSearch,
      filteredUmrOptions,
      isUmrSelected,
      allUmrsSelected,
      toggleAllUmrs,
      umrDropdownLabel,
      toggleUmr,
      selectAllFilteredUmrs,
      isUmrFilterActive,
      accountNameOptions,
      accountNameSearch,
      filteredAccountNameOptions,
      isAccountNameSelected,
      allAccountNamesSelected,
      toggleAllAccountNames,
      accountNameDropdownLabel,
      toggleAccountName,
      selectAllFilteredAccountNames,
      isAccountNameFilterActive,
      claimOptions,
      claimSearch,
      filteredClaimOptions,
      displayedClaimOptions,
      hiddenClaimCount,
      isClaimSelected,
      allClaimsSelected,
      toggleAllClaims,
      claimDropdownLabel,
      toggleClaim,
      selectAllFilteredClaims,
      isClaimFilterActive,
      dcaSearch,
      filteredDcaPartyGroups,
      isDcaGroupSelected,
      dcaFlyout,
      dcaFlyoutRef,
      variancePanelRef,
      variancePanelHeight,
      dcaFlyoutGroup,
      isPartyTypeExpanded,
      togglePartyTypeFlyout,
      closeDcaFlyout,
      resetChecklistSearches,
      isPartyTypeSelected,
      isPartyTypePartiallySelected,
      togglePartyType,
      allDcaGroupsSelected,
      toggleAllDcaGroups,
      dcaDropdownLabel,
      toggleDcaGroup,
      selectAllFilteredDcaGroups,
      dcaSectionLabel,
      roleScope,
      isSuperUser,
      isDemoModeAvailable,
      demoMode,
      maskCellValue,
      maskUmr,
      maskClaim,
      maskAccountName,
      maskPartyGroupName,
      fcpAccountCount,
      displayedFcpAccountCount,
      fcpUmrList,
      recUmrLoading,
      displayedFcpUmrList,
      renderedFcpUmrList,
      onRecUmrListScroll,
      umrPanelSortBy,
      umrPanelSortDesc,
      toggleUmrPanelSort,
      umrPanelPeriodDropdownExpandedYears,
      isUmrPanelPeriodDropdownYearExpanded,
      toggleUmrPanelPeriodDropdownYearExpanded,
      displayedVarianceRows,
      varianceTotals,
      varianceTotalRows,
      varianceLoadingInitial,
      varianceLoadingMore,
      varianceExporting,
      hasMoreVarianceDetails,
      varianceLoadMoreCount,
      loadMoreVarianceDetails,

      VARIANCE_TYPE_SUBMIT_OPTIONS,
      VARIANCE_TYPE_MENU_OPTIONS,
      ADMIN_APPROVAL_MENU_OPTIONS,
      ADMIN_APPROVAL_FILTER_DISPLAY_OPTIONS,
      DEMO_EXPORT_DISABLED_TITLE,
      adminApprovalMenuOptions,
      inlineOptionsMenuRef,
      canEditVariance,
      canEditAdminReview,
      rowKey,
      checkedRowKeys,
      isEditLocked,
      dropdownPopperOpts,
      isRowCheckable,
      isRowEditableBySubmitter,
      isRowEditableByAdmin,
      toggleCheckedRow,
      allVisibleRowsChecked,
      toggleAllCheckedRows,
      varianceTypeChoice,
      varianceTypeOtherText,
      pendingComment,
      pendingSubmissionDate,
      isVarianceTypeInvalid,
      isCommentInvalid,
      onVarianceTypeChange,
      onVarianceTypeOtherInput,
      onCommentInput,
      adminApprovalChoice,
      pendingAdminComment,
      pendingAdminDate,
      isAdminCommentInvalid,
      onAdminApprovalChange,
      onAdminCommentInput,
      discardVarianceEdits,
      saveVarianceEdits,
      savingVarianceEdits,
      showUnsavedChangesModal,
      guardUnsavedChanges,
      confirmSaveAndContinue,
      confirmDiscardAndContinue,
      cancelUnsavedChangesModal,
      bdxDetailRows,
      cashbookDetailRows,
      bdxTotalRows,
      cashbookTotalRows,
      bdxLoadingInitial,
      cashbookLoadingInitial,
      bdxLoadingMore,
      cashbookLoadingMore,
      hasMoreBdxDetails,
      hasMoreCashbookDetails,
      bdxLoadMoreCount,
      cashbookLoadMoreCount,
      loadMoreBdxDetails,
      loadMoreCashbookDetails,
      downloadVarianceExcel,
      downloadBdxExcel,
      downloadCashbookExcel,
      downloadUmrExcel,
      bdxExporting,
      cashbookExporting,
      umrExporting,
      bdxTotal,
      paidTpaFeesTotal,
      debitTotal,
      creditTotal,

      selectUmrFromList,
      isUmrRowSelected,
      handleTransactionRowClick,
      isTransactionRowSelected,
      clearFilters,
      isTopFilterActive,
      clearTopFilter,
      isVarianceTableFilterActive,
      clearVarianceTableFilters,
      isBdxTableFilterActive,
      clearBdxTableFilters,
      isCashbookTableFilterActive,
      clearCashbookTableFilters,
      isRecUmrsTableFilterActive,
      clearRecUmrsTableFilters,
      formatCurrency,
      formatCurrencyNoSymbol,
      formatDate,
      formatDateTime,
      submitDateTitle,
      adminDateTitle,
      hoveredRowTooltip,
      showRowTooltip,
      moveRowTooltip,
      hideRowTooltip,
      fetchReport
    }
  }
  // No beforeRouteLeave here: vue-router only calls in-component navigation guards on the
  // component a route actually matches, and this one is a nested CHILD of Reconciliation.vue
  // (the real /reconciliation route component), so a guard defined here never fires at all -
  // clicking a top-nav tab mid-edit silently left the page (reported bug). It lives in
  // Reconciliation.vue instead, calling guardUnsavedChanges (returned from setup above, so it's
  // reachable through a template ref on this component) with vue-router's own `next` as the
  // retry action.
})
</script>

<style lang="scss" scoped>
@import './RecProductionReport.scss';
</style>

<style lang="scss">
// Unscoped on purpose: .router-view (and reportShell, its own parent) are ancestors outside
// this component's own template, so scoped CSS (even with ::v-deep) can never reach them. Both
// classes are added/removed by this component's own mount/unmount hooks above, so neither
// override ever applies outside this report being shown.
//
// reportShell (App.vue's own flex/overlay wrapper - also holds AppNavbar/MaintenanceBanner/
// AppFooter as siblings of .router-view) becomes a flex column here, so .router-view's own
// flex: 1 1 auto below is what actually gives it a real, definite height - "100vh minus
// whatever the navbar/footer actually render at", computed natively by the browser's own flex
// algorithm, not a frozen pixel number from a one-shot JS measurement (see reportShell's own
// script-side comment for why that broke across browsers). > *:not(.router-view) covers
// AppNavbar/MaintenanceBanner/AppFooter without needing to name each one - whichever of them
// happen to be rendered keep their own natural size instead of being compressed to help
// .router-view grow.
.fcp-report-shell-active {
  display: flex !important;
  flex-direction: column !important;
  min-height: 0;

  > *:not(.router-view) {
    flex-shrink: 0;
  }
}

// .router-view is already display: flex (Bootstrap's own d-flex utility, applied here because
// the Reconciliation route sets meta.flexContainer - see AppContent.vue) at its default
// flex-direction: row - deliberately NOT overridden to column here: with a single child
// (.fcp-report) and row direction, align-items: stretch's cross axis is vertical, which is
// exactly what stretches that child to match .router-view's own height below. Switching to
// column would flip stretch onto the horizontal axis instead and lose that entirely.
// align-items/justify-content were already here (overriding the same d-flex utility's default
// center/center, which otherwise centers .fcp-report instead of filling); flex/min-height are
// the new part, giving .router-view a real, definite height from reportShell's own flex column
// above rather than an auto one - see .fcp-report's own height: 100% in RecProductionReport.scss
// for the other half of this (a percentage height child needs a definite-height parent to
// resolve against; the flex system above is what makes .router-view's own height count as one).
.router-view.fcp-report-active {
  align-items: stretch !important;
  justify-content: flex-start !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
}

// Unscoped for the same reason as .fcp-report-shell-active/.router-view.fcp-report-active above -
// <b-modal> portals its .modal DOM to <body> (unlike <b-dropdown>'s menu, which stays put and
// only needs position: fixed - see .fcp-checklist-menu's own comment), so it's never a
// descendant of .fcp-report at all once mounted and scoped CSS (even ::v-deep) can't reach it.
// modal-class="fcp-unsaved-changes-modal" (see the template) is what scopes these rules to just
// this one modal instead of every <b-modal> anywhere in the app. Brings it in line with this
// report's own small-scale, orange-accented look instead of the theme's plain generic default.
.fcp-unsaved-changes-modal {
  .modal-content {
    border-radius: 8px;
  }

  .modal-header {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #ecdfc8;
  }

  .modal-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #b06a1e;
  }

  .modal-body {
    padding: 0.85rem 1rem;
    font-size: 0.72rem;
    color: #495057;
    text-align: justify;
  }

  .modal-footer {
    padding: 0.6rem 1rem;
    gap: 0.4rem;
  }

  button.btn {
    font-size: 0.65rem;
    padding: 0.3rem 0.7rem;
    line-height: 1.2;
  }

  // Same red as .fcp-variance-edit-bar__discard elsewhere on this same page (a plain text link
  // there, not a bordered button - adapted here since a bare link would read as less significant
  // than the other two real buttons sitting right next to it in this modal's footer, when
  // discarding is just as real a choice as saving).
  .btn-warning {
    background-color: #fff;
    border-color: #e2574c;
    color: #e2574c !important;

    &:hover,
    &:focus {
      background-color: #fdf1f0;
      color: #c94435 !important;
    }
  }
}
</style>